import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, addDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import lenderData from './loanbrokerdata.json';
import { generateSummaryPDF } from './PdfGenerator';
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { Image } from '@react-pdf/renderer';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase'; // Ensure 'storage' is properly initialized in your Firebase setup
import LoanPaymentCalculator from './LoanPaymentCalculator'; // Import the LoanPaymentCalculator component
import ImportantFinancials from './ImportantFinancials'; // Import the ImportantFinancials component
import { CheckIcon, CheckCircleIcon, MinusCircleIcon, ChevronLeftIcon, ChevronUpIcon, CreditCardIcon, UserGroupIcon, XCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import LoanFiles from './loanfiles';
import { 
  PaperClipIcon, 
  ArrowLeftIcon, 
  DocumentTextIcon, 
  ArrowDownTrayIcon, 
  ArrowUpIcon, 
  ArrowDownIcon, 
  ArrowUpTrayIcon, 
  ArrowTopRightOnSquareIcon, 
  ExclamationCircleIcon, 
  DocumentIcon, 
  ChevronDownIcon,
  PhoneIcon,
  PlusIcon,
} from '@heroicons/react/20/solid';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import LoanOfficerAssignment from './LoanOfficerAssignment';
import OcrReview from './OcrReview';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { Fragment } from 'react';
import DebtServiceCoverageCalculator from './DebtServiceCoverageCalc';
import { documentRequirements } from '../customerPortal/documentTracker';
import FinancialMetricsDisplayAdmin from './FinancialMetricsDisplayAdmin';
import PrequalFinancialMetricsDisplay from './PrequalFinancialMetricsDisplay';
import PrequalificationOcrReview from './PrequalificationOcrReview';
import PrequalDebtServiceCoverageCalculator from './prequalDebtServiceCoverageCalc';
import PrequalificationTable from './PrequalificationTable';
import { Menu } from '@headlessui/react'
import {
  ArchiveBoxIcon,
  ArrowRightCircleIcon,
  DocumentDuplicateIcon,
  HeartIcon,
  PencilSquareIcon,
  TrashIcon,
  UserPlusIcon,
  EnvelopeIcon,
  BellIcon,
  CheckBadgeIcon,
} from '@heroicons/react/20/solid';
import { CurrencyDollarIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import EmailHistoryPanel from './EmailHistoryPanel';
import { LoanStatuses } from '../constants/statuses';
import prequalTemplate from '../images/prequalification-template.png';
import JSZip from 'jszip';
import { ClockIcon } from '@heroicons/react/24/outline'; // Import ClockIcon
import { formatDistanceToNowStrict, differenceInDays } from 'date-fns'; // Using date-fns for cleaner duration formatting
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'; // Import alert icon
import CapitalReadinessAdmin from './CapitalReadinessAdmin';

// Helper to get the appropriate dashboard URL based on current user
const getAdminDashboardUrl = () => {
  const currentUser = auth.currentUser;
  if (currentUser?.email?.toLowerCase() === 'ben@lended.ai') {
    return '/ben-portal';
  }
  return '/admin-portal';
};

const replaceNullsWithEmptyStrings = (obj) => {
  if (typeof obj !== 'object' || obj === null || obj === undefined) {
    return obj === null || obj === undefined ? '' : obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(replaceNullsWithEmptyStrings);
  }

  return Object.entries(obj).reduce((acc, [key, value]) => {
    acc[key] = replaceNullsWithEmptyStrings(value);
    return acc;
  }, {});
};
const statusOptions = [
  { name: 'Awaiting Term Sheet', icon: ClockIcon, action: LoanStatuses.AWAITING_TERM_SHEET },
  { name: 'Move to Term Sheet Given', icon: DocumentTextIcon, action: LoanStatuses.TERM_SHEET_GIVEN },
  { name: 'Term Sheet Signed', icon: PencilSquareIcon, action: LoanStatuses.TERM_SHEET_SIGNED },
  { name: 'Move to Underwriting', icon: PencilSquareIcon, action: LoanStatuses.UNDERWRITING },
  { name: 'Move to Closing', icon: ArchiveBoxIcon, action: LoanStatuses.CLOSING },
  { name: 'Mark as Funded', icon: CheckBadgeIcon, action: LoanStatuses.FUNDED },
];
const fetchDocuments = async (loanId) => {
  try {
    const documentsRef = collection(db, 'documentMetadata');
    const q = query(documentsRef, where('loanApplicationId', '==', loanId));
    const querySnapshot = await getDocs(q);
    const documentsData = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Group documents by label
    const groupedDocuments = {};
    documentsData.forEach((document) => {
      if (!groupedDocuments[document.label]) {
        groupedDocuments[document.label] = [];
      }
      groupedDocuments[document.label].push(document);
    });

    return groupedDocuments;
  } catch (error) {
    console.error('Error fetching documents:', error);
    return {};
  }
};

const fetchAndMatchLenders = async (loan) => {
  try {
    const lendersRef = collection(db, 'banks');
    const lendersSnapshot = await getDocs(lendersRef);
    const lenders = lendersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    console.log('All lenders:', lenders);
    console.log('Loan details:', loan);

    const matchedLenders = lenders.filter(lender => {
      console.log(`\nChecking lender: ${lender.bankName}`);

      // Check if the lender serves the business state
      const lenderStates = Array.isArray(lender.state) ? lender.state : [lender.state];
      const stateMatch = lenderStates.includes(loan.businessState) || lenderStates.length === 50;
      
      if (!stateMatch) {
        console.log(`State mismatch: Lender states - ${lenderStates.join(', ')}, Business state - ${loan.businessState}`);
        return false;
      }

      // Check if the loan amount is within the lender's range
      const loanAmount = parseFloat(loan.loanSize);
      if (lender.optimalLoanAmount && lender.optimalLoanAmount.min && lender.optimalLoanAmount.max) {
        if (loanAmount < lender.optimalLoanAmount.min || loanAmount > lender.optimalLoanAmount.max) {
          console.log(`Loan amount mismatch: Lender range - ${lender.optimalLoanAmount.min} to ${lender.optimalLoanAmount.max}, Loan amount - ${loanAmount}`);
          return false;
        }
      } else {
        console.log(`Lender ${lender.bankName} is missing optimalLoanAmount data`);
      }

      // Check credit score requirement (if available)
      const creditScore = Math.max(...Object.values(loan.creditScores).map(score => parseInt(score)));
      if (lender.creditScore && creditScore < lender.creditScore) {
        console.log(`Credit score mismatch: Required - ${lender.creditScore}, Actual - ${creditScore}`);
        return false;
      }

      // Check DSCR requirement (if available)
      const currentYearDSCR = loan?.financialAnalysis?.['Business Tax Return - Current Year']?.dscr;
      if (currentYearDSCR && lender.debtServiceRequirement && currentYearDSCR < lender.debtServiceRequirement) {
        console.log(`DSCR mismatch: Required - ${lender.debtServiceRequirement}, Actual - ${currentYearDSCR}`);
        return false;
      }

      console.log(`Lender ${lender.bankName} matched`);
      return true;
    });

    console.log('Matched lenders:', matchedLenders);

    // Sort lenders based on a composite score (lower is better)
    const sortedLenders = matchedLenders.sort((a, b) => {
      const scoreA = ((a.usualLoanRateMin + a.usualLoanRateMax) / 2 || 0) + (a.processTime || 0) / 30 + (a.termSheetTime || 0) / 7 - (a.approvalPercentage || 0) / 100;
      const scoreB = ((b.usualLoanRateMin + b.usualLoanRateMax) / 2 || 0) + (b.processTime || 0) / 30 + (b.termSheetTime || 0) / 7 - (b.approvalPercentage || 0) / 100;
      return scoreA - scoreB;
    });

    return sortedLenders; // Return all matched lenders
  } catch (error) {
    console.error('Error fetching and matching lenders:', error);
    return [];
  }
};

// Helper function to format status strings nicely
const formatStatusName = (status) => {
  if (!status) return 'Unknown';
  return status
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, char => char.toUpperCase()); // Capitalize first letter of each word
};

// Helper function to get the relevant timestamp, calculate duration, and days in status
const getStatusDuration = (loan) => {
  if (!loan || !loan.loanStatus) {
    return { statusName: 'Unknown', duration: 'N/A', statusDate: null, daysInStatus: 0 };
  }

  let statusDate = null;
  const status = loan.loanStatus;

  // *** Prioritize documentsUploadedAt for SUBMITTED status when docs are marked complete ***
  if (status === LoanStatuses.SUBMITTED && loan.allDocumentsUploaded === true) {
    statusDate = loan.documentsUploadedAt; // Use this timestamp as the start for this phase
  } else {
    // Original mapping for other statuses
    switch (status) {
      case LoanStatuses.FUNDED: statusDate = loan.fundedAt; break;
      case LoanStatuses.CLOSING: statusDate = loan.closingInitiatedAt; break;
      case LoanStatuses.REJECTED: statusDate = loan.rejectedAt; break;
      case LoanStatuses.UNDERWRITING: statusDate = loan.inUnderwritingAt; break;
      case LoanStatuses.DOCUMENT_CLARIFICATION: statusDate = loan.documentsRequestedAt; break;
      case LoanStatuses.TERM_SHEET_EXTENDED: statusDate = loan.termSheetExtendedAt; break;
      case LoanStatuses.PREQUALIFIED_FOR: statusDate = loan.prequalifiedAt; break;
      case LoanStatuses.SUBMITTED: // This now only covers SUBMITTED *before* docs uploaded
      case LoanStatuses.NEW_APPLICATION:
        statusDate = loan.submissionTimestamp || loan.createdAt; break;
      default: statusDate = loan.createdAt;
    }
  }

  let duration = 'N/A';
  let actualDate = null;
  let daysInStatus = 0;

  if (statusDate) {
    try {
      actualDate = statusDate.toDate ? statusDate.toDate() : new Date(statusDate);
      if (!isNaN(actualDate.getTime())) {
        duration = formatDistanceToNowStrict(actualDate, { addSuffix: true });
        daysInStatus = differenceInDays(new Date(), actualDate);
      } else {
        console.warn("Invalid status date found:", statusDate);
        actualDate = null;
      }
    } catch (e) {
      console.error("Error processing status date:", e);
      actualDate = null;
    }
  }

  return {
    statusName: formatStatusName(status),
    duration: duration,
    statusDate: actualDate,
    daysInStatus: daysInStatus
  };
};

// *** Define Checklists for different statuses ***
const statusChecklists = {
  [LoanStatuses.NEW_APPLICATION]: [
    'Call to explain process',
    // Removed 'Get all files uploaded' - this is implied by moving to SUBMITTED
  ],
  [LoanStatuses.SUBMITTED]: [ // Checklist items for when the status *is* SUBMITTED
    'Send initial package to bank'
  ],
  // Removed SUBMITTED_DocsUploaded key
  [LoanStatuses.PREQUALIFIED_FOR]: [
    'Send consulting agreement',
    'Signed consulting agreement',
  ],
  [LoanStatuses.TERM_SHEET_EXTENDED]: [
    'Send consulting agreement',
    'Signed consulting agreement',
  ],
  [LoanStatuses.DOCUMENT_CLARIFICATION]: [
    'Review received documents',
    'Send updated package to bank',
  ],
  [LoanStatuses.CLOSING]: [
    'Send closing package to bank',
  ],
   // Add other statuses if they need checklists
   [LoanStatuses.UNDERWRITING]: [], // Example: No checklist needed
   [LoanStatuses.REJECTED]: [],
   [LoanStatuses.FUNDED]: [],
};

const LoanDetails = () => {
  const { loanId } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const [documentsByLabel, setDocumentsByLabel] = useState({});
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [topLenders, setTopLenders] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [deductions, setDeductions] = useState([]);
  const [personalGuarantorResumes, setPersonalGuarantorResumes] = useState([]);
  const [resumeScore, setResumeScore] = useState(0);
  const [isApplicantInfoExpanded, setIsApplicantInfoExpanded] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedDeductions, setSelectedDeductions] = useState({
    'Business Tax Return - Current Year': {},
    'Business Tax Return - Last Year': {},
    'Business Tax Return - 2 Years Ago': {}
  });
  const [isLoanFilesExpanded, setIsLoanFilesExpanded] = useState(true);
  const [isLoanCalculatorExpanded, setIsLoanCalculatorExpanded] = useState(true);
  const [isDebtServiceCalcExpanded, setIsDebtServiceCalcExpanded] = useState(true);
  const [personalFinancialData, setPersonalFinancialData] = useState([]);
  const [activeGuarantorIndex, setActiveGuarantorIndex] = useState(0);

  const [steps, setSteps] = useState([
    { 
      id: '01', 
      name: 'Pre-Check', 
      description: 'Initial document review',
      href: '#', 
      status: 'upcoming',
      items: [
        { name: 'Personal Financial Statement', statusKey: 'isPersonalFinancialStatementComplete' },
        { name: 'Credit Bureau Report', statusKey: 'isCreditBureauReportComplete' },
        { name: 'Resumes for Key Team Members', statusKey: 'isResumesComplete' },
        { name: 'Personal Tax Returns', statusKey: 'isPersonalTaxReturnsComplete' },
      ]
    },
    { 
      id: '02', 
      name: 'Loan Application', 
      description: 'Application form completion',
      href: '#', 
      status: 'upcoming',
      items: [
        { name: 'Complete Application Form', statusKey: 'isApplicationComplete' },
      ]
    },
    { 
      id: '03', 
      name: 'Term Sheet', 
      description: 'Final review and approval',
      href: '#', 
      status: 'upcoming',
      items: [
        { name: 'Business Tax Returns', statusKey: 'isTermSheetComplete' },
      ]
    },
  ]);
  const [personalGuarantors, setPersonalGuarantors] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [matchedLenders, setMatchedLenders] = useState([]);
  const [assigningBank, setAssigningBank] = useState(null);
  const [isDropboxModalOpen, setIsDropboxModalOpen] = useState(false);
  const [dropboxLink, setDropboxLink] = useState('');
  const [assigningBankId, setAssigningBankId] = useState(null);
  const [assignedBanks, setAssignedBanks] = useState({});
  const [ocrData, setOcrData] = useState({});
  const [isProgressCardExpanded, setIsProgressCardExpanded] = useState(true);
  const [modalAmountInput, setModalAmountInput] = useState('');
  const [isAmountModalOpen, setIsAmountModalOpen] = useState(false);
  const [amountModalContext, setAmountModalContext] = useState({ action: null, title: '', field: '', requiresImage: false });
  const [isUploadedFilesExpanded, setIsUploadedFilesExpanded] = useState(false);
  const [isWorkbookUploadExpanded, setIsWorkbookUploadExpanded] = useState(true);
  const [workbookFile, setWorkbookFile] = useState(null);
  const [workbookUploading, setWorkbookUploading] = useState(false);
  const [workbookUploadProgress, setWorkbookUploadProgress] = useState(0);
  const [workbookUploadError, setWorkbookUploadError] = useState(null);
  const [emailHistoryExpanded, setEmailHistoryExpanded] = useState(false);
  const [missingDocs, setMissingDocs] = useState([]);
  const [brokerInfo, setBrokerInfo] = useState(null);
  const [isRequestDocsModalOpen, setIsRequestDocsModalOpen] = useState(false);
  const [currentDocRequestInput, setCurrentDocRequestInput] = useState('');
  const [pendingDocRequests, setPendingDocRequests] = useState([]);
  const [allLoanDocuments, setAllLoanDocuments] = useState([]);
  // Reclassify-file UI state: which doc is being edited + which label is pending.
  const [reclassifyDocId, setReclassifyDocId] = useState(null);
  const [reclassifyLabelDraft, setReclassifyLabelDraft] = useState('');
  const [reclassifyCustomDraft, setReclassifyCustomDraft] = useState('');
  const [reclassifySaving, setReclassifySaving] = useState(false);
  // Sort order for the Uploaded & Requested Documents list.
  // 'label' = original grouping (default), 'newest' / 'oldest' by uploadedAt.
  const [uploadedFilesSort, setUploadedFilesSort] = useState('label');
  const [requestModalPurpose, setRequestModalPurpose] = useState(null); // e.g., LoanStatuses.CLOSING or LoanStatuses.DOCUMENT_CLARIFICATION
  const [notes, setNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [isEditingFollowUp, setIsEditingFollowUp] = useState(false);
  const [followUpReason, setFollowUpReason] = useState('');
  const [followUpNotes, setFollowUpNotes] = useState([]);
  const [newFollowUpNote, setNewFollowUpNote] = useState('');
  const [newFollowUpReason, setNewFollowUpReason] = useState('');
  const [isEditingFollowUpNotes, setIsEditingFollowUpNotes] = useState(false);
  const [assignedWorkingCapitalBank, setAssignedWorkingCapitalBank] = useState('');
  const [isEditingBankAssignment, setIsEditingBankAssignment] = useState(false);
  const [assignedWorkingCapitalBanks, setAssignedWorkingCapitalBanks] = useState([]);
  const [selectedBankToAdd, setSelectedBankToAdd] = useState('');

  // Business cards and prequalification state
  const [selectedBusinessIndex, setSelectedBusinessIndex] = useState(null);
  const [isPrequalifyModalOpen, setIsPrequalifyModalOpen] = useState(false);
  const [prequalifyBusinessIndex, setPrequalifyBusinessIndex] = useState(null);
  const [prequalifyData, setPrequalifyData] = useState({
    amount: '',
    structure: {
      sbaLoan: '',
      sellerNote: '',
      cashDown: '',
    }
  });

  // Prequalification analysis expanded sections state
  const [expandedPrequalSection, setExpandedPrequalSection] = useState(null); // 'analysis', 'financial', 'ocr'
  const [businessOcrData, setBusinessOcrData] = useState({});
  const [businessOcrMetadata, setBusinessOcrMetadata] = useState([]);

  // Borrower prequalification state (separate from business/listing prequalification)
  const [borrowerPrequalAmount, setBorrowerPrequalAmount] = useState('');
  const [isEditingBorrowerPrequal, setIsEditingBorrowerPrequal] = useState(false);
  const [borrowerPrequalifiedAt, setBorrowerPrequalifiedAt] = useState(null);

  const toggleApplicantInfo = () => {
    setIsApplicantInfoExpanded(!isApplicantInfoExpanded);
  };

  const toggleLoanFiles = () => {
    setIsLoanFilesExpanded(!isLoanFilesExpanded);
  };

  const toggleLoanCalculator = () => {
    setIsLoanCalculatorExpanded(!isLoanCalculatorExpanded);
  };
  const calculateNetWorth = (guarantorData) => {
    if (!guarantorData || !guarantorData.Assets || !guarantorData.Liabilities) return 'N/A';
    
    const assetKeys = ['CashOnHandAndInBanks', 'SavingsAccounts', 'StocksAndBonds', 'RealEstate', 'Automobiles', 'OtherAssets'];
    const liabilityKeys = ['AccountsPayable', 'NotesPayableToBanksAndOthers', 'InstallmentAccountAuto', 'InstallmentAccountOther', 'LoanAgainstLifeInsurance', 'MortgagesOnRealEstate', 'UnpaidTaxes', 'OtherLiabilities'];
    
    let totalAssets = 0;
    let totalLiabilities = 0;

    // Calculate total assets
    if (guarantorData.Assets.Total) {
      totalAssets = parseFloat(guarantorData.Assets.Total);
    } else {
      totalAssets = assetKeys.reduce((sum, key) => sum + parseFloat(guarantorData.Assets[key] || 0), 0);
    }

    // Calculate total liabilities
    if (guarantorData.Liabilities.TotalLiabilities) {
      totalLiabilities = parseFloat(guarantorData.Liabilities.TotalLiabilities);
    } else {
      totalLiabilities = liabilityKeys.reduce((sum, key) => sum + parseFloat(guarantorData.Liabilities[key] || 0), 0);
    }

    const netWorth = totalAssets - totalLiabilities;
    return isNaN(netWorth) ? 'N/A' : netWorth;
  };
  const checkDocumentProgress = () => {
    const documentStatus = documentRequirements.reduce((acc, doc) => {
      if (doc.Individual) {
        acc[doc.Name] = personalGuarantors.every((guarantor) =>
          doc.Labels.every((label) =>
            uploadedDocuments.some(
              (uploadedDoc) =>
                uploadedDoc.documentName === doc.Name &&
                uploadedDoc.ownerId === `${guarantor.firstName} ${guarantor.lastName}` &&
                uploadedDoc.label === label
            )
          )
        );
      } else {
        acc[doc.Name] = doc.Labels.every((label) =>
          doc.Optional
            ? true
            : uploadedDocuments.some(
                (uploadedDoc) =>
                  uploadedDoc.documentName === doc.Name &&
                  uploadedDoc.ownerId === null &&
                  uploadedDoc.label === label
              )
        );
      }
      return acc;
    }, {});

    const preCheckComplete = documentRequirements
      .filter((doc) => doc.Individual)
      .every((doc) => documentStatus[doc.Name]);

    const businessDocumentsComplete = documentRequirements
      .filter((doc) => !doc.Individual)
      .every((doc) => documentStatus[doc.Name]);

    const requiredFields = [
      'businessAge',
      'businessState',
      'businessSummary',
      'ebitda',
      'loanSize',
      'realEstateAppraisal',
      'revenue',
    ];
    const formComplete = requiredFields.every((field) => !!loan[field]);

    const creditScoreComplete =
      loan &&
      loan.creditScores &&
      Object.values(loan.creditScores).some((score) => score > 0);

    return {
      documentStatus,
      preCheckComplete,
      businessDocumentsComplete,
      formComplete,
      creditScoreComplete,
    };
  };

  function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
  }

  useEffect(() => {
    if (loan && loan.resumeScore !== undefined) {
      setResumeScore(loan.resumeScore);
    }
  }, [loan]);

  useEffect(() => {
    if (loan && documentsByLabel) {
      const allRequiredDocuments = documentRequirements.flatMap(doc => 
        doc.Individual
          ? personalGuarantors.flatMap(guarantor => 
              doc.Labels.map(label => ({
                ...doc,
                label,
                key: `${doc.Name}-${label}-${guarantor.firstName} ${guarantor.lastName}`,
                guarantor
              }))
            )
          : doc.Labels.map(label => ({
              ...doc,
              label,
              key: `${doc.Name}-${label}`
            }))
      );
  
      const uploadedDocs = Object.entries(documentsByLabel)
        .flatMap(([label, docs]) => docs.map(d => ({ ...d, label })));
  
      const missing = allRequiredDocuments.filter(doc => 
        !uploadedDocs.some(uploadedDoc => 
          uploadedDoc.label === doc.label && 
          (doc.Individual ? uploadedDoc.ownerId === `${doc.guarantor.firstName} ${doc.guarantor.lastName}` : true)
        )
      );
  
      setMissingDocs(missing);
    }
  }, [loan, documentsByLabel, personalGuarantors]);

  useEffect(() => {
    const fetchLoanDetails = async () => {
      if (!loanId) {
        console.error('Loan ID is missing');
        navigate(getAdminDashboardUrl());
        return;
      }

      try {
        const loanDoc = await getDoc(doc(db, 'loanApplications', loanId));
        if (loanDoc.exists()) {
          const loanData = loanDoc.data();
          // Ensure statusChecklistState is initialized as an object if null/undefined
          setLoan({ id: loanDoc.id, ...loanData, statusChecklistState: loanData.statusChecklistState || {} });
          setPersonalGuarantors(loanData.personalGuarantors || []);
          
          // Load notes if they exist
          if (loanData.notes) {
            setNotes(loanData.notes);
          }
          
          // Load follow-up date if it exists
          if (loanData.followUpDate) {
            setFollowUpDate(loanData.followUpDate);
          }
          
          // Load follow-up reason if it exists
          if (loanData.followUpReason) {
            setFollowUpReason(loanData.followUpReason);
          }
          
          // Load follow-up notes if they exist
          if (loanData.followUpNotes) {
            // Handle both old string format and new array format
            if (Array.isArray(loanData.followUpNotes)) {
              setFollowUpNotes(loanData.followUpNotes);
            } else {
              // Convert old string format to new array format
              setFollowUpNotes([{
                note: loanData.followUpNotes,
                timestamp: loanData.followUpNotesUpdatedAt || serverTimestamp(),
                id: Date.now()
              }]);
            }
          }

          // Load assigned working capital bank if it exists
          if (loanData.assignedWorkingCapitalBank) {
            setAssignedWorkingCapitalBank(loanData.assignedWorkingCapitalBank);
          }

          // Load assigned working capital banks (multiple) if they exist
          if (loanData.assignedWorkingCapitalBanks && Array.isArray(loanData.assignedWorkingCapitalBanks)) {
            setAssignedWorkingCapitalBanks(loanData.assignedWorkingCapitalBanks);
          } else if (loanData.assignedWorkingCapitalBank) {
            // Convert legacy single bank to new array format
            setAssignedWorkingCapitalBanks([loanData.assignedWorkingCapitalBank]);
          }

          // Load borrower prequalification data if it exists
          if (loanData.borrowerPrequalAmount) {
            setBorrowerPrequalAmount(loanData.borrowerPrequalAmount);
          }
          if (loanData.borrowerPrequalifiedAt) {
            setBorrowerPrequalifiedAt(loanData.borrowerPrequalifiedAt);
          }

          // Check if loan has a brokerId and fetch broker info
          if (loanData.userId) {
            const brokersRef = collection(db, 'brokers');
            const q = query(brokersRef, where('userId', '==', loanData.userId));
            const brokerSnapshot = await getDocs(q);
            
            if (!brokerSnapshot.empty) {
              const brokerData = brokerSnapshot.docs[0].data();
              setBrokerInfo(brokerData);
            }
          }
        } else {
          console.error('Loan not found');
          navigate(getAdminDashboardUrl());
        }
      } catch (error) {
        console.error('Error fetching loan details:', error);
      }
    };

    const fetchOCRData = async () => {
      if (!loanId) return;

      try {
        const ocrMetadataRef = collection(db, 'OCRMetadata');
        const q = query(ocrMetadataRef, where('loanApplicationId', '==', loanId));
        const querySnapshot = await getDocs(q);
        
        const processedData = {};
        const personalFinancialOCRs = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.documentName === "Business Tax Returns") {
            const year = getYearFromLabel(data.label);
            processedData[year] = {
              ordinaryBusinessIncome: parseFloat(data.ocrResult?.ordinary_business_income) || 0,
              grossRevenue: parseFloat(data.ocrResult?.gross_receipts_or_sales) || 0,
              officerCompensation: parseFloat(data.ocrResult?.compensation_of_officers) || 0,
              interest: parseFloat(data.ocrResult?.interest) || 0,
              depreciation: parseFloat(data.ocrResult?.depreciation) || 0,
              otherDeductions: data.ocrResult?.other_deductions || {},
              year: year,
            };
          } else if (data.documentName === "Personal Financial Statement") {
            personalFinancialOCRs.push(data.ocrResult);
          }
        });

        setOcrData(processedData);
        setPersonalFinancialData(personalFinancialOCRs);
      } catch (error) {
        console.error('Error fetching OCR data:', error);
      }
    };

    fetchLoanDetails();
    fetchOCRData();
  }, [loanId, navigate]); // Ensure dependencies are correct

  const getYearFromLabel = (label) => {
    const yearLabels = {
      'Business Tax Return - Current Year': '2023',
      'Business Tax Return - Last Year': '2022',
      'Business Tax Return - 2 Years Ago': '2021'
    };
    return yearLabels[label] || label;
  };

  useEffect(() => {
    const fetchLoanDocuments = async () => {
      // Fetch ALL documents, don't group by label immediately here
      try {
        const documentsRef = collection(db, 'documentMetadata');
        const q = query(documentsRef, where('loanApplicationId', '==', loanId));
        const querySnapshot = await getDocs(q);
        const documentsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllLoanDocuments(documentsData); // Store the flat list of all documents
      } catch (error) {
        console.error('Error fetching documents:', error);
        setAllLoanDocuments([]);
      }
    };

    fetchLoanDocuments();
  }, [loanId]); // Removed documentsByLabel from dependency array if it was there

  useEffect(() => {
    if (loan) {
      const {
        documentStatus,
        preCheckComplete,
        businessDocumentsComplete,
        formComplete,
        creditScoreComplete,
      } = checkDocumentProgress();

      const updatedSteps = [
        {
          id: '01',
          name: 'Pre-Check',
          description: 'Initial document review',
          status: preCheckComplete && creditScoreComplete ? 'complete' : 'upcoming',
          items: documentRequirements
            .filter((doc) => doc.Individual)
            .map((doc) => ({
              name: doc.Name,
              status: documentStatus[doc.Name] ? 'complete' : 'upcoming',
            })),
        },
        {
          id: '02',
          name: 'Loan Application',
          description: 'Application form completion',
          status: formComplete
            ? 'complete'
            : preCheckComplete && creditScoreComplete
            ? 'current'
            : 'upcoming',
          items: [
            { name: 'Complete Application Form', status: formComplete ? 'complete' : 'upcoming' },
          ],
        },
        {
          id: '03',
          name: 'Term Sheet',
          description: 'Final review and approval',
          status: businessDocumentsComplete ? 'complete' : formComplete ? 'current' : 'upcoming',
          items: documentRequirements
            .filter((doc) => !doc.Individual)
            .map((doc) => ({
              name: doc.Name,
              status: documentStatus[doc.Name] ? 'complete' : 'upcoming',
            })),
        },
      ];

      setSteps(updatedSteps);
    }
  }, [loan, uploadedDocuments, personalGuarantors]);

  useEffect(() => {
    const getLenders = async () => {
      if (loan) {
        const lenders = await fetchAndMatchLenders(loan);
        setMatchedLenders(lenders);
      }
    };
    getLenders();
  }, [loan]);

  const fetchBankAssignments = async () => {
    try {
      const assignmentsRef = collection(db, 'bankAssignments');
      const q = query(assignmentsRef, where('applicationId', '==', loanId));
      const querySnapshot = await getDocs(q);
      const assignments = {};
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        assignments[data.bankId] = data;
      });
      setAssignedBanks(assignments);
    } catch (error) {
      console.error('Error fetching bank assignments:', error);
    }
  };

  useEffect(() => {
    fetchBankAssignments();
  }, [loanId]);

  const handleGenerateSummaryPDF = async () => {
    try {
      const pdfBlob = await generateSummaryPDF(loan);
      if (pdfBlob instanceof Blob) {
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
      } else {
        throw new Error('Generated PDF is not a Blob');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };
  const calculateAnnualDebtService = (loan) => {
    const totalLoanAmount = Number(loan.loanSize || 0);
    const sba7aPercentage = Number(loan.loanStructure?.sba7aLoan || 0) / 100;
    const sba7aLoanAmount = totalLoanAmount * sba7aPercentage;
    const interestRate = 0.105; // 10.5% interest rate
    const loanTerm = 10; // 10-year term

    if (sba7aLoanAmount <= 0) {
      return 0;
    }

    const monthlyRate = interestRate / 12;
    const numberOfPayments = loanTerm * 12;
    const monthlyPayment = (sba7aLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
    return monthlyPayment * 12;
  };
  const handleDeductionToggle = (year, deductionKey, deductionValue) => {
    setSelectedDeductions(prevDeductions => ({
      ...prevDeductions,
      [year]: {
        ...prevDeductions[year],
        [deductionKey]: prevDeductions[year][deductionKey] ? undefined : deductionValue
      }
    }));
  };

  const handleDocumentClick = async (document) => {
    try {
      const ocrMetadataRef = collection(db, 'OCRMetadata');
      const q = query(ocrMetadataRef, where('documentId', '==', document.id));
      const querySnapshot = await getDocs(q);
      const ocrMetadata = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ocrResult: replaceNullsWithEmptyStrings(doc.data().ocrResult),
        ownerId: doc.data().ownerId, // Add this line to include ownerId
      }));
  
      if (ocrMetadata.length > 0 && ocrMetadata[0].ocrResult) {
        setSelectedDocument({ ...document, ocrMetadata });
      } else {
        console.warn('No OCR metadata found for document:', document);
        setSelectedDocument({ ...document, ocrMetadata: [] });
      }
    } catch (error) {
      console.error('Error fetching OCR metadata:', error);
      setSelectedDocument({ ...document, ocrMetadata: [] });
    }
  };

  const handleOcrInputChange = (e, index, key) => {
    const value = e.target.value;
    setSelectedDocument((prevDocument) => {
      const newOcrMetadata = [...prevDocument.ocrMetadata];
      newOcrMetadata[index].ocrResult[key] = value;
      return {
        ...prevDocument,
        ocrMetadata: newOcrMetadata,
      };
    });
  };

  const handleOtherDeductionsChange = (e, index, key) => {
    const value = e.target.value;
    setDeductions((prevDeductions) =>
      prevDeductions.map((deduction, i) =>
        i === index ? { ...deduction, [key]: value } : deduction
      )
    );
  };

  const handleSaveFinancials = async (financials) => {
    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      const loanSnapshot = await getDoc(loanRef);
      if (loanSnapshot.exists()) {
        const loanData = loanSnapshot.data();
        const updatedLoanData = {
          ...loanData,
          financialAnalysis: {
            ...(loanData.financialAnalysis || {}),
            [financials.year]: financials
          }
        };

        await updateDoc(loanRef, updatedLoanData);
        console.log('Financial analysis updated successfully.');
      }
    } catch (error) {
      console.error('Error saving financial analysis:', error);
    }
  };

  const downloadFile = async (fileUrl, fileName) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    }
  };
  const handleSaveChanges = async (loanId, documentId, updatedOcrData, documentLabel) => {
    try {
      console.log('Starting save process for document:', documentLabel);
      console.log('Document ID:', documentId);
      console.log('Updated OCR data:', updatedOcrData);

      // 1. Update OCR metadata
      const ocrMetadataRef = doc(db, 'OCRMetadata', documentId);
      const ocrMetadataSnap = await getDoc(ocrMetadataRef);

      if (!ocrMetadataSnap.exists()) {
        console.log('OCR metadata document does not exist. Creating new document.');
        await setDoc(ocrMetadataRef, { ocrResult: updatedOcrData });
      } else {
        console.log('OCR metadata document exists. Updating document.');
        await updateDoc(ocrMetadataRef, { ocrResult: updatedOcrData });
      }
      console.log('OCR metadata updated successfully.');

      // 2. Update loan application
      const loanRef = doc(db, 'loanApplications', loanId);
      const loanSnapshot = await getDoc(loanRef);
      
      if (loanSnapshot.exists()) {
        const loanData = loanSnapshot.data();
        const updatedLoanData = { ...loanData };
  
        console.log('Updating loan data for document type:', documentLabel);
  
        // Initialize financialAnalysis if it doesn't exist
        if (!updatedLoanData.financialAnalysis) {
          updatedLoanData.financialAnalysis = {};
        }
  
        // Update specific fields based on document type
        if (documentLabel.includes('Business Tax Return')) {
          let yearKey;
          if (documentLabel.includes('Current Year')) {
            yearKey = 'Business Tax Return - Current Year';
          } else if (documentLabel.includes('Last Year')) {
            yearKey = 'Business Tax Return - Last Year';
          } else if (documentLabel.includes('2 Years Ago')) {
            yearKey = 'Business Tax Return - 2 Years Ago';
          }
  
          if (yearKey) {
            // Initialize the year in financialAnalysis if it doesn't exist
            if (!updatedLoanData.financialAnalysis[yearKey]) {
              updatedLoanData.financialAnalysis[yearKey] = {};
            }
  
            // Update the financialAnalysis for this year
            updatedLoanData.financialAnalysis[yearKey] = {
              ...updatedLoanData.financialAnalysis[yearKey],
              ordinaryBusinessIncome: parseFloat(updatedOcrData.ordinary_business_income) || 0,
              grossRevenue: parseFloat(updatedOcrData.gross_receipts_or_sales) || 0,
              officerCompensation: parseFloat(updatedOcrData.compensation_of_officers) || 0,
              interest: parseFloat(updatedOcrData.interest) || 0,
              depreciation: parseFloat(updatedOcrData.depreciation) || 0,
              year: yearKey,
            };

            // Calculate NOI and DSCR
            const sde = updatedLoanData.financialAnalysis[yearKey].ordinaryBusinessIncome +
                      updatedLoanData.financialAnalysis[yearKey].officerCompensation +
                      updatedLoanData.financialAnalysis[yearKey].interest +
                      updatedLoanData.financialAnalysis[yearKey].depreciation;
            
            const newOfficerCompensation = 80000 * (loan.personalGuarantors?.length || 1); // Assuming $80,000 per guarantor
            const noi = sde - newOfficerCompensation;
            
            updatedLoanData.financialAnalysis[yearKey].noi = noi;
            updatedLoanData.financialAnalysis[yearKey].newOfficerCompensation = newOfficerCompensation;

            // Calculate DSCR (you may need to adjust this based on your specific DSCR calculation)
            const annualDebtService = calculateAnnualDebtService(loan); // You'll need to implement this function
            updatedLoanData.financialAnalysis[yearKey].dscr = noi / annualDebtService;

            // Handle other deductions
            if (updatedOcrData.other_deductions) {
              const otherDeductions = {};
              if (Array.isArray(updatedOcrData.other_deductions)) {
                updatedOcrData.other_deductions.forEach(deduction => {
                  otherDeductions[deduction.category] = parseFloat(deduction.amount) || 0;
                });
              } else if (typeof updatedOcrData.other_deductions === 'object') {
                Object.entries(updatedOcrData.other_deductions).forEach(([category, amount]) => {
                  otherDeductions[category] = parseFloat(amount) || 0;
                });
              }
              updatedLoanData.financialAnalysis[yearKey].otherDeductions = otherDeductions;
            }
  
            console.log(`Updated Business Tax Return data for ${yearKey}`);
          } else {
            console.log('Unable to determine year for Business Tax Return');
          }
        } else if (documentLabel === 'Credit Bureau Report') {
          updatedLoanData.CreditScore = parseInt(updatedOcrData.CreditScore) || 0;
          console.log('Updated Credit Score');
        } else if (documentLabel === 'Personal Financial Statement') {
          updatedLoanData.totalAssets = parseFloat(updatedOcrData.totalAssets) || 0;
          updatedLoanData.totalLiabilities = parseFloat(updatedOcrData.totalLiabilities) || 0;
          updatedLoanData.netWorth = parseFloat(updatedOcrData.netWorth) || 0;
          console.log('Updated Personal Financial Statement data');
        } else if (documentLabel === 'Resumes for Key Team Members') {
          updatedLoanData.resumeScore = parseInt(updatedOcrData.resumeScore) || 0;
          console.log('Updated Resume Score');
        } else {
          console.log('No specific updates for document type:', documentLabel);
        }
  
        console.log('Attempting to update loan application with:', updatedLoanData);
        await updateDoc(loanRef, updatedLoanData);
        console.log('Loan application updated successfully.');
  
        // 3. Update local state
        setLoan(prevLoan => ({
          ...prevLoan,
          ...updatedLoanData
        }));
        console.log('Local loan state updated');
  
        // 4. Refresh the document data
        const updatedDocuments = await fetchDocuments(loanId);
        setDocumentsByLabel(updatedDocuments);
        console.log('Document data refreshed');
  
        // 5. Update selected document
        setSelectedDocument(prevDoc => ({
          ...prevDoc,
          ocrMetadata: [{
            ...prevDoc.ocrMetadata[0],
            ocrResult: updatedOcrData
          }]
        }));
        console.log('Selected document updated');
  
        console.log('Changes saved successfully.');
      } else {
        throw new Error('Loan application not found');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      console.error('Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      throw error; // Re-throw the error to be caught in the LoanFiles component
    }
  };

  const handleAssignBank = async (bankId) => {
    setAssigningBankId(bankId);
    setIsDropboxModalOpen(true);
  };

  const handleDropboxLinkSubmit = async () => {
    if (!dropboxLink || !assigningBankId || !loanId) {
      console.error('Missing required information');
      return;
    }

    try {
      console.log('Starting bank assignment process for bank ID:', assigningBankId);
      
      // Update loan application
      const loanRef = doc(db, 'loanApplications', loanId);
      await updateDoc(loanRef, {
        assignedBank: assigningBankId
      });
      console.log('Loan application updated with assigned bank');

      // Create bank assignment
      const now = new Date();
      const assignmentData = {
        bankId: assigningBankId,
        applicationId: loanId,
        assignedAt: serverTimestamp(),
        currentStage: {
          name: 'underReview',
          enteredAt: serverTimestamp()
        },
        stageHistory: [{
          name: 'underReview',
          enteredAt: now.toISOString(),
          exitedAt: null
        }],
        dropboxLink: dropboxLink
      };
      const assignmentRef = await addDoc(collection(db, 'bankAssignments'), assignmentData);
      console.log(`Bank assignment created with ID: ${assignmentRef.id}`);

      // Fetch bank data and create email
      const bankRef = doc(db, 'banks', assigningBankId);
      const bankSnap = await getDoc(bankRef);
      if (bankSnap.exists()) {
        const bankData = bankSnap.data();
        console.log('Bank data retrieved:', bankData);
        
        console.log('Calling createEmailDocument');
        const emailId = await createEmailDocument(bankData);
        console.log('Email document created successfully with ID:', emailId);

        // Check the created email document
        const emailDocRef = doc(db, 'email', emailId);
        const emailDocSnap = await getDoc(emailDocRef);
        if (emailDocSnap.exists()) {
          console.log('Email document data:', emailDocSnap.data());
        } else {
          console.log('Email document does not exist');
        }
      } else {
        console.error('Bank not found:', assigningBankId);
      }

      console.log(`Application ${loanId} assigned to bank with ID ${assigningBankId}.`);
      alert('Bank assigned successfully!');
      setIsDropboxModalOpen(false);
      setDropboxLink('');
      setAssigningBankId(null);

      // Refresh the loan details after assignment
      const updatedLoanSnap = await getDoc(loanRef);
      if (updatedLoanSnap.exists()) {
        setLoan({ id: updatedLoanSnap.id, ...updatedLoanSnap.data() });
      }

      // Refresh the bank assignments
      await fetchBankAssignments();
    } catch (error) {
      console.error('Error in handleDropboxLinkSubmit:', error);
      console.error('Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      alert('Failed to assign bank. Please check the console for error details.');
    }
  };

  const createEmailDocument = async (bankData) => {
    console.log('createEmailDocument called with bankData:', bankData);
    try {
      console.log('Creating email document for bank:', bankData.bankName);
      const emailRef = collection(db, 'email');
      console.log('Email collection reference created');

      // Generate the summary PDF
      const pdfBlob = await generateSummaryPDF(loan);
      
      // Upload the PDF to Firebase Storage
      const storageRef = ref(storage, `loan_summaries/${loanId}_summary.pdf`);
      await uploadBytesResumable(storageRef, pdfBlob);
      
      // Get the download URL for the uploaded PDF
      const pdfUrl = await getDownloadURL(storageRef);
  
      const loginUrl = "https://lended.ai/login"; // Replace with your actual login URL
  
      const emailData = {
        to: bankData.email,
        from: "Isak @ Lended <isak@lended.ai>",  // Changed from sbacapital.ai
        replyTo: "isak@lended.ai", // Changed from sbacapital.ai
        subject: "Next Steps for Your Loan Application",
        templateName: "LoanMatch",
        sentAt: new Date().toISOString(),
        message: {
          subject: "You've been matched with a loan!",
          firstName: bankData.loanOfficerName,
          loanDetails: "A new loan application has been matched with your bank in the loan portal. Please log in to review the details.",
          html: `<p1>Hey ${bankData.loanOfficerName},</p1>
  
  <p>A new loan application has been matched with your bank in the loan portal. Please <a href="${loginUrl}">log in</a> to review the details.</p>
  
  <p>We've prepared a summary of the loan application for your convenience. You can view it <a href="${pdfUrl}">here</a>.</p>
  
  <p>Looking forward to hearing back from you!</p>
   
  <p>Best,</p>
  <p>Isak</p>
  <p>Lended</p>
  `,
        },
      };
      console.log('Email data prepared:', JSON.stringify(emailData, null, 2));
  
      const emailDoc = await addDoc(emailRef, emailData);
      console.log('Email document created with ID:', emailDoc.id);
      return emailDoc.id;
    } catch (error) {
      console.error('Error creating email document:', error);
      console.error('Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      throw error;
    }
  };


const renderWorkbookUpload = () => {
    const handleWorkbookUpload = async () => {
      if (!workbookFile) return;
      setWorkbookUploading(true);
      setWorkbookUploadError(null);
      setWorkbookUploadProgress(0);

      try {
        // Upload to listings path so the parser cloud function can find it
        const storageRef = ref(storage, `listings/${loanId}/workbook/${workbookFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, workbookFile);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setWorkbookUploadProgress(progress);
          },
          (error) => {
            console.error('Workbook upload error:', error);
            setWorkbookUploadError('Upload failed. Please try again.');
            setWorkbookUploading(false);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            // Write to listings collection to trigger parser (2-step for onDocumentUpdated)
            const listingsDocRef = doc(db, 'listings', loanId);
            await setDoc(listingsDocRef, {
              workbookFileUploaded: false,
              workbookParsedAt: null,
              workbookParseFailed: null,
              recordType: 'standard',
              loanId,
              borrowerName: `${loan?.firstName || ''} ${loan?.lastName || ''}`.trim(),
              uploadedAt: serverTimestamp(),
            }, { merge: true });
            await updateDoc(listingsDocRef, { workbookFileUploaded: true });

            // Also update the loan doc — workbook upload kicks the deal into
            // "Awaiting Term Sheet" unless it's already further along the funnel.
            const loanDocRef = doc(db, 'loanApplications', loanId);
            const currentLoanSnap = await getDoc(loanDocRef);
            const currentStatus = currentLoanSnap.exists() ? currentLoanSnap.data().loanStatus : null;
            const downstreamStatuses = new Set([
              LoanStatuses.TERM_SHEET_GIVEN,
              LoanStatuses.TERM_SHEET_SIGNED,
              LoanStatuses.TERM_SHEET_EXTENDED,
              LoanStatuses.UNDERWRITING,
              LoanStatuses.CLOSING,
              LoanStatuses.FUNDED,
            ]);
            const workbookUpdates = {
              workbookUploaded: true,
              workbookComplete: true,
              recordType: 'standard',
              workbookURL: downloadURL,
              workbookFileName: workbookFile.name,
              workbookUploadedAt: new Date().toISOString(),
            };
            if (!downstreamStatuses.has(currentStatus)) {
              workbookUpdates.loanStatus = LoanStatuses.AWAITING_TERM_SHEET;
            }
            await updateDoc(loanDocRef, workbookUpdates);

            // Listen for parser to finish
            const unsubscribe = onSnapshot(listingsDocRef, async (snap) => {
              const parsedData = snap.data();
              if (parsedData?.workbookParsedAt) {
                unsubscribe();
                // Copy all parsed financial data to the loanApplications doc
                const vals = { ...parsedData };
                delete vals.workbookFileUploaded;
                delete vals.uploadedAt;
                Object.keys(vals).forEach(k => {
                  const v = vals[k];
                  if (v && typeof v === 'object' && !Array.isArray(v) && typeof v.toDate === 'function') delete vals[k];
                  if (k.startsWith('_')) delete vals[k];
                });
                await updateDoc(loanDocRef, { ...vals, workbookParsedAt: serverTimestamp() });

                // Fetch all uploaded documents for this loan to include in email
                const docsRef = collection(db, 'documentMetadata');
                const docsSnap = await getDocs(query(docsRef, where('loanApplicationId', '==', loanId)));
                const allDocs = docsSnap.docs.map(d => d.data());

                // Build document list HTML
                const docListHtml = allDocs.length > 0
                  ? allDocs.map(d => `<li><a href="${d.fileUrl || '#'}" style="color:#4f46e5;">${d.documentName || d.fileName || d.label || 'Document'}</a> ${d.label ? `(${d.label})` : ''}</li>`).join('')
                  : '<li>No additional documents uploaded yet</li>';

                // Package all uploaded documents into a single zip on Storage
                // so the workbook email can link a "Download all" button.
                // Best-effort: if a file fails to fetch (CORS, missing URL,
                // etc.) it's just skipped so the button still works for the
                // rest. If the whole thing fails we skip the button.
                let downloadAllUrl = '';
                try {
                  if (allDocs.length > 0) {
                    const zip = new JSZip();
                    let added = 0;
                    await Promise.allSettled(allDocs.map(async (d) => {
                      if (!d.fileUrl || !d.fileName) return;
                      try {
                        const resp = await fetch(d.fileUrl, { mode: 'cors' });
                        if (!resp.ok) return;
                        const blob = await resp.blob();
                        // Prefix each file with its label folder so the zip
                        // stays organized (e.g. "Personal Tax Return 2024/1040.pdf").
                        const folder = d.label || d.documentName || 'Other';
                        const safeName = d.fileName.replace(/[\\/:*?"<>|]/g, '_');
                        zip.file(`${folder}/${safeName}`, blob);
                        added += 1;
                      } catch (e) {
                        console.warn('[workbook email zip] fetch failed for', d.fileName, e.message);
                      }
                    }));
                    if (added > 0) {
                      const zipBlob = await zip.generateAsync({ type: 'blob' });
                      const zipRef = ref(storage, `loan-zips/${loanId}/all-documents-${Date.now()}.zip`);
                      const zipSnap = await uploadBytesResumable(zipRef, zipBlob);
                      downloadAllUrl = await getDownloadURL(zipSnap.ref);
                    }
                  }
                } catch (zipErr) {
                  console.warn('[workbook email zip] failed to build/upload zip:', zipErr.message || zipErr);
                }
                const downloadAllButton = downloadAllUrl
                  ? `<p style="margin-top:12px;"><a href="${downloadAllUrl}" style="display:inline-block;padding:8px 20px;background:#059669;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:bold;">Download All Documents (.zip)</a></p>`
                  : '';

                // Build financial summary — merge parsed data with loan data
                const borrowerName = `${loan?.firstName || ''} ${loan?.lastName || ''}`.trim() || 'Unknown Borrower';
            const fmtAmt = (n) => typeof n === 'number' ? '$' + n.toLocaleString() : 'N/A';

            // Send email to Max with deal summary + all documents
            try {
              const emailRef = collection(db, 'email');
              await addDoc(emailRef, {
                to: ['max@lended.ai', 'ben@lended.ai', 'isak@lended.ai'],
                from: 'Isak @ Lended <isak@lended.ai>',
                replyTo: 'isak@lended.ai',
                subject: `${borrowerName} — Workbook & Documents Uploaded`,
                message: {
                  subject: `${borrowerName} — Workbook & Documents Uploaded`,
                  html: (() => { /* financial email builder */
                    const L = { ...(loan || {}), ...vals };
                    const f = (v) => typeof v === 'number' ? '$' + v.toLocaleString() : (v ? '$' + Number(v).toLocaleString() : '$0');
                    const hdr = 'style="background:#4f46e5;color:#fff;padding:10px 16px;text-align:right;font-weight:bold;font-size:13px;"';
                    const cell = 'style="padding:8px 16px;text-align:right;border-bottom:1px solid #e2e8f0;font-size:13px;"';
                    const cellL = 'style="padding:8px 16px;border-bottom:1px solid #e2e8f0;font-size:13px;color:#374151;"';
                    const boldCell = 'style="padding:8px 16px;text-align:right;border-bottom:1px solid #e2e8f0;font-size:13px;font-weight:bold;"';
                    const boldCellL = 'style="padding:8px 16px;border-bottom:1px solid #e2e8f0;font-size:13px;font-weight:bold;color:#374151;"';

                    // Column layout — purely a relabel of the existing data
                    // (don't move fields). The parser reads workbook cols
                    // C/E/G/I into _2022/_2023/_2024/_interim — those slots
                    // actually hold 2023/2024/P&L-2025/Int-2026 data in the
                    // current workbook template. So we keep the same field
                    // keys and just update the headers. TTM is the only new
                    // column; parser reads it from column L into `_ttm`.
                    const onExt = L.onExtension2025 === true || L.taxesOnExtension === true;
                    const columns = [
                      { key: '2022',    label: 'TAXES 2023' },
                      { key: '2023',    label: 'TAXES 2024' },
                      { key: '2024',    label: onExt ? 'P+L 2025' : 'TAXES 2025' },
                      { key: 'interim', label: onExt ? 'Int 2026' : `FY ${L.interimYear || '2025'}` },
                      { key: 'ttm',     label: 'TTM' },
                    ];

                    // Financial row helper
                    const finRow = (label, field, bold) => {
                      const tl = bold ? boldCellL : cellL;
                      const tc = bold ? boldCell : cell;
                      const cells = columns.map((c) => `<td ${tc}>${f(L[`${field}_${c.key}`])}</td>`).join('');
                      return `<tr><td ${tl}>${label}</td>${cells}</tr>`;
                    };

                    const ads = (key) => f(L[`totalDebtService_${key}`] || L.annualDebtService);
                    const dscrCell = (key) => {
                      const v = L[`dscr_${key}`] !== undefined ? L[`dscr_${key}`] : L[`dscr_core_${key}`];
                      if (typeof v !== 'number') {
                        return `<td ${boldCell}>—</td>`;
                      }
                      const color = v >= 1.3 ? '#16a34a' : v >= 1.1 ? '#ea580c' : '#dc2626';
                      return `<td style="padding:8px 16px;text-align:right;font-size:13px;font-weight:bold;color:${color};">${v.toFixed(2)}x</td>`;
                    };

                    return `
<h2 style="color:#4f46e5;margin:0 0 20px 0;">${borrowerName} — Financial Overview</h2>

<table style="width:100%;border-collapse:collapse;margin-bottom:24px;">
  <thead>
    <tr>
      <th style="background:#4f46e5;color:#fff;padding:10px 16px;text-align:left;font-size:13px;border-radius:6px 0 0 0;">Metric</th>
      ${columns.map((c, i) => {
        const radius = i === columns.length - 1 ? 'border-radius:0 6px 0 0;' : '';
        return `<th style="background:#4f46e5;color:#fff;padding:10px 16px;text-align:right;font-weight:bold;font-size:13px;${radius}">${c.label}</th>`;
      }).join('')}
    </tr>
  </thead>
  <tbody>
    ${finRow('Gross Sales', 'grossSales', false)}
    ${finRow('COGS', 'cogs', false)}
    ${finRow('Gross Profit', 'grossProfit', true)}
    ${finRow('Net Profit', 'netProfit', true)}
    ${finRow('Officers Comp', 'officersComp', false)}
    ${finRow('Depreciation', 'depreciation', false)}
    ${finRow('Interest', 'interest', false)}
    <tr><td ${boldCellL}>Annual Debt Service</td>${columns.map((c) => `<td ${boldCell}>${ads(c.key)}</td>`).join('')}</tr>
    <tr style="background:#f0fdf4;"><td ${boldCellL}>DSCR (EBITDA+SDE)</td>${columns.map((c) => dscrCell(c.key)).join('')}</tr>
  </tbody>
</table>

<h3 style="color:#4f46e5;margin:24px 0 12px 0;">Sources & Uses</h3>
<table style="border-collapse:collapse;margin-bottom:24px;">
  <tr><td style="padding:6px 16px 6px 0;color:#374151;">Purchase Price</td><td style="padding:6px 0;font-weight:bold;">${f(L.purchasePrice)}</td></tr>
  <tr><td style="padding:6px 16px 6px 0;color:#374151;">SBA Loan Amount</td><td style="padding:6px 0;font-weight:bold;">${f(L.sbaLoanAmount)}</td></tr>
  <tr><td style="padding:6px 16px 6px 0;color:#374151;">Borrower Equity</td><td style="padding:6px 0;font-weight:bold;">${f(L.borrowerEquity)}</td></tr>
</table>

<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:20px;margin:16px 0;">
  <h3 style="color:#1e3a5f;margin:0 0 12px 0;">Deal Info</h3>
  <table style="width:100%;border-collapse:collapse;">
    <tr><td style="padding:6px 0;color:#64748b;">Borrower</td><td style="padding:6px 0;text-align:right;font-weight:bold;">${borrowerName}</td></tr>
    <tr style="border-top:1px solid #e2e8f0;"><td style="padding:6px 0;color:#64748b;">Email</td><td style="padding:6px 0;text-align:right;">${L.email || 'N/A'}</td></tr>
    <tr style="border-top:1px solid #e2e8f0;"><td style="padding:6px 0;color:#64748b;">Phone</td><td style="padding:6px 0;text-align:right;">${L.phoneNumber || 'N/A'}</td></tr>
    <tr style="border-top:1px solid #e2e8f0;"><td style="padding:6px 0;color:#64748b;">Business</td><td style="padding:6px 0;text-align:right;font-weight:bold;">${L.businessName || 'N/A'}</td></tr>
    <tr style="border-top:1px solid #e2e8f0;"><td style="padding:6px 0;color:#64748b;">Loan Purpose</td><td style="padding:6px 0;text-align:right;">${L.loanPurpose || 'Acquisition'}</td></tr>
    <tr style="border-top:1px solid #e2e8f0;"><td style="padding:6px 0;color:#64748b;">Industry</td><td style="padding:6px 0;text-align:right;">${L.industry || 'N/A'}</td></tr>
    <tr style="border-top:1px solid #e2e8f0;"><td style="padding:6px 0;color:#64748b;">State</td><td style="padding:6px 0;text-align:right;">${L.businessState || 'N/A'}</td></tr>
  </table>
</div>

<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:20px;margin:16px 0;">
  <h3 style="color:#1e3a5f;margin:0 0 12px 0;">Workbook</h3>
  <p><a href="${downloadURL}" style="display:inline-block;padding:8px 20px;background:#4f46e5;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:bold;">Download Workbook (${workbookFile.name})</a></p>
</div>

<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:20px;margin:16px 0;">
  <h3 style="color:#1e3a5f;margin:0 0 12px 0;">All Uploaded Documents (${allDocs.length})</h3>
  ${downloadAllButton}
  <ul style="line-height:1.8;padding-left:20px;">
    ${docListHtml}
  </ul>
</div>

${L.additionalNotes ? `<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:16px;margin:16px 0;"><strong>Notes:</strong> ${L.additionalNotes}</div>` : ''}

<p>Best,</p>
<p>Isak<br>(914) 414-1106<br><a href="mailto:isak@lended.ai">isak@lended.ai</a> | <a href="https://lended.ai">lended.ai</a></p>`;
                  })()
                },
              });
              console.log('Workbook email sent to max@lended.ai');

                // Mark email sent on the loan doc
                await updateDoc(loanDocRef, { workbookEmailSent: true });
              } catch (emailErr) {
                console.error('Error sending workbook email:', emailErr);
              }

              // Refresh loan data
              const updatedLoan = await getDoc(loanDocRef);
              if (updatedLoan.exists()) setLoan({ id: updatedLoan.id, ...updatedLoan.data() });
              setWorkbookUploading(false);
              setWorkbookFile(null);
              setWorkbookUploadProgress(0);
              }
              if (parsedData?.workbookParseFailed) {
                unsubscribe();
                setWorkbookUploadError(parsedData.workbookParseError || 'Workbook parse failed');
                setWorkbookUploading(false);
              }
            });
            // Timeout after 2 minutes
            setTimeout(() => { unsubscribe(); }, 120000);
          }
        );
      } catch (error) {
        console.error('Workbook upload error:', error);
        setWorkbookUploadError('Upload failed. Please try again.');
        setWorkbookUploading(false);
      }
    };

    return (
      <div className="mb-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div
          className="px-4 py-5 sm:px-6 flex justify-between items-center cursor-pointer"
          onClick={() => setIsWorkbookUploadExpanded(!isWorkbookUploadExpanded)}
        >
          <div className="flex items-center">
            <ArrowUpTrayIcon className="h-5 w-5 text-gray-500 mr-2" />
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">Workbook Upload</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Upload a completed workbook for this loan application
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {loan?.workbookUploaded && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Workbook Uploaded
              </span>
            )}
            {loan?.workbookEmailSent && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Email sent to Max
              </span>
            )}
            <ChevronDownIcon
              className={`h-5 w-5 text-gray-500 transform transition-transform duration-200 ${isWorkbookUploadExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
        {isWorkbookUploadExpanded && (
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Workbook File (.xlsx, .xls, .xlsm)
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls,.xlsm"
                  onChange={(e) => setWorkbookFile(e.target.files[0] || null)}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  disabled={workbookUploading}
                />
              </div>

              {workbookUploadError && (
                <div className="text-sm text-red-600">{workbookUploadError}</div>
              )}

              {workbookUploading && (
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${workbookUploadProgress}%` }}
                  ></div>
                  <p className="text-sm text-gray-500 mt-1">{workbookUploadProgress}% uploaded</p>
                </div>
              )}

              <button
                onClick={handleWorkbookUpload}
                disabled={!workbookFile || workbookUploading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                {workbookUploading ? 'Uploading...' : 'Upload Workbook'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

// Reclassify-file label options. Grouped categories so the dropdown is
// scannable for swaps like Business → Acquiring Business tax returns.
const RECLASSIFY_LABEL_GROUPS = [
  { category: 'Personal', items: [
    'Personal Financial Statement',
    'Personal Tax Return 2025',
    'Personal Tax Return 2024',
    'Personal Tax Return 2023',
    'Personal Tax Return 2022',
    'Resume',
    'Credit Report',
    'Bank Statements',
  ] },
  { category: 'Target Business', items: [
    'Business Tax Return 2025',
    'Business Tax Return 2024',
    'Business Tax Return 2023',
    'Business Tax Return 2022',
    'Interim P&L 2026 YTD',
    'Trailing 12 Months P&L',
    'Balance Sheet',
    'AR Aging',
    'AP Aging',
    'Debt Schedule',
    'Business Valuation',
  ] },
  { category: 'Acquiring Company (Business-to-Business)', items: [
    'Acquiring Business Tax Return 2025',
    'Acquiring Business Tax Return 2024',
    'Acquiring Business Tax Return 2023',
    'Acquiring Business Interim P&L 2026 YTD',
    'Acquiring Business P&L',
    'Acquiring Business Balance Sheet',
  ] },
  { category: 'Deal Documents', items: [
    'LOI',
    'CIM',
    'Articles of Organization',
    'Operating Agreement',
    'Lease Agreement',
    'Franchise Agreement',
    'Real Estate Valuation',
  ] },
];

const handleReclassifyStart = (doc) => {
    setReclassifyDocId(doc.id);
    setReclassifyLabelDraft(doc.label || doc.documentName || '');
    setReclassifyCustomDraft('');
  };

  const handleReclassifyCancel = () => {
    setReclassifyDocId(null);
    setReclassifyLabelDraft('');
    setReclassifyCustomDraft('');
  };

  const handleReclassifySave = async (docRecord) => {
    const newLabel = (reclassifyCustomDraft.trim() || reclassifyLabelDraft || '').trim();
    if (!newLabel) {
      alert('Pick a label or type a custom one.');
      return;
    }
    if (newLabel === (docRecord.label || docRecord.documentName)) {
      handleReclassifyCancel();
      return;
    }
    setReclassifySaving(true);
    try {
      await updateDoc(doc(db, 'documentMetadata', docRecord.id), {
        label: newLabel,
        documentName: newLabel,
        reclassifiedAt: serverTimestamp(),
        reclassifiedBy: 'Ben',
        previousLabel: docRecord.label || docRecord.documentName || null,
      });
      setAllLoanDocuments((prev) =>
        prev.map((d) => (d.id === docRecord.id ? { ...d, label: newLabel, documentName: newLabel } : d))
      );
      handleReclassifyCancel();
    } catch (err) {
      console.error('Reclassify failed:', err);
      alert('Could not change the label: ' + (err.message || err));
    } finally {
      setReclassifySaving(false);
    }
  };

  const renderUploadedFiles = () => {
    // Separate documents into standard (by label) and requested (by requestedDocumentName)
    const standardDocsByLabel = {};
    const requestedDocsList = []; // Holds metadata of uploaded requested files

    allLoanDocuments.forEach(doc => {
      if (doc.requestedDocumentName) {
        requestedDocsList.push(doc);
      } else if (doc.label) {
        // Group standard docs by label for easier lookup below (can be useful for rendering uploaded list)
        if (!standardDocsByLabel[doc.label]) {
          standardDocsByLabel[doc.label] = [];
        }
        standardDocsByLabel[doc.label].push(doc);
      }
    });

    // Combine standard docs into a flat list for rendering in the second section
    const standardUploadedDocs = Object.entries(standardDocsByLabel).flatMap(([label, docs]) =>
      docs.map(doc => ({ ...doc, displayLabel: label }))
    );

    // --- Refined Missing Items Calculation ---
    let missingItemsList = [];
    let relevantRequiredDocs = []; // Keep track of requirements for reference

    if (loan?.loanPurpose === 'workingCapital') {
        console.log("--- Starting Working Capital Check ---");
        console.log("Loan Data (at start of check):", loan);
        console.log("Loan Uploaded Documents (at start of check):", loan?.uploadedDocuments);

        // Keep this map consistent for user-friendly names -> expected keys
        const workingCapitalDocKeys = {
            "Personal Financial Statement": "Personal Financial Statement",
            "Personal Tax Returns": "Personal Tax Returns",
            "Business Tax Returns": "Business Tax Returns",
            "Interim P&L": "Interim P&L",
            "Balance Sheet": "Balance Sheet",
            "Debt Schedule": "Debt Schedule",
            "Bank Statements": "Bank Statements",
        };
        console.log("Working Capital Doc Keys Map:", workingCapitalDocKeys);

        // --- Direct Check based ONLY on loan.uploadedDocuments ---
        Object.entries(workingCapitalDocKeys).forEach(([docName, baseKey]) => {
            // Generate both potential key formats
            const keyWithSpace = baseKey; // e.g., "Bank Statements"
            const keyWithUnderscore = baseKey.replace(/\s+/g, '_'); // e.g., "Bank_Statements"

            // Check if *either* key exists and is true in uploadedDocuments
            const isUploadedWithSpace = loan?.uploadedDocuments?.[keyWithSpace];
            const isUploadedWithUnderscore = loan?.uploadedDocuments?.[keyWithUnderscore];
            const isUploaded = isUploadedWithSpace === true || isUploadedWithUnderscore === true; // Document is considered uploaded if either key is true

            // Log the checks clearly
            console.log(`Checking WC Doc (Flexible): Name="${docName}" | Key w/ Space="${keyWithSpace}" | Key w/ Underscore="${keyWithUnderscore}" | Uploaded Status: ${isUploaded} (Space: ${isUploadedWithSpace}, Underscore: ${isUploadedWithUnderscore})`);

            // Add to missing list ONLY if neither format is explicitly true
            if (!isUploaded) {
                if (!missingItemsList.includes(docName)) {
                    missingItemsList.push(docName);
                    console.log(`   -> ADDED "${docName}" to missing list because status is not true for either key format.`);
                }
            } else {
                console.log(`   -> SKIPPED "${docName}" because status is true for at least one key format.`);
            }
        });
        // --- End Direct Check ---

        // --- Check Credit Score separately ---
        loan?.personalGuarantors?.forEach((guarantor, index) => {
            const score = loan.creditScores?.[index];
            console.log(`Checking Credit Score for ${guarantor.firstName} ${guarantor.lastName} (Index: ${index}): Score = ${score}`);
            if (score === undefined || score === null || score === '' || isNaN(parseInt(score)) || parseInt(score) <= 0) {
               const missingItemName = `Credit Score for ${guarantor.firstName} ${guarantor.lastName}`;
               if (!missingItemsList.includes(missingItemName)) {
                  missingItemsList.push(missingItemName);
                  console.log(`   -> ADDED "${missingItemName}" to missing list.`);
               }
            }
        });

        console.log("--- Finished Working Capital Check ---");

    } else {
      // --- Fallback logic for non-workingCapital loans ---
      // Filter based on documentRequirements for non-WC loans
      relevantRequiredDocs = documentRequirements.filter(req => !req.Optional);
      console.log('Filtered Relevant Required Docs (Other):', relevantRequiredDocs.map(r => r.Name));

      missingItemsList = relevantRequiredDocs
        .filter(reqDoc => {
            const keyWithSpace = reqDoc.Name; // e.g., "Business Tax Returns"
            const keyWithUnderscore = reqDoc.Name.replace(/[()/\[\]]/g, '').replace(/\s+/g, '_').trim(); // e.g., "Business_Tax_Returns"
            // Check both formats for non-WC loans as well
            const isUploadedWithSpace = loan?.uploadedDocuments?.[keyWithSpace];
            const isUploadedWithUnderscore = loan?.uploadedDocuments?.[keyWithUnderscore];
            const isUploaded = isUploadedWithSpace === true || isUploadedWithUnderscore === true;

            console.log(`Checking Other Doc: Name="${reqDoc.Name}" | Key w/ Space="${keyWithSpace}" | Key w/ Underscore="${keyWithUnderscore}" | Uploaded Status: ${isUploaded}`);
            return !isUploaded; // Add if not explicitly true in either format
         })
        .map(doc => doc.Name);

      // Credit score check for non-WC loans
      loan?.personalGuarantors?.forEach((guarantor, index) => {
         const score = loan.creditScores?.[index];
         console.log(`Checking Credit Score (Other) for ${guarantor.firstName} ${guarantor.lastName} (Index: ${index}): Score = ${score}`);
         if (score === undefined || score === null || score === '' || isNaN(parseInt(score)) || parseInt(score) <= 0) {
             const missingItemName = `Credit Score for ${guarantor.firstName} ${guarantor.lastName}`;
             if (!missingItemsList.includes(missingItemName)) {
                 missingItemsList.push(missingItemName);
                 console.log(`   -> ADDED "${missingItemName}" to missing list (Other).`);
             }
         }
      });
    }

    const pendingRequested = loan?.requestedDocuments?.filter(req => req.status !== 'uploaded') || [];
    const totalMissingCount = missingItemsList.length + pendingRequested.length;
    console.log('Final Missing Items List before render:', missingItemsList);

    // Sort by uploadedAt when the user picks a recency option. uploadedAt is
    // usually a Firestore Timestamp (has toDate) or an ISO string. Falls back
    // to 0 so items with no timestamp sink to the bottom for "newest first".
    const uploadedAtMs = (d) => {
      const raw = d?.uploadedAt;
      if (!raw) return 0;
      if (raw?.toDate) return raw.toDate().getTime();
      if (raw?.seconds) return raw.seconds * 1000;
      const parsed = new Date(raw).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    const applySort = (list) => {
      if (uploadedFilesSort === 'newest') {
        return [...list].sort((a, b) => uploadedAtMs(b) - uploadedAtMs(a));
      }
      if (uploadedFilesSort === 'oldest') {
        return [...list].sort((a, b) => uploadedAtMs(a) - uploadedAtMs(b));
      }
      return list;
    };
    const sortedStandardDocs = applySort(standardUploadedDocs);
    const sortedRequestedDocs = applySort(requestedDocsList);

    return (
      <div className="mb-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center gap-4">
          <div
            className="flex-1 min-w-0 cursor-pointer"
            onClick={() => setIsUploadedFilesExpanded(!isUploadedFilesExpanded)}
          >
            <h3 className="text-lg leading-6 font-medium text-gray-900">Uploaded & Requested Documents</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              {totalMissingCount > 0
                ? `${totalMissingCount} item(s) pending (standard info/docs or requested docs)`
                : 'All known documents uploaded'}
            </p>
          </div>
          {isUploadedFilesExpanded && (standardUploadedDocs.length + requestedDocsList.length) > 0 && (
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
              {[
                { key: 'label',  label: 'By label' },
                { key: 'newest', label: 'Newest' },
                { key: 'oldest', label: 'Oldest' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setUploadedFilesSort(opt.key)}
                  className={`text-xs font-medium px-2.5 py-1 rounded-md transition-colors ${
                    uploadedFilesSort === opt.key
                      ? 'bg-white shadow-sm text-indigo-700'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          <div
            className="cursor-pointer flex-shrink-0"
            onClick={() => setIsUploadedFilesExpanded(!isUploadedFilesExpanded)}
          >
            <ChevronDownIcon
              className={`h-5 w-5 text-gray-500 transform transition-transform duration-200 ${isUploadedFilesExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
        {isUploadedFilesExpanded && (
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            {/* Missing Standard Documents/Info Summary — removed per request.
                Keep this comment so the surrounding render block stays readable. */}

             {/* Pending Requested Documents Summary */}
             {pendingRequested.length > 0 && (
              <div className="mb-4 bg-orange-50 p-4 border border-orange-100 rounded-md">
                 <div className="flex">
                   <div className="flex-shrink-0">
                     <ExclamationCircleIcon className="h-5 w-5 text-orange-400" aria-hidden="true" />
                   </div>
                   <div className="ml-3">
                     <h3 className="text-sm font-medium text-orange-800">Pending Requested Documents</h3>
                     <div className="mt-2 text-sm text-orange-700">
                       <ul className="list-disc pl-5 space-y-1">
                         {pendingRequested.map((doc, index) => (
                           <li key={`pending-${index}`}>{doc.name}</li>
                         ))}
                       </ul>
                     </div>
                   </div>
                 </div>
              </div>
             )}

            {/* Uploaded Documents List (Standard + Requested) */}
            {(standardUploadedDocs.length > 0 || requestedDocsList.length > 0) ? (
              <ul role="list" className="divide-y divide-gray-200">
                {/* Render Standard Uploaded Documents */}
                {sortedStandardDocs.map((docItem, index) => {
                  const isEditing = reclassifyDocId === docItem.id;
                  return (
                  <li key={`std-${docItem.id || index}`} className="pl-3 pr-4 py-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div className="w-0 flex-1 flex items-center">
                        <PaperClipIcon className="flex-shrink-0 h-5 w-5 text-gray-400" aria-hidden="true" />
                        <div className="ml-2 flex-1 w-0">
                          <p className="font-medium text-gray-900 truncate">{docItem.displayLabel}</p>
                          <p className="text-gray-500 truncate">{docItem.fileName || 'Unnamed File'}</p>
                          {docItem.ownerId && <p className="text-xs text-gray-400 truncate">Guarantor: {docItem.ownerId}</p>}
                          {uploadedFilesSort !== 'label' && uploadedAtMs(docItem) > 0 && (
                            <p className="text-xs text-gray-400 truncate">Uploaded {new Date(uploadedAtMs(docItem)).toLocaleString()}</p>
                          )}
                        </div>
                      </div>
                      <div className="ml-4 flex-shrink-0 flex items-center gap-3">
                        {!isEditing && (
                          <button
                            type="button"
                            onClick={() => handleReclassifyStart(docItem)}
                            className="font-medium text-xs text-gray-600 hover:text-indigo-600"
                            title="Change this file's label"
                          >
                            Reclassify
                          </button>
                        )}
                        <a
                          href={docItem.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-indigo-600 hover:text-indigo-500 flex items-center"
                        >
                          <ArrowTopRightOnSquareIcon className="h-5 w-5 mr-1" />
                          View
                        </a>
                      </div>
                    </div>
                    {isEditing && (
                      <div className="mt-3 ml-7 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                          <select
                            value={reclassifyLabelDraft}
                            onChange={(e) => setReclassifyLabelDraft(e.target.value)}
                            disabled={reclassifySaving}
                            className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          >
                            <option value="">Pick a label…</option>
                            {RECLASSIFY_LABEL_GROUPS.map((group) => (
                              <optgroup key={group.category} label={group.category}>
                                {group.items.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={reclassifyCustomDraft}
                            onChange={(e) => setReclassifyCustomDraft(e.target.value)}
                            placeholder="…or type a custom label (overrides)"
                            disabled={reclassifySaving}
                            className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          />
                        </div>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={handleReclassifyCancel}
                            disabled={reclassifySaving}
                            className="text-xs font-medium px-3 py-1.5 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReclassifySave(docItem)}
                            disabled={reclassifySaving || (!reclassifyLabelDraft && !reclassifyCustomDraft.trim())}
                            className="text-xs font-medium px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {reclassifySaving ? 'Saving…' : 'Save label'}
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
                })}

                {/* Render Requested Uploaded Documents */}
                 {requestedDocsList.length > 0 && (
                  <li className="pt-4 pb-2">
                      <h4 className="text-sm font-semibold text-gray-600">Uploaded Requested Documents:</h4>
                  </li>
                 )}
                {sortedRequestedDocs.map((docItem, index) => {
                  const isEditing = reclassifyDocId === docItem.id;
                  return (
                  <li key={`req-${docItem.id || index}`} className="pl-3 pr-4 py-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div className="w-0 flex-1 flex items-center">
                        <PaperClipIcon className="flex-shrink-0 h-5 w-5 text-blue-400" aria-hidden="true" />
                        <div className="ml-2 flex-1 w-0">
                          <p className="font-medium text-gray-900 truncate">{docItem.requestedDocumentName}</p>
                          <p className="text-gray-500 truncate">{docItem.fileName || 'Unnamed File'}</p>
                          {docItem.uploadedAt && <p className="text-xs text-gray-400 truncate">Uploaded: {new Date(docItem.uploadedAt).toLocaleDateString()}</p>}
                        </div>
                      </div>
                      <div className="ml-4 flex-shrink-0 flex items-center gap-3">
                        {!isEditing && (
                          <button
                            type="button"
                            onClick={() => handleReclassifyStart(docItem)}
                            className="font-medium text-xs text-gray-600 hover:text-indigo-600"
                            title="Change this file's label"
                          >
                            Reclassify
                          </button>
                        )}
                        <a
                          href={docItem.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-indigo-600 hover:text-indigo-500 flex items-center"
                        >
                          <ArrowTopRightOnSquareIcon className="h-5 w-5 mr-1" />
                          View
                        </a>
                      </div>
                    </div>
                    {isEditing && (
                      <div className="mt-3 ml-7 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                          <select
                            value={reclassifyLabelDraft}
                            onChange={(e) => setReclassifyLabelDraft(e.target.value)}
                            disabled={reclassifySaving}
                            className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          >
                            <option value="">Pick a label…</option>
                            {RECLASSIFY_LABEL_GROUPS.map((group) => (
                              <optgroup key={group.category} label={group.category}>
                                {group.items.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={reclassifyCustomDraft}
                            onChange={(e) => setReclassifyCustomDraft(e.target.value)}
                            placeholder="…or type a custom label (overrides)"
                            disabled={reclassifySaving}
                            className="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          />
                        </div>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={handleReclassifyCancel}
                            disabled={reclassifySaving}
                            className="text-xs font-medium px-3 py-1.5 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReclassifySave(docItem)}
                            disabled={reclassifySaving || (!reclassifyLabelDraft && !reclassifyCustomDraft.trim())}
                            className="text-xs font-medium px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {reclassifySaving ? 'Saving…' : 'Save label'}
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
                })}
              </ul>
            ) : (
               <p className="text-sm text-gray-500">No documents have been uploaded yet.</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDocumentStatus = (documentName, isIndividual) => {
    if (isIndividual) {
      return personalGuarantors.every((guarantor) =>
        uploadedDocuments.some(
          (doc) =>
            doc.documentName === documentName &&
            doc.ownerId === `${guarantor.firstName} ${guarantor.lastName}`
        )
      );
             } else {
      return uploadedDocuments.some(
        (doc) => doc.documentName === documentName && doc.ownerId === null
      );
    }
  };

  const renderProgressDetails = (stepName) => {
    const { documentStatus } = checkDocumentProgress();

    if (stepName === 'Pre-Check') {
      const preCheckDocs = documentRequirements.filter((doc) => doc.Individual);

      return (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-900">Document Progress</h4>
          <ul className="mt-2 space-y-2">
            {preCheckDocs.map((doc) => (
              <li key={doc.DocumentID} className="flex items-center">
                {documentStatus[doc.Name] ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <MinusCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                )}
                <span className="text-sm text-gray-700">{doc.Name}</span>
              </li>
            ))}
            <li className="flex items-center">
              {loan.creditScores && Object.values(loan.creditScores).some(score => score > 0) ? (
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              ) : (
                <MinusCircleIcon className="h-5 w-5 text-red-500 mr-2" />
              )}
              <span className="text-sm text-gray-700">Credit Score</span>
            </li>
          </ul>
        </div>
      );
    } else if (stepName === 'Loan Application') {
      const requiredFields = [
        { name: 'Business Age', field: 'businessAge' },
        { name: 'Business State', field: 'businessState' },
        { name: 'Business Summary', field: 'businessSummary' },
        { name: 'EBITDA', field: 'ebitda' },
        { name: 'Loan Size', field: 'loanSize' },
        { name: 'Real Estate Appraisal', field: 'realEstateAppraisal' },
        { name: 'Revenue', field: 'revenue' }
      ];

      return (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-900">Application Status</h4>
          <ul className="mt-2 space-y-2">
            {requiredFields.map((item) => (
              <li key={item.field} className="flex items-center">
                {loan[item.field] ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <MinusCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                )}
                <span className="text-sm text-gray-700">{item.name}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    } else if (stepName === 'Term Sheet') {
      const businessDocs = documentRequirements.filter((doc) => !doc.Individual);

      return (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-900">Document Progress</h4>
          <ul className="mt-2 space-y-2">
            {businessDocs.map((doc) => (
              <li key={doc.DocumentID} className="flex items-center">
                {documentStatus[doc.Name] ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <MinusCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                )}
                <span className="text-sm text-gray-700">{doc.Name}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return null;
  };

  const getCreditScore = (loan) => {
    if (!loan || !loan.creditScores) {
      return 'N/A';
    }
    const scores = Object.values(loan.creditScores);
    return scores.length > 0 ? Math.max(...scores.map(score => parseInt(score) || 0)) : 'N/A';
  };

  const getDSCR = (loan, year) => {
    return loan?.financialAnalysis?.[year]?.dscr?.toFixed(2) || 'N/A';
  };

  if (!loan) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Render functions for different document types
  const renderPersonalFinancialStatement = (ocrResult, index) => {
    return (
      <div className="bg-gray-100 p-4 rounded mb-4">
        {Object.entries(ocrResult).map(([key, value]) => {
          if (typeof value === 'object' && !Array.isArray(value)) {
            return (
              <div key={key} className="mb-4">
                <h5 className="text-lg font-medium mb-2">{key}</h5>
                {Object.entries(value).map(([subKey, subValue]) => (
                  <div key={subKey} className="flex items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700 mr-2">{subKey}</label>
                    <input
                      type="text"
                      value={subValue}
                      onChange={(e) => handleOcrInputChange(e, index, `${key}.${subKey}`)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                ))}
              </div>
            );
          } else if (Array.isArray(value)) {
            return (
              <div key={key} className="mb-4">
                <h5 className="text-lg font-medium mb-2">{key}</h5>
                {value.map((item, subIndex) => (
                  <div key={subIndex} className="mb-2">
                    {Object.entries(item).map(([subKey, subValue]) => (
                      <div key={subKey} className="flex items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700 mr-2">{subKey}</label>
                        <input
                          type="text"
                          value={subValue}
                          onChange={(e) => handleOcrInputChange(e, index, `${key}.${subIndex}.${subKey}`)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          } else {
            return (
              <div key={key} className="flex items-center mb-2">
                <label className="block text-sm font-medium text-gray-700 mr-2">{key}</label>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleOcrInputChange(e, index, key)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            );
          }
        })}
      </div>
    );
  };

  const renderCreditBureauReport = (ocrResult, index) => {
    return (
      <div className="bg-gray-100 p-4 rounded mb-4">
        <div className="flex items-center mb-2">
          <label className="block text-sm font-medium text-gray-700 mr-2">Credit Score</label>
          <input
            type="text"
            value={ocrResult.CreditScore}
            onChange={(e) => handleOcrInputChange(e, index, 'CreditScore')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
      </div>
    );
  };

  const renderBankStatement = (ocrResult, index) => {
    return (
      <div className="bg-gray-100 p-4 rounded mb-4">
        <div className="flex items-center mb-2">
          <label className="block text-sm font-medium text-gray-700 mr-2">Balance</label>
          <input
            type="text"
            value={ocrResult.Balance}
            onChange={(e) => handleOcrInputChange(e, index, 'Balance')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
      </div>
    );
  };

  const renderPersonalTaxReturns = (ocrResult, index) => {
    return (
      <div className="bg-gray-100 p-4 rounded mb-4">
        <div className="flex items-center mb-2">
          <label className="block text-sm font-medium text-gray-700 mr-2">Salary</label>
          <input
            type="text"
            value={ocrResult.Salary}
            onChange={(e) => handleOcrInputChange(e, index, 'Salary')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
      </div>
    );
  };

  const renderBusinessTaxReturns = (ocrResult, index) => {
    const priorityFields = ['compensation_of_officers', 'ordinary_business_income', 'gross_receipts_or_sales'];
    const year = selectedDocument.label;
    
    const renderField = (key, value) => (
      <div key={key} className="flex items-center mb-2">
        <label className="block text-sm font-medium text-gray-700 mr-2">
          {key.replace(/_/g, ' ').charAt(0).toUpperCase() + key.replace(/_/g, ' ').slice(1)}:
        </label>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => handleOcrInputChange(e, index, key)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        />
      </div>
    );
  
    const renderOtherDeductions = (deductions) => {
      if (Array.isArray(deductions)) {
        return deductions.map((deduction, i) => (
          <div key={i} className="flex items-center mb-2">
            <input
              type="checkbox"
              checked={!!selectedDeductions[year]?.[deduction.category]}
              onChange={() => handleDeductionToggle(year, deduction.category, Number(deduction.amount))}
              className="mr-2"
            />
            <label className="block text-sm font-medium text-gray-700 mr-2">{deduction.category}:</label>
            <input
              type="number"
              value={deduction.amount || 0}
              onChange={(e) => {
                const newAmount = e.target.value;
                handleOcrInputChange(e, index, `other_deductions.${i}.amount`);
                if (selectedDeductions[year]?.[deduction.category]) {
                  handleDeductionToggle(year, deduction.category, Number(newAmount));
                }
              }}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        ));
      } else if (typeof deductions === 'object' && deductions !== null) {
        return Object.entries(deductions).map(([category, amount]) => (
          <div key={category} className="flex items-center mb-2">
            <input
              type="checkbox"
              checked={!!selectedDeductions[year]?.[category]}
              onChange={() => handleDeductionToggle(year, category, Number(amount))}
              className="mr-2"
            />
            <label className="block text-sm font-medium text-gray-700 mr-2">{category}:</label>
            <input
              type="number"
              value={amount || 0}
              onChange={(e) => {
                const newAmount = e.target.value;
                handleOcrInputChange(e, index, `other_deductions.${category}`);
                if (selectedDeductions[year]?.[category]) {
                  handleDeductionToggle(year, category, Number(newAmount));
                }
              }}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        ));
      }
      return <div>No deductions data available</div>;
    };
  
    return (
      <div className="bg-gray-100 p-4 rounded mb-4">
        {/* Priority fields */}
        {priorityFields.map(field => ocrResult[field] !== undefined && renderField(field, ocrResult[field]))}
  
        {/* Other fields (excluding priority fields and other_deductions) */}
        {Object.entries(ocrResult)
          .filter(([key]) => !priorityFields.includes(key) && key !== 'other_deductions')
          .map(([key, value]) => renderField(key, value))}
  
        {/* Other Deductions */}
        {ocrResult.other_deductions && (
          <div className="mt-4">
            <h5 className="text-lg font-medium mb-2">Other Deductions</h5>
            {renderOtherDeductions(ocrResult.other_deductions)}
          </div>
        )}
      </div>
    );
  };
  // Handle choosing a deal to move forward with
  const handleChooseDeal = async (businessIndex, dealStage = 'In Underwriting') => {
    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      const businesses = loan.businesses || [];

      // Update the chosen business
      const updatedBusinesses = businesses.map((b, idx) => {
        if (idx === businessIndex) {
          return {
            ...b,
            chosenAsBuyer: true,
            dealStage: dealStage,
            chosenAt: new Date().toISOString()
          };
        }
        // Unmark other businesses as chosen
        return { ...b, chosenAsBuyer: false };
      });

      // Get the chosen business to track broker info
      const chosenBusiness = updatedBusinesses[businessIndex];

      await updateDoc(loanRef, {
        businesses: updatedBusinesses,
        loanStatus: dealStage === 'In Underwriting' ? LoanStatuses.UNDERWRITING :
                   dealStage === 'Closing' ? LoanStatuses.CLOSING : loan.loanStatus,
        chosenBusinessIndex: businessIndex,
        chosenBusinessBrokerId: chosenBusiness?.brokerId || null,
        lastUpdated: serverTimestamp()
      });

      // Refresh loan data
      const updatedLoan = await getDoc(loanRef);
      setLoan({ id: updatedLoan.id, ...updatedLoan.data() });

      alert(`Successfully marked as moving forward with "${chosenBusiness?.businessName || 'this deal'}"`);
    } catch (error) {
      console.error('Error choosing deal:', error);
      alert('Error updating deal status');
    }
  };

  // Handle changing deal stage for a chosen business
  const handleDealStageChange = async (businessIndex, newStage) => {
    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      const businesses = loan.businesses || [];

      const updatedBusinesses = businesses.map((b, idx) => {
        if (idx === businessIndex) {
          return { ...b, dealStage: newStage };
        }
        return b;
      });

      // Map stage to loan status
      const statusMap = {
        'Prequalified': LoanStatuses.PREQUALIFIED_FOR,
        'In Underwriting': LoanStatuses.UNDERWRITING,
        'Closing': LoanStatuses.CLOSING,
        'Funded': LoanStatuses.FUNDED
      };

      await updateDoc(loanRef, {
        businesses: updatedBusinesses,
        loanStatus: statusMap[newStage] || loan.loanStatus,
        lastUpdated: serverTimestamp()
      });

      // Refresh loan data
      const updatedLoan = await getDoc(loanRef);
      setLoan({ id: updatedLoan.id, ...updatedLoan.data() });
    } catch (error) {
      console.error('Error updating deal stage:', error);
      alert('Error updating stage');
    }
  };

  // Handle unchoosing a deal
  const handleUnchooseDeal = async (businessIndex) => {
    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      const businesses = loan.businesses || [];

      const updatedBusinesses = businesses.map((b, idx) => {
        if (idx === businessIndex) {
          return { ...b, chosenAsBuyer: false, dealStage: null };
        }
        return b;
      });

      await updateDoc(loanRef, {
        businesses: updatedBusinesses,
        loanStatus: LoanStatuses.PREQUALIFIED_FOR,
        chosenBusinessIndex: null,
        chosenBusinessBrokerId: null,
        lastUpdated: serverTimestamp()
      });

      // Refresh loan data
      const updatedLoan = await getDoc(loanRef);
      setLoan({ id: updatedLoan.id, ...updatedLoan.data() });
    } catch (error) {
      console.error('Error unchoosing deal:', error);
      alert('Error updating deal status');
    }
  };

  const handleStatusChange = async (newStatus, requiresAmountInput = false, modalTitle = '', amountField = '', requiresImage = false) => {
    // Only Request Documents opens the modal (which sends an email). All other
    // status changes in the Options dropdown are pure internal tracking — no
    // email goes to the client. Move to Closing used to open this same modal
    // and email a "closing documents" notice; that's intentionally gone.
    if (newStatus === LoanStatuses.DOCUMENT_CLARIFICATION) {
      setRequestModalPurpose(newStatus);
      setPendingDocRequests([]);
      setCurrentDocRequestInput('');
      setIsRequestDocsModalOpen(true);
      return;
    }

    // --- Handle actions requiring an Amount input ---
    if (requiresAmountInput) {
      setAmountModalContext({ // Use separate context ONLY for amount modal
        action: newStatus,
        title: modalTitle,
        field: amountField,
        requiresImage: requiresImage
      });
      setModalAmountInput('');
      setIsAmountModalOpen(true);
      return; // Stop further processing here
    }
    // --- End Handle Amount Actions ---

    // --- Handle direct status changes (Underwriting, Closing, Funded, etc.) ---
    // Internal-only — never sends client email.
    let success = false;
    let finalAlertMessage = '';

    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      const updateData = { loanStatus: newStatus };
      // Add specific timestamps
      if (newStatus === LoanStatuses.UNDERWRITING) updateData.inUnderwritingAt = serverTimestamp();
      else if (newStatus === LoanStatuses.REJECTED) updateData.rejectedAt = serverTimestamp();
      else if (newStatus === LoanStatuses.CLOSING) updateData.closingInitiatedAt = serverTimestamp();
      else if (newStatus === LoanStatuses.FUNDED) updateData.fundedAt = serverTimestamp();

      await updateDoc(loanRef, updateData);
      success = true;
      console.log(`Firestore updated loan ${loanId} status to ${newStatus}`);

      // Update local state
      setLoan(prevLoan => {
        const updatedLoan = { ...prevLoan, loanStatus: newStatus };
        if (newStatus === LoanStatuses.UNDERWRITING) updatedLoan.inUnderwritingAt = new Date();
        else if (newStatus === LoanStatuses.REJECTED) updatedLoan.rejectedAt = new Date();
        else if (newStatus === LoanStatuses.CLOSING) updatedLoan.closingInitiatedAt = new Date();
        else if (newStatus === LoanStatuses.FUNDED) updatedLoan.fundedAt = new Date();
        return updatedLoan;
      });

      finalAlertMessage = `Loan status updated to ${newStatus}.`;
    } catch (error) {
      console.error(`Error updating loan status to ${newStatus}:`, error);
      finalAlertMessage = `Failed to update loan status. Error: ${error.message || 'Unknown error'}`;
      success = false;
    } finally {
        alert(finalAlertMessage);
    }
  };

  // --- Functions for Request Documents Modal ---
  const handleAddDocRequest = () => {
    if (currentDocRequestInput.trim() && !pendingDocRequests.includes(currentDocRequestInput.trim())) {
      setPendingDocRequests([...pendingDocRequests, currentDocRequestInput.trim()]);
      setCurrentDocRequestInput(''); // Clear input after adding
    }
  };

  const handleRemoveDocRequest = (docNameToRemove) => {
    setPendingDocRequests(pendingDocRequests.filter(name => name !== docNameToRemove));
  };

  // Function to send "Documents Requested" Email
  const sendDocumentsRequestedEmail = async (loanData, requestedDocNames) => {
    if (!loanData || !loanData.emails || !loanData.emails[0] || !requestedDocNames || requestedDocNames.length === 0) {
      console.error('Missing data for documents requested email:', { loanData, requestedDocNames });
      return false; // Indicate failure
    }

    try {
      const emailRef = collection(db, 'email');
      const storageRef = ref(storage, 'download (2).png'); // Correct logo path if needed
      const logoUrl = await getDownloadURL(storageRef);
      const sharedUploadUrl = `https://lended.ai/shared-upload/${loanId}`; // Or login URL

      // Create an HTML list of requested documents
      const documentListHtml = requestedDocNames.map(name => `<li style="color: #000000;">${name}</li>`).join('');

      const emailData = {
        to: loanData.emails[0],
        from: {
          email: "isak@lended.ai",
          name: "Isak @ Lended"
        },
        replyTo: "isak@lended.ai",
        subject: `Action Required: Additional Documents Needed for Your Loan Application`,
        templateName: "DocumentsRequested",
        sentAt: new Date().toISOString(), // Use a descriptive template name
        message: {
          subject: `Action Required: Additional Documents Needed`,
          firstName: loanData.firstName || 'there',
          html: `<div style="white-space: normal; word-wrap: break-word;">
<p style="color: #000000;">Hey ${loanData.firstName || 'there'},</p>

<p style="color: #000000;">To continue processing your loan application, we need a few more documents. Please upload the following items:</p>

<ul style="margin-top: 10px; margin-bottom: 10px; padding-left: 20px;">
  ${documentListHtml}
</ul>

<p style="color: #000000;">You can upload these documents securely through your application portal:</p>
<p style="color: #000000;"><a href="${sharedUploadUrl}">Upload Requested Documents</a></p>

<p style="color: #000000;">Please upload these as soon as possible so we can keep your application moving forward. Let us know if you have any questions.</p>

<p style="color: #000000;">Best,</p>

<div style="margin-top: 20px;">
  <p style="margin: 0; color: #000000;"><strong>Isak</strong></p>
  <p style="margin: 0; color: #000000;">CEO | Lended</p>
  <p style="margin: 0;"><a href="mailto:isak@lended.ai">isak@lended.ai</a></p>
  <p style="margin: 0;"><a href="tel:+19144141106">914-414-1106</a></p>
  <p style="margin-top: 10px;">
    <a href="https://lended.ai">
      <img src="${logoUrl}" alt="Lended" style="height: 30px; width: auto;">
    </a>
  </p>
</div>
</div>`,
        },
      };

      const emailDoc = await addDoc(emailRef, emailData);
      console.log('Documents Requested email created with ID:', emailDoc.id);
      return true; // Indicate success
    } catch (error) {
      console.error('Error sending documents requested email:', error);
      return false; // Indicate failure
    }
  };

  // Update the function signature to accept targetStatus
  const submitDocumentRequests = async () => {
    // *** Use the state variable directly ***
    const targetStatus = requestModalPurpose;
    const requestedDocNames = [...pendingDocRequests];

    // Check if the purpose was set correctly
    if (!targetStatus || (targetStatus !== LoanStatuses.CLOSING && targetStatus !== LoanStatuses.DOCUMENT_CLARIFICATION)) {
        console.error("Invalid request modal purpose:", targetStatus);
        alert("An internal error occurred: Invalid action type. Please try again.");
        return;
    }

    if (requestedDocNames.length === 0) {
      alert('Please add at least one document name to request.');
      return;
    }

    let success = false;
    let emailSent = false;
    let finalAlertMessage = '';

    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      const requestedDocsData = requestedDocNames.map(name => ({ name: name, status: 'pending', requestedAt: new Date().toISOString() }));

      // Prepare update data - THIS is where the status changes
      const updateData = {
        loanStatus: targetStatus, // Set to CLOSING or DOCUMENT_CLARIFICATION
        requestedDocuments: requestedDocsData, // Add the list of requested docs
      };
      // Add specific timestamps
      if (targetStatus === LoanStatuses.DOCUMENT_CLARIFICATION) {
          updateData.documentsRequestedAt = serverTimestamp();
      } else if (targetStatus === LoanStatuses.CLOSING) {
          updateData.closingInitiatedAt = serverTimestamp();
      }

      // Update Firestore
      console.log(`Updating Firestore loan ${loanId} with status: ${targetStatus}`, updateData);
      await updateDoc(loanRef, updateData);
      success = true;
      console.log(`Firestore updated for ${targetStatus} request.`);

      // Send the correct Email
      const currentLoanDataForEmail = { ...loan, loanStatus: targetStatus }; // Use updated status for email context
      if (targetStatus === LoanStatuses.CLOSING) {
        emailSent = await sendClosingDocumentsRequestedEmail(currentLoanDataForEmail, requestedDocNames);
      } else {
        emailSent = await sendDocumentsRequestedEmail(currentLoanDataForEmail, requestedDocNames);
      }

      // Update local state ONLY if Firestore update was successful
      if (success) {
        setLoan(prevLoan => {
           const updatedLoan = {
               ...prevLoan,
               loanStatus: targetStatus, // Reflect status change locally
               requestedDocuments: requestedDocsData,
           };
           if (targetStatus === LoanStatuses.DOCUMENT_CLARIFICATION) {
               updatedLoan.documentsRequestedAt = new Date(); // Estimate client time
           } else if (targetStatus === LoanStatuses.CLOSING) {
               updatedLoan.closingInitiatedAt = new Date(); // Estimate client time
           }
           return updatedLoan;
        });
      }

       // Determine final message
       const actionVerb = targetStatus === LoanStatuses.CLOSING ? 'Loan moved to Closing and requests sent' : 'Document requests sent';
       if (success && emailSent) {
           finalAlertMessage = `${actionVerb} successfully. Notification email sent.`;
       } else if (success && !emailSent) {
           finalAlertMessage = `${actionVerb}, but failed to send notification email.`;
       } else {
           finalAlertMessage = `Failed to process ${targetStatus} request. Please try again.`;
       }

    } catch (error) {
      console.error(`Error submitting ${targetStatus} requests:`, error);
      finalAlertMessage = `Failed to process ${targetStatus} request. Error: ${error.message}`;
      success = false;
    } finally {
        // Close modal only on success
        if (success) {
            setIsRequestDocsModalOpen(false);
            setPendingDocRequests([]);
            setCurrentDocRequestInput('');
            setRequestModalPurpose(null); // Reset purpose state
        }
        alert(finalAlertMessage);
    }
  };
  // --- End Functions for Request Documents Modal ---

  const renderResumes = (ocrResult, index) => {
    return (
      <div className="bg-gray-100 p-4 rounded mb-4">
        {ocrResult.experiences.map((experience, i) => (
          <div key={i} className="mb-4">
            <div className="flex items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 mr-2">Company</label>
              <input
                type="text"
                value={experience.company}
                onChange={(e) => handleOcrInputChange(e, index, `experiences.${i}.company`)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div className="flex items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 mr-2">Duration</label>
              <input
                type="text"
                value={experience.duration}
                onChange={(e) => handleOcrInputChange(e, index, `experiences.${i}.duration`)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div className="flex items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 mr-2">Position</label>
              <input
                type="text"
                value={experience.position}
                onChange={(e) => handleOcrInputChange(e, index, `experiences.${i}.position`)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div className="flex items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 mr-2">Summary</label>
              <input
                type="text"
                value={experience.summary}
                onChange={(e) => handleOcrInputChange(e, index, `experiences.${i}.summary`)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
          </div>
        ))}
      </div>
    );
  };
  const renderFinancialItem = (label, value, isTotal = false) => (
    <div className={`py-2 ${isTotal ? 'font-bold' : ''}`}>
      <span className="text-gray-600">{label}:</span>
      <span className="float-right">{formatCurrency(value)}</span>
    </div>
  );
  const renderPersonalFinancialInfo = () => {
    if (personalFinancialData.length === 0 || !loan || !loan.personalGuarantors) {
      console.log('No personal financial data available:', { personalFinancialData, loan });
      return <div>No personal financial data available.</div>;
    }
  
    console.log('Active guarantor data:', personalFinancialData[activeGuarantorIndex]);

    const activeGuarantorData = personalFinancialData[activeGuarantorIndex];
    if (!activeGuarantorData) {
      console.log('No data for active guarantor:', { activeGuarantorIndex, personalFinancialData });
      return <div>No data available for the selected guarantor.</div>;
    }

    return (
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Guarantor's Personal Financial Information</h3>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <div className="sm:divide-y sm:divide-gray-200">
            {/* Tabs for multiple guarantors */}
            <div className="mb-4 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {loan.personalGuarantors.map((guarantor, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveGuarantorIndex(index)}
                    className={`${
                      index === activeGuarantorIndex
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    {`${guarantor.firstName} ${guarantor.lastName}`}
                  </button>
                ))}
              </nav>
            </div>
            {/* Render active guarantor's data */}
            {activeGuarantorData && (
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-6">
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-2">Assets</h4>
                  {renderFinancialItem("Cash on Hand", activeGuarantorData.Assets?.CashOnHandAndInBanks)}
                  {renderFinancialItem("Savings Accounts", activeGuarantorData.Assets?.SavingsAccounts)}
                  {renderFinancialItem("Stocks and Bonds", activeGuarantorData.Assets?.StocksAndBonds)}
                  {renderFinancialItem("Real Estate", activeGuarantorData.Assets?.RealEstate)}
                  {renderFinancialItem("Automobiles", activeGuarantorData.Assets?.Automobiles)}
                  {renderFinancialItem("Other Assets", activeGuarantorData.Assets?.OtherAssets)}
                  {renderFinancialItem("Total Assets", activeGuarantorData.Assets?.Total, true)}
                </div>
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-2">Liabilities</h4>
                  {renderFinancialItem("Accounts Payable", activeGuarantorData.Liabilities?.AccountsPayable)}
                  {renderFinancialItem("Notes Payable", activeGuarantorData.Liabilities?.NotesPayableToBanksAndOthers)}
                  {renderFinancialItem("Auto Installment", activeGuarantorData.Liabilities?.InstallmentAccountAuto)}
                  {renderFinancialItem("Other Installment", activeGuarantorData.Liabilities?.InstallmentAccountOther)}
                  {renderFinancialItem("Life Insurance Loans", activeGuarantorData.Liabilities?.LoanAgainstLifeInsurance)}
                  {renderFinancialItem("Mortgages", activeGuarantorData.Liabilities?.MortgagesOnRealEstate)}
                  {renderFinancialItem("Unpaid Taxes", activeGuarantorData.Liabilities?.UnpaidTaxes)}
                  {renderFinancialItem("Other Liabilities", activeGuarantorData.Liabilities?.OtherLiabilities)}
                  {renderFinancialItem("Total Liabilities", activeGuarantorData.Liabilities?.TotalLiabilities, true)}
                </div>
              </div>
            )}
            <div className="py-4 sm:py-5 sm:px-6">
              <div className="text-lg font-bold">
                Net Worth: {formatCurrency(calculateNetWorth(activeGuarantorData))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  const formatCurrency = (value) => {
    const numValue = parseFloat(value);
    return isNaN(numValue) ? 'N/A' : `$${numValue.toLocaleString()}`;
  };
  const renderOcrMetadata = (ocrMetadata, documentName, index) => {
    switch (documentName) {
      case 'Personal Financial Statement':
        return renderPersonalFinancialStatement(ocrMetadata, index);
      case 'Credit Bureau Report':
        return renderCreditBureauReport(ocrMetadata.ocrResult, index);
      case 'Bank Statement for Equity Injection':
        return renderBankStatement(ocrMetadata.ocrResult, index);
      case 'Personal Tax Returns':
        return renderPersonalTaxReturns(ocrMetadata.ocrResult, index);
      case 'Business Tax Returns':
        return renderBusinessTaxReturns(ocrMetadata.ocrResult, index);
      case 'Resumes for Key Team Members':
        return renderResumes(ocrMetadata.ocrResult, index);
      default:
        return null;
    }
  };

  // Separate documents into personal and non-personal categories
  const personalDocuments = {};
  const nonPersonalDocuments = {};

  Object.entries(documentsByLabel).forEach(([label, documents]) => {
    const requirement = documentRequirements.find((req) => req.Labels.includes(label));
    if (requirement && requirement.Individual) {
      documents.forEach((document) => {
        if (!personalDocuments[document.ownerId]) {
          personalDocuments[document.ownerId] = [];
        }
        personalDocuments[document.ownerId].push({ label, document });
      });
    } else {
      if (!nonPersonalDocuments[label]) {
        nonPersonalDocuments[label] = [];
      }
      nonPersonalDocuments[label].push(...documents);
    }
  });

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  const getFinancialData = (key) => {
    const years = ['2 Years Ago', 'Last Year', 'Current Year'];
    return years.map(year => ({
      name: year,
      value: parseFloat(loan?.financialAnalysis?.[`Business Tax Return - ${year}`]?.[key] || 0)
    }));
  };

  const GRAPH_COLORS = ['#008081', '#6593F5', '#3FE0D0'];

  const FinancialStatCard = ({ item }) => (
    <div className="overflow-hidden rounded-lg bg-white px-6 py-8 shadow sm:p-8">
      <dt className="truncate text-lg font-medium text-gray-500">{item.name}</dt>
      <dd className="mt-2 flex flex-col">
        <div className="flex items-baseline justify-between">
          <div className="flex items-baseline text-3xl font-semibold text-black">
            {item.stat}
            <span className="ml-2 text-base font-medium text-gray-500">from {item.previousStat}</span>
          </div>
          <div
            className={classNames(
              item.changeType === 'increase' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
              'inline-flex items-baseline rounded-full px-3 py-1 text-base font-medium'
            )}
          >
            {item.changeType === 'increase' ? (
              <ArrowUpIcon className="-ml-1 mr-0.5 h-6 w-6 flex-shrink-0 self-center text-green-500" aria-hidden="true" />
            ) : (
              <ArrowDownIcon className="-ml-1 mr-0.5 h-6 w-6 flex-shrink-0 self-center text-red-500" aria-hidden="true" />
            )}
            <span className="sr-only"> {item.changeType === 'increase' ? 'Increased' : 'Decreased'} by </span>
            {item.change}
          </div>
        </div>
        <div className="mt-6 h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={item.data}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill={item.color} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </dd>
    </div>
  );

  const financialStats = [
    { 
      name: 'DSCR', 
      stat: loan?.financialAnalysis?.['Business Tax Return - Current Year']?.dscr?.toFixed(2) || 'N/A', 
      previousStat: loan?.financialAnalysis?.['Business Tax Return - Last Year']?.dscr?.toFixed(2) || 'N/A', 
      change: ((loan?.financialAnalysis?.['Business Tax Return - Current Year']?.dscr / loan?.financialAnalysis?.['Business Tax Return - Last Year']?.dscr - 1) * 100).toFixed(2) + '%' || 'N/A',
      changeType: loan?.financialAnalysis?.['Business Tax Return - Current Year']?.dscr > loan?.financialAnalysis?.['Business Tax Return - Last Year']?.dscr ? 'increase' : 'decrease',
      data: getFinancialData('dscr'),
      color: GRAPH_COLORS[0]
    },
    { 
      name: 'Net Operating Income', 
      stat: `$${loan?.financialAnalysis?.['Business Tax Return - Current Year']?.noi?.toLocaleString() || 'N/A'}`, 
      previousStat: `$${loan?.financialAnalysis?.['Business Tax Return - Last Year']?.noi?.toLocaleString() || 'N/A'}`, 
      change: ((loan?.financialAnalysis?.['Business Tax Return - Current Year']?.noi / loan?.financialAnalysis?.['Business Tax Return - Last Year']?.noi - 1) * 100).toFixed(2) + '%' || 'N/A',
      changeType: loan?.financialAnalysis?.['Business Tax Return - Current Year']?.noi > loan?.financialAnalysis?.['Business Tax Return - Last Year']?.noi ? 'increase' : 'decrease',
      data: getFinancialData('noi'),
      color: GRAPH_COLORS[1]
    },
    { 
      name: 'Ordinary Business Income', 
      stat: `$${loan?.financialAnalysis?.['Business Tax Return - Current Year']?.ordinaryBusinessIncome?.toLocaleString() || 'N/A'}`, 
      previousStat: `$${loan?.financialAnalysis?.['Business Tax Return - Last Year']?.ordinaryBusinessIncome?.toLocaleString() || 'N/A'}`, 
      change: ((loan?.financialAnalysis?.['Business Tax Return - Current Year']?.ordinaryBusinessIncome / loan?.financialAnalysis?.['Business Tax Return - Last Year']?.ordinaryBusinessIncome - 1) * 100).toFixed(2) + '%' || 'N/A',
      changeType: loan?.financialAnalysis?.['Business Tax Return - Current Year']?.ordinaryBusinessIncome > loan?.financialAnalysis?.['Business Tax Return - Last Year']?.ordinaryBusinessIncome ? 'increase' : 'decrease',
      data: getFinancialData('ordinaryBusinessIncome'),
      color: GRAPH_COLORS[2]
    },
  ];

  const renderWorkingCapitalUse = () => {
    if (loan.loanPurpose === 'workingCapital' && loan.workingCapitalUse) {
      return (
        <div className="mt-8 bg-blue-50 shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-blue-900">Working Capital Use</h3>
            <p className="mt-1 max-w-2xl text-sm text-blue-500">Details on how the working capital will be used</p>
          </div>
          <div className="border-t border-blue-200 px-4 py-5 sm:p-0">
            <dl className="sm:divide-y sm:divide-blue-200">
              <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-blue-500">Intended Use</dt>
                <dd className="mt-1 text-sm text-blue-900 sm:mt-0 sm:col-span-2">{loan.workingCapitalUse}</dd>
              </div>
            </dl>
          </div>
        </div>
      );
    }
    return null;
  };
  const updateLoanStatus = async (newStatus) => {
    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      await updateDoc(loanRef, { loanStatus: newStatus });
      setLoan(prevLoan => ({ ...prevLoan, loanStatus: newStatus }));
      alert(`Loan status updated to: ${newStatus}`);
    } catch (error) {
      console.error('Error updating loan status:', error);
      alert('Failed to update loan status. Please try again.');
    }
  };
  const handleReject = () => {
    updateLoanStatus(LoanStatuses.REJECTED);
  };

  const handleApprove = () => {
    updateLoanStatus(LoanStatuses.FUNDED);
  };

  const handlePrequalify = () => {
    // This function might be redundant now, handleStatusChange handles opening the modal
    // Keep it if used elsewhere, otherwise consider removing
    setIsAmountModalOpen(true);
  };

  // Function to send Prequalification Email
  const sendPrequalificationEmail = async (loanData, amount) => {
    if (!loanData || !loanData.emails || !loanData.emails[0] || !amount) {
      console.error('Missing data for prequalification email:', { loanData, amount });
      // Don't alert here, let the main function handle success/failure alerts
      return false; // Indicate failure
    }

    try {
      const emailRef = collection(db, 'email');
      const storageRef = ref(storage, 'download (2).png'); // Correct logo path if needed
      const logoUrl = await getDownloadURL(storageRef);
      const sharedUploadUrl = `https://lended.ai/shared-upload/${loanId}`; // Or login URL

      const emailData = {
        to: loanData.emails[0],
        from: {
          email: "isak@lended.ai",
          name: "Isak @ Lended"
        },
        replyTo: "isak@lended.ai",
        subject: `Congratulations! You've Been Prequalified!`,
        templateName: "PrequalificationNotice",
        sentAt: new Date().toISOString(),
        message: {
          subject: `Congratulations! You've Been Prequalified!`,
          firstName: loanData.firstName || 'there',
          html: `<div style="white-space: normal; word-wrap: break-word;">
<p style="color: #000000;">Hey ${loanData.firstName || 'there'},</p>
<p style="color: #000000;">Great news! Based on the initial information provided, you have been prequalified for a loan amount of <strong>$${Number(amount).toLocaleString()}</strong>.</p>
<p style="color: #000000;">This prequalification is an important step forward. Our team will now begin the underwriting process.</p>
<p style="color: #000000;">You can monitor the status and manage any further document requests through your portal:</p>
<p style="color: #000000;"><a href="${sharedUploadUrl}">Access Your Application Portal</a></p>
<p style="color: #000000;">We'll keep you updated on the progress. Feel free to reach out if you have any questions.</p>
<p style="color: #000000;">Best,</p>
<div style="margin-top: 20px;">
  <p style="margin: 0; color: #000000;"><strong>Isak</strong></p>
  <p style="margin: 0; color: #000000;">CEO | Lended</p>
  <p style="margin: 0;"><a href="mailto:isak@lended.ai">isak@lended.ai</a></p>
  <p style="margin: 0;"><a href="tel:+19144141106">914-414-1106</a></p>
  <p style="margin-top: 10px;">
    <a href="https://lended.ai">
      <img src="${logoUrl}" alt="Lended" style="height: 30px; width: auto;">
    </a>
  </p>
</div>
</div>`,
        },
      };

      const emailDoc = await addDoc(emailRef, emailData);
      console.log('Prequalification email created with ID:', emailDoc.id);
      return true; // Indicate success
    } catch (error) {
      console.error('Error sending prequalification email:', error);
      return false; // Indicate failure
    }
  };

  // Function to send Term Sheet Email
  const sendTermSheetEmail = async (loanData, amount) => {
    if (!loanData || !loanData.emails || !loanData.emails[0] || !amount) {
      console.error('Missing data for term sheet email:', { loanData, amount });
      return false; // Indicate failure
    }

    try {
      const emailRef = collection(db, 'email');
      const storageRef = ref(storage, 'download (2).png'); // Correct logo path if needed
      const logoUrl = await getDownloadURL(storageRef);
      const sharedUploadUrl = `https://lended.ai/shared-upload/${loanId}`; // Or login URL

      const emailData = {
        to: loanData.emails[0],
        from: {
          email: "isak@lended.ai",
          name: "Isak @ Lended"
        },
        replyTo: "isak@lended.ai",
        subject: `Your Loan Term Sheet is Ready!`,
        templateName: "TermSheetNotice",
        sentAt: new Date().toISOString(),
        message: {
          subject: `Your Loan Term Sheet is Ready!`,
          firstName: loanData.firstName || 'there',
          html: `<div style="white-space: normal; word-wrap: break-word;">
<p style="color: #000000;">Hey ${loanData.firstName || 'there'},</p>
<p style="color: #000000;">We're pleased to inform you that a term sheet for a loan amount of <strong>$${Number(amount).toLocaleString()}</strong> has been received.</p>
<p style="color: #000000;">You will recieve an email with more details on the terms and next steps shortly.</p>
<p style="color: #000000;">You can access your application portal here:</p>
<p style="color: #000000;"><a href="${sharedUploadUrl}">Access Your Application Portal</a></p>
<p style="color: #000000;">Please let us know if you have any questions regarding the terms.</p>
<p style="color: #000000;">Best,</p>
<div style="margin-top: 20px;">
  <p style="margin: 0; color: #000000;"><strong>Isak</strong></p>
  <p style="margin: 0; color: #000000;">CEO | Lended</p>
  <p style="margin: 0;"><a href="mailto:isak@lended.ai">isak@lended.ai</a></p>
  <p style="margin: 0;"><a href="tel:+19144141106">914-414-1106</a></p>
  <p style="margin-top: 10px;">
    <a href="https://lended.ai">
      <img src="${logoUrl}" alt="Lended" style="height: 30px; width: auto;">
    </a>
  </p>
</div>
</div>`,
        },
      };

      const emailDoc = await addDoc(emailRef, emailData);
      console.log('Term sheet email created with ID:', emailDoc.id);
      return true; // Indicate success
    } catch (error) {
      console.error('Error sending term sheet email:', error);
      return false; // Indicate failure
    }
  };

  const submitModalAmount = async () => {
    const { action, field, requiresImage } = amountModalContext;
    const enteredAmount = modalAmountInput; // Use a stable variable

    if (!enteredAmount) {
      alert('Please enter an amount.');
      return;
    }
    if (!action || !field) {
      console.error("Modal context not set correctly");
      alert('An error occurred. Please try again.');
      return;
    }

    let success = false;
    let emailSent = false;
    let finalAlertMessage = '';

    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      const updateData = {
        loanStatus: action,
        [field]: parseFloat(enteredAmount),
      };
      let imageUrl = null;

      // Prepare timestamp and potentially image URL
      if (action === LoanStatuses.PREQUALIFIED_FOR) {
        updateData.prequalifiedAt = serverTimestamp();
        if (requiresImage) {
          // --- Canvas/Image Logic ---
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = 800;
          canvas.height = 800;
          const templateImage = new window.Image();
          templateImage.crossOrigin = "anonymous";
          templateImage.src = prequalTemplate;

          await new Promise((resolve, reject) => {
            templateImage.onload = () => {
              try {
                ctx.drawImage(templateImage, 0, 0, canvas.width, canvas.height);
                ctx.fillStyle = '#5ce1e6';
                ctx.font = 'bold 120px Arial';
                ctx.textAlign = 'center';
                ctx.fillText(`$${Number(enteredAmount).toLocaleString()}`, canvas.width / 2, 400);
                resolve();
              } catch (err) { reject(err); }
            };
            templateImage.onerror = (e) => { reject(new Error(`Failed to load image: ${e.message}`)); }
          });

          const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
          const storageRef = ref(storage, `prequalification-images/${loanId}.png`);
          await uploadBytesResumable(storageRef, blob);
          imageUrl = await getDownloadURL(storageRef);
          updateData.prequalificationImageUrl = imageUrl;
          // --- End Image Logic ---
        }
      } else if (action === LoanStatuses.TERM_SHEET_EXTENDED) {
        updateData.termSheetExtendedAt = serverTimestamp();
      }

      // --- Update Firestore ---
      await updateDoc(loanRef, updateData);
      console.log('Firestore document updated successfully.');
      success = true; // Mark Firestore update as successful

      // --- Send Email ---
      // Construct data needed for email using current state + new updates
      const currentLoanDataForEmail = {
        ...loan,
        [field]: parseFloat(enteredAmount),
        loanStatus: action,
        // Ensure emails and firstName are included if they exist in loan state
        emails: loan?.emails,
        firstName: loan?.firstName
      };

      if (action === LoanStatuses.PREQUALIFIED_FOR) {
        emailSent = await sendPrequalificationEmail(currentLoanDataForEmail, enteredAmount);
      } else if (action === LoanStatuses.TERM_SHEET_EXTENDED) {
        emailSent = await sendTermSheetEmail(currentLoanDataForEmail, enteredAmount);
      } else {
        emailSent = true; // No email needed for other actions passing through here
      }

      // --- Update Local State ---
      if (success) { // Only update local state if Firestore update was successful
          setLoan(prevLoan => {
            const updatedLoan = {
              ...prevLoan,
              loanStatus: action,
              [field]: parseFloat(enteredAmount),
            };
            if (action === LoanStatuses.PREQUALIFIED_FOR) {
              updatedLoan.prequalifiedAt = new Date(); // Use client date as estimate
              if (imageUrl) { updatedLoan.prequalificationImageUrl = imageUrl; }
            } else if (action === LoanStatuses.TERM_SHEET_EXTENDED) {
              updatedLoan.termSheetExtendedAt = new Date(); // Use client date as estimate
            }
            return updatedLoan;
          });
      }

      // --- Determine Final Alert Message ---
      if (success && emailSent) {
        finalAlertMessage = `Loan status updated to ${action} for $${enteredAmount}. Email notification sent.`;
      } else if (success && !emailSent) {
        finalAlertMessage = `Loan status updated to ${action} for $${enteredAmount}, but failed to send email notification.`;
      } else {
         // This case shouldn't happen if logic is correct, but as a fallback:
        finalAlertMessage = `Failed to update loan status. Please try again.`;
      }

    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      finalAlertMessage = `Failed to update ${field}. Please try again. Error: ${error.message}`;
      success = false; // Ensure success is false on error
    } finally {
       // --- Close Modal and Show Alert ---
       if (success) { // Only close modal on success
         setIsAmountModalOpen(false);
         setModalAmountInput('');
         setAmountModalContext({ action: null, title: '', field: '', requiresImage: false });
       }
       alert(finalAlertMessage); // Show the determined message
    }
  };

  const renderLoanSizeAndBusinessSummary = () => {
    if (!loan.loanSize || !loan.businessSummary) {
      return null;
    }

    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-5 py-4">
            <h3 className="text-sm font-semibold text-gray-900">Requested loan</h3>
            <p className="mt-0.5 text-xs text-gray-500">Amount currently requested</p>
          </div>
          <div className="p-5">
            <div className="flex items-center">
              <div className="mr-4 flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50"><CurrencyDollarIcon className="h-6 w-6 text-emerald-600" /></div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-400">Loan amount</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">
                  ${Number(loan.loanSize).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-5 py-4">
            <h3 className="text-sm font-semibold text-gray-900">Business overview</h3>
            <p className="mt-0.5 text-xs text-gray-500">Quick context for underwriting</p>
          </div>
          <div className="p-5">
            <div className="flex items-start">
              <div className="mr-4 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50"><BuildingOfficeIcon className="h-6 w-6 text-indigo-600" /></div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-400">Summary</p>
                <p className="mt-1 text-sm leading-6 text-gray-700">
                  {loan.businessSummary}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmployeeCount = () => {
    if (!loan.numberOfEmployees) {
      return null;
    }

    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-900">Employees</h3>
          <p className="mt-0.5 text-xs text-gray-500">Current team size</p>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center">
              <div className="mr-4 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50"><UserGroupIcon className="h-6 w-6 text-blue-600" /></div>
            <div>
              <p className="text-lg font-semibold text-gray-800">Total Employees</p>
                <p className="text-2xl font-bold text-gray-900">
                {loan.numberOfEmployees}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCreditScoreCard = () => {
    if (!loan.personalGuarantors || !loan.creditScores) {
      return null;
    }

    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-900">Credit scores</h3>
          <p className="mt-0.5 text-xs text-gray-500">Personal guarantor credit</p>
        </div>
        <div className="px-4 py-5 sm:p-6">
          {loan.personalGuarantors.map((guarantor, index) => (
            <div key={index} className="flex items-center mb-4 last:mb-0">
              <div className="mr-4 flex h-11 w-11 items-center justify-center rounded-xl bg-purple-50"><CreditCardIcon className="h-6 w-6 text-purple-600" /></div>
              <div>
                <p className="text-lg font-semibold text-gray-800">{`${guarantor.firstName} ${guarantor.lastName}`}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {loan.creditScores[index] || 'N/A'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const toggleProgressCard = () => {
    setIsProgressCardExpanded(!isProgressCardExpanded);
  };

  const toggleUploadedFiles = () => {
    setIsUploadedFilesExpanded(!isUploadedFilesExpanded);
  };

  if (!loan) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const createFollowUpEmail = async () => {
    try {
      if (!loan || !loan.emails || !loan.emails[0]) {
        alert('Cannot send email: Missing applicant email address');
        return;
      }
      
      // Get the logo URL from Firebase Storage
      const storageRef = ref(storage, 'download (2).png');
      const logoUrl = await getDownloadURL(storageRef);
      
      const emailRef = collection(db, 'email');
      const sharedUploadUrl = `https://lended.ai/shared-upload/${loanId}`;
      
      const emailData = {
        to: loan.emails[0],
        from: {
          email: "isak@lended.ai",
          name: "Isak @ Lended"
        },
        replyTo: "isak@lended.ai",
        subject: "Next Steps for Your Loan Application...",
        templateName: "FollowUp",
        sentAt: new Date().toISOString(),
        message: {
          subject: "Next Steps for Your Loan Application...",
          firstName: loan.firstName || 'there',
          html: `<div style="white-space: normal; word-wrap: break-word;">
<p style="color: #000000;">Hey ${loan.firstName || 'there'},</p>

<p style="color: #000000;">Thanks for hopping on the call earlier. Here's the <a href="${sharedUploadUrl}">link to finish your application</a>.</p>

<p style="color: #000000;">Once you finish getting those files uploaded, we'll be able to start putting together your application and will have updates for you within 24 hours.</p>

<p style="color: #000000;">If you have any questions feel free to reach out to me via email or phone!</p>

<p style="color: #000000;">Best,</p>

<div style="margin-top: 20px;">
  <p style="margin: 0; color: #000000;"><strong>Isak</strong></p>
  <p style="margin: 0; color: #000000;">CEO | Lended</p>
  <p style="margin: 0;"><a href="mailto:isak@lended.ai">isak@lended.ai</a></p>
  <p style="margin: 0;"><a href="tel:+19144141106">914-414-1106</a></p>
  <p style="margin-top: 10px;">
    <a href="https://lended.ai">
      <img src="${logoUrl}" alt="Lended" style="height: 30px; width: auto;">
    </a>
  </p>
</div>
</div>`,
        },
      };

      const emailDoc = await addDoc(emailRef, emailData);
      console.log('Follow-up email created with ID:', emailDoc.id);
      alert('Follow-up email sent successfully!');
      return emailDoc.id;
    } catch (error) {
      console.error('Error creating follow-up email:', error);
      alert('Error sending follow-up email. Please try again.');
      throw error;
    }
  };

  const sendReminderEmail = async () => {
    try {
      if (!loan || !loan.emails || !loan.emails[0]) {
        alert('Cannot send email: Missing applicant email address');
      return;
    }

      // Get the logo URL from Firebase Storage
      const storageRef = ref(storage, 'download (2).png');
      const logoUrl = await getDownloadURL(storageRef);
      
      const emailRef = collection(db, 'email');
      const sharedUploadUrl = `https://lended.ai/shared-upload/${loanId}`;

      const emailData = {
        to: loan.emails[0],
        from: {
          email: "isak@lended.ai",
          name: "Isak @ Lended"
        },
        replyTo: "isak@lended.ai",
        subject: "Reminder for Your Loan Application...",
        templateName: "Reminder",
        sentAt: new Date().toISOString(),
        message: {
          subject: "Reminder for Your Loan Application...",
          firstName: loan.firstName || 'there',
          html: `<div style="white-space: normal; word-wrap: break-word;">
<p style="color: #000000;">Hey ${loan.firstName || 'there'},</p>

<p style="color: #000000;">I noticed that your loan application is still pending. I wanted to reach out to see if you had any questions or difficulties with it.</p>

<p style="color: #000000;">You can access your application portal here: <a href="${sharedUploadUrl}">Upload Documents</a></p>

<p style="color: #000000;">Feel free to reach out if you need any clarification or help with the process. Once the rest of the files are uploaded, we'll be able to start putting together your application and will have updates for you within 24 hours.</p>

<p style="color: #000000;">Best regards,</p>

<div style="margin-top: 20px;">
  <p style="margin: 0; color: #000000;"><strong>Isak</strong></p>
  <p style="margin: 0; color: #000000;">CEO | Lended</p>
  <p style="margin: 0;"><a href="mailto:isak@lended.ai">isak@lended.ai</a></p>
  <p style="margin: 0;"><a href="tel:+19144141106">914-414-1106</a></p>
  <p style="margin-top: 10px;">
    <a href="https://lended.ai">
      <img src="${logoUrl}" alt="Lended" style="height: 30px; width: auto;">
    </a>
  </p>
</div>
</div>`,
        },
      };

      const emailDoc = await addDoc(emailRef, emailData);
      console.log('Reminder email created with ID:', emailDoc.id);
      alert('Reminder email sent successfully!');
      return emailDoc.id;
    } catch (error) {
      console.error('Error creating reminder email:', error);
      alert('Error sending reminder email. Please try again.');
      throw error;
    }
  };

  // Add this new function to render the broker card
  const renderBrokerCard = () => {
    if (!brokerInfo) return null;

    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-900">Broker</h3>
          <p className="mt-0.5 text-xs text-gray-500">Referring relationship</p>
        </div>
        <div className="p-6">
          <div className="flex items-center">
            <div className="mr-4 flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50"><UserGroupIcon className="h-6 w-6 text-indigo-600" /></div>
            <div>
              <h4 className="text-lg font-semibold text-gray-800">
                {brokerInfo.firstName} {brokerInfo.lastName}
              </h4>
              <p className="text-gray-600">{brokerInfo.companyName}</p>
              {brokerInfo.email && (
                <p className="text-gray-600">
                  <a href={`mailto:${brokerInfo.email}`} className="text-blue-600 hover:text-blue-800">
                    {brokerInfo.email}
                  </a>
                </p>
              )}
              {brokerInfo.phone && (
                <p className="text-gray-600">
                  <a href={`tel:${brokerInfo.phone}`} className="text-blue-600 hover:text-blue-800">
                    {brokerInfo.phone}
                  </a>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Get businesses array from loan data
  const getBusinesses = () => {
    if (!loan) return [];
    if (loan.businesses && loan.businesses.length > 0) {
      return loan.businesses.map((business, index) => ({ ...business, index }));
    }
    // Legacy: check for business data at root level
    if (loan.businessName || loan.loanSize) {
      return [{
        businessName: loan.businessName,
        loanSize: loan.loanSize,
        industry: loan.industry,
        businessState: loan.businessState,
        businessSummary: loan.businessSummary,
        loanStructure: loan.loanStructure,
        prequalified: loan.prequalified,
        prequalificationData: loan.prequalificationData,
        index: -1
      }];
    }
    return [];
  };

  // Handle prequalification submission
  const handlePrequalifySubmit = async () => {
    if (!prequalifyData.amount) {
      alert('Please enter an amount');
      return;
    }

    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      const businesses = getBusinesses();

      const prequalificationInfo = {
        amount: prequalifyData.amount,
        structure: prequalifyData.structure,
        prequalifiedAt: new Date(),
        prequalifiedBy: 'admin',
      };

      if (prequalifyBusinessIndex === -1) {
        // Legacy: update at root level
        await updateDoc(loanRef, {
          prequalified: true,
          prequalificationData: prequalificationInfo,
        });
      } else {
        // Update specific business in array
        const updatedBusinesses = [...(loan.businesses || [])];
        updatedBusinesses[prequalifyBusinessIndex] = {
          ...updatedBusinesses[prequalifyBusinessIndex],
          prequalified: true,
          prequalificationData: prequalificationInfo,
        };
        await updateDoc(loanRef, { businesses: updatedBusinesses });
      }

      setIsPrequalifyModalOpen(false);
      setPrequalifyData({ amount: '', structure: { sbaLoan: '', sellerNote: '', cashDown: '' } });
      alert('Business prequalified successfully!');
    } catch (error) {
      console.error('Error prequalifying business:', error);
      alert('Error prequalifying business');
    }
  };

  // Render business cards
  const renderBusinessCards = () => {
    const businesses = getBusinesses();
    if (businesses.length === 0) return null;

    return (
      <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-8">
        <div className="px-5 py-4 border-b border-gray-100 bg-white">
          <h3 className="text-sm font-semibold text-gray-900">Businesses / Listings</h3>
          <p className="mt-0.5 text-xs text-gray-500">Select a business to review its details</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {businesses.filter(b => b.businessName || b.loanSize).map((business, idx) => (
              <div
                key={`business-${business.index}`}
                className={`relative border rounded-xl p-4 cursor-pointer transition-all ${
                  business.chosenAsBuyer
                    ? 'border-blue-300 bg-blue-50 ring-1 ring-blue-200'
                    : business.termSheetRequested
                      ? 'border-amber-300 bg-amber-50'
                      : selectedBusinessIndex === business.index
                        ? 'border-indigo-300 bg-indigo-50 ring-1 ring-indigo-200'
                        : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-sm'
                }`}
                onClick={() => setSelectedBusinessIndex(
                  selectedBusinessIndex === business.index ? null : business.index
                )}
              >
                {/* Chosen as Buyer badge - highest priority */}
                {business.chosenAsBuyer && (
                  <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-400">
                      <CheckCircleIcon className="w-4 h-4 mr-1" />
                      Chosen as Buyer
                    </span>
                    {business.listingName && (
                      <span className="text-xs text-blue-600 mt-1">
                        For: {business.listingName}
                      </span>
                    )}
                  </div>
                )}
                {/* Term Sheet Requested badge */}
                {business.termSheetRequested && !business.chosenAsBuyer && (
                  <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                      <DocumentTextIcon className="w-4 h-4 mr-1" />
                      Term Sheet Requested
                    </span>
                  </div>
                )}
                {/* Prequalified badge */}
                {business.prequalified && !business.termSheetRequested && !business.chosenAsBuyer && (
                  <div className="absolute top-2 right-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircleIcon className="w-4 h-4 mr-1" />
                      Prequalified
                    </span>
                  </div>
                )}
                {/* Ready to Prequalify badge - shows when all docs uploaded but not prequalified */}
                {(business.allDocumentsUploaded || loan?.allDocumentsUploaded) && !business.prequalified && !business.termSheetRequested && !business.chosenAsBuyer && loan?.loanStatus !== LoanStatuses.PREQUALIFIED_FOR && (
                  <div className="absolute top-2 right-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
                      <CheckBadgeIcon className="w-4 h-4 mr-1" />
                      Ready to Prequalify
                    </span>
                  </div>
                )}

                <div className="flex items-start">
                  <BuildingOfficeIcon className="h-10 w-10 text-indigo-500 mr-3 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-lg font-semibold text-gray-900 truncate">
                      {business.businessName || 'Unnamed Business'}
                    </h4>
                    {business.loanSize && (
                      <p className="text-sm text-gray-600">
                        Loan Size: ${Number(business.loanSize).toLocaleString()}
                      </p>
                    )}
                    {business.businessState && (
                      <p className="text-sm text-gray-500">{business.businessState}</p>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="mt-4 flex flex-col gap-2">
                  <div className="flex gap-2 flex-wrap">
                    {!business.prequalified && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPrequalifyBusinessIndex(business.index);
                          setIsPrequalifyModalOpen(true);
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
                      >
                        <CheckBadgeIcon className="w-4 h-4 mr-1" />
                        Prequalify
                      </button>
                    )}
                    {/* Move Forward button - only for prequalified businesses not yet chosen */}
                    {business.prequalified && !business.chosenAsBuyer && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChooseDeal(business.index);
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                      >
                        <ArrowRightIcon className="w-4 h-4 mr-1" />
                        Move Forward
                      </button>
                    )}
                  </div>
                  {/* Stage progression for chosen businesses */}
                  {business.chosenAsBuyer && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <select
                        value={business.dealStage || 'In Underwriting'}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleDealStageChange(business.index, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="text-sm border border-blue-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="In Underwriting">In Underwriting</option>
                        <option value="Closing">Closing</option>
                        <option value="Funded">Funded</option>
                      </select>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Are you sure you want to remove this business as the chosen deal?')) {
                            handleUnchooseDeal(business.index);
                          }
                        }}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 border border-red-200"
                      >
                        <XCircleIcon className="w-3 h-3 mr-1" />
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Business Details Panel */}
          {selectedBusinessIndex !== null && (
            <div className="mt-6 border-t pt-6">
              {(() => {
                const business = getBusinesses().find(b => b.index === selectedBusinessIndex);
                if (!business) return null;

                return (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {business.businessName || 'Unnamed Business'} - Details
                      </h4>
                      <button
                        onClick={() => setSelectedBusinessIndex(null)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <span className="sr-only">Close</span>
                        ✕
                      </button>
                    </div>

                    {/* Form Answers */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-3">Application Details</h5>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <div>
                          <dt className="text-gray-500">Loan Size</dt>
                          <dd className="font-medium">${Number(business.loanSize || 0).toLocaleString()}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">State</dt>
                          <dd className="font-medium">{business.businessState || 'N/A'}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Business Age</dt>
                          <dd className="font-medium">{business.businessAge || 'N/A'}</dd>
                        </div>
                        <div>
                          <dt className="text-gray-500">Purchase Type</dt>
                          <dd className="font-medium">{business.purchaseType || 'N/A'}</dd>
                        </div>
                        {business.loanStructure && (
                          <>
                            <div>
                              <dt className="text-gray-500">SBA Loan</dt>
                              <dd className="font-medium">{business.loanStructure.sbaLoan || 'N/A'}%</dd>
                            </div>
                            <div>
                              <dt className="text-gray-500">Seller Note</dt>
                              <dd className="font-medium">{business.loanStructure.sellerNote || 'N/A'}%</dd>
                            </div>
                            <div>
                              <dt className="text-gray-500">Cash Down</dt>
                              <dd className="font-medium">{business.loanStructure.cashDown || 'N/A'}%</dd>
                            </div>
                          </>
                        )}
                      </dl>
                      {business.businessSummary && (
                        <div className="mt-4">
                          <dt className="text-gray-500 text-sm">Business Summary</dt>
                          <dd className="mt-1 text-sm text-gray-900">{business.businessSummary}</dd>
                        </div>
                      )}
                    </div>

                    {/* Uploaded Documents for this Business */}
                    {(() => {
                      const businessDocs = allLoanDocuments.filter(doc =>
                        doc.businessIndex !== undefined &&
                        doc.businessIndex !== null &&
                        doc.businessIndex == business.index &&
                        !doc.ownerId // Exclude personal/guarantor documents
                      );

                      if (businessDocs.length === 0) return null;

                      return (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <h5 className="font-medium text-blue-900 mb-3 flex items-center">
                            <DocumentTextIcon className="w-5 h-5 mr-2" />
                            Uploaded Documents ({businessDocs.length})
                          </h5>
                          <ul className="space-y-2">
                            {businessDocs.map((doc, idx) => (
                              <li key={idx} className="flex items-center justify-between text-sm">
                                <div className="flex items-center">
                                  <DocumentIcon className="w-4 h-4 text-blue-600 mr-2" />
                                  <span className="text-blue-900">{doc.label || doc.documentName || 'Document'}</span>
                                </div>
                                <a
                                  href={doc.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 underline"
                                >
                                  View
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}

                    {/* Prequalification Info */}
                    {business.prequalified && business.prequalificationData && (
                      <div className="bg-green-50 rounded-lg p-4">
                        <h5 className="font-medium text-green-900 mb-3 flex items-center">
                          <CheckCircleIcon className="w-5 h-5 mr-2" />
                          Prequalification Details
                        </h5>
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          <div>
                            <dt className="text-green-700">Amount</dt>
                            <dd className="font-medium text-green-900">
                              ${Number(business.prequalificationData.amount || 0).toLocaleString()}
                            </dd>
                          </div>
                          {business.prequalificationData.structure && (
                            <>
                              <div>
                                <dt className="text-green-700">SBA Loan</dt>
                                <dd className="font-medium text-green-900">
                                  {business.prequalificationData.structure.sbaLoan || 'N/A'}%
                                </dd>
                              </div>
                              <div>
                                <dt className="text-green-700">Seller Note</dt>
                                <dd className="font-medium text-green-900">
                                  {business.prequalificationData.structure.sellerNote || 'N/A'}%
                                </dd>
                              </div>
                              <div>
                                <dt className="text-green-700">Cash Down</dt>
                                <dd className="font-medium text-green-900">
                                  {business.prequalificationData.structure.cashDown || 'N/A'}%
                                </dd>
                              </div>
                            </>
                          )}
                        </dl>
                      </div>
                    )}

                    {/* Prequalification Tools - Expandable Sections */}
                    <div className="border-t pt-4 mt-4">
                      <h5 className="font-medium text-gray-900 mb-3">Prequalification Tools</h5>

                      {/* Prequalification Analysis Section */}
                      <div className="border rounded-lg mb-2 overflow-hidden">
                        <button
                          onClick={() => setExpandedPrequalSection(expandedPrequalSection === 'analysis' ? null : 'analysis')}
                          className="w-full px-4 py-3 bg-indigo-50 hover:bg-indigo-100 flex items-center justify-between text-left"
                        >
                          <span className="font-medium text-indigo-900">Prequalification Analysis</span>
                          {expandedPrequalSection === 'analysis' ? (
                            <ChevronUpIcon className="w-5 h-5 text-indigo-600" />
                          ) : (
                            <ChevronDownIcon className="w-5 h-5 text-indigo-600" />
                          )}
                        </button>
                        {expandedPrequalSection === 'analysis' && (
                          <div className="p-4 bg-white">
                            <PrequalificationTable
                              ocrData={businessOcrData}
                              loanData={{
                                projectedSalePrice: business.purchasePrice || business.loanSize,
                                businessName: business.businessName,
                                ebitda: business.ebitda,
                                loanId: loanId,
                                businessIndex: business.index,
                                ...business
                              }}
                              isLocked={false}
                            />
                          </div>
                        )}
                      </div>

                      {/* Financial Analysis Section */}
                      <div className="border rounded-lg mb-2 overflow-hidden">
                        <button
                          onClick={() => setExpandedPrequalSection(expandedPrequalSection === 'financial' ? null : 'financial')}
                          className="w-full px-4 py-3 bg-emerald-50 hover:bg-emerald-100 flex items-center justify-between text-left"
                        >
                          <span className="font-medium text-emerald-900">Financial Analysis</span>
                          {expandedPrequalSection === 'financial' ? (
                            <ChevronUpIcon className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <ChevronDownIcon className="w-5 h-5 text-emerald-600" />
                          )}
                        </button>
                        {expandedPrequalSection === 'financial' && (
                          <div className="p-4 bg-white">
                            <PrequalFinancialMetricsDisplay
                              ocrData={businessOcrData}
                              prequalData={{
                                projectedSalePrice: business.purchasePrice || business.loanSize,
                                businessName: business.businessName,
                                ...business
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* OCR Review Section */}
                      <div className="border rounded-lg overflow-hidden">
                        <button
                          onClick={() => setExpandedPrequalSection(expandedPrequalSection === 'ocr' ? null : 'ocr')}
                          className="w-full px-4 py-3 bg-amber-50 hover:bg-amber-100 flex items-center justify-between text-left"
                        >
                          <span className="font-medium text-amber-900">OCR Review</span>
                          {expandedPrequalSection === 'ocr' ? (
                            <ChevronUpIcon className="w-5 h-5 text-amber-600" />
                          ) : (
                            <ChevronDownIcon className="w-5 h-5 text-amber-600" />
                          )}
                        </button>
                        {expandedPrequalSection === 'ocr' && (
                          <div className="p-4 bg-white">
                            <PrequalificationOcrReview
                              applicationId={loanId}
                              businessIndex={business.index}
                              prequalData={{
                                businessName: business.businessName,
                                ...business
                              }}
                              onOcrDataUpdate={(newOcrData) => {
                                setBusinessOcrData(newOcrData);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Prequalify Modal
  const renderPrequalifyModal = () => (
    <Transition.Root show={isPrequalifyModalOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setIsPrequalifyModalOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <CheckBadgeIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                      Prequalify Business
                    </Dialog.Title>
                    <div className="mt-4 text-left">
                      {/* Amount */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Prequalification Amount
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-2 text-gray-500">$</span>
                          <input
                            type="text"
                            value={prequalifyData.amount}
                            onChange={(e) => setPrequalifyData(prev => ({
                              ...prev,
                              amount: e.target.value.replace(/[^0-9]/g, '')
                            }))}
                            className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                            placeholder="Enter amount"
                          />
                        </div>
                      </div>

                      {/* Loan Structure */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Loan Structure
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">SBA Loan %</label>
                            <input
                              type="number"
                              value={prequalifyData.structure.sbaLoan}
                              onChange={(e) => setPrequalifyData(prev => ({
                                ...prev,
                                structure: { ...prev.structure, sbaLoan: e.target.value }
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                              placeholder="80"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Seller Note %</label>
                            <input
                              type="number"
                              value={prequalifyData.structure.sellerNote}
                              onChange={(e) => setPrequalifyData(prev => ({
                                ...prev,
                                structure: { ...prev.structure, sellerNote: e.target.value }
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                              placeholder="10"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Cash Down %</label>
                            <input
                              type="number"
                              value={prequalifyData.structure.cashDown}
                              onChange={(e) => setPrequalifyData(prev => ({
                                ...prev,
                                structure: { ...prev.structure, cashDown: e.target.value }
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                              placeholder="10"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 sm:col-start-2"
                    onClick={handlePrequalifySubmit}
                  >
                    Prequalify
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    onClick={() => setIsPrequalifyModalOpen(false)}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );

  // Add this function with the other functions
  const markDocumentsAsUploaded = async () => {
    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      // *** Update status to SUBMITTED when marking docs complete ***
      await updateDoc(loanRef, {
        allDocumentsUploaded: true,
        documentsUploadedAt: serverTimestamp(),
        loanStatus: LoanStatuses.SUBMITTED // Set status to Submitted
      });

      // Update local state
      setLoan(prevLoan => ({
        ...prevLoan,
        allDocumentsUploaded: true,
        documentsUploadedAt: new Date(),
        loanStatus: LoanStatuses.SUBMITTED // Update local status
      }));

      alert('Documents marked as uploaded and status updated to Submitted.');
    } catch (error) {
      console.error('Error marking documents as uploaded:', error);
      alert('Failed to mark documents as uploaded');
    }
  };

  const downloadAllFiles = async () => {
    try {
      // Ensure loan data and documents are available
      if (!loan || !allLoanDocuments || allLoanDocuments.length === 0) {
        alert('No documents found or loan data is missing.');
        return;
      }

      const zip = new JSZip();

      // Use allLoanDocuments directly instead of documentsByLabel
      const allDocs = allLoanDocuments; // Use the flat list

      // Show loading message
      alert('Starting download... This may take a moment.');

      let failedDownloads = 0;

      // Download each file and add to zip
      // Use Promise.allSettled to wait for all fetches, even if some fail
      const downloadPromises = allDocs.map(async (doc) => {
        if (!doc.fileUrl || !doc.fileName) {
           console.warn('Skipping document without fileUrl or fileName:', doc);
           return { status: 'skipped', reason: 'Missing URL or Filename', doc };
        }
        try {
          console.log(`Attempting to fetch: ${doc.fileName} from ${doc.fileUrl}`);
          // Explicitly use cors mode, though often default
          const response = await fetch(doc.fileUrl, { mode: 'cors' });

          if (!response.ok) {
            // Log detailed error for failed fetches
            console.error(`Failed to fetch ${doc.fileName}: ${response.status} ${response.statusText}`);
            const errorBody = await response.text().catch(() => 'Could not read error body'); // Try to get more info
            console.error(`Response body (if any): ${errorBody}`);
            failedDownloads++;
            return { status: 'failed', reason: `${response.status} ${response.statusText}`, doc };
          }

          const blob = await response.blob();

          // --- Determine the correct label/folder ---
          // Find the requirement to decide if it's individual or business
          const requirement = documentRequirements.find(req => req.Labels.includes(doc.label || doc.documentName || 'Unknown'));
          let folderPath = doc.label || doc.documentName || 'Other'; // Default folder is the label or doc name

          // If it's an individual doc and has an ownerId, use that subfolder
          if (requirement?.Individual && doc.ownerId) {
              folderPath = `${folderPath}/${doc.ownerId}/`;
          } else {
              folderPath = `${folderPath}/`; // Business or general docs
          }
          // --- End Folder Logic ---

          const fileName = doc.fileName;

          // Add file to zip
          zip.file(folderPath + fileName, blob);

          console.log(`Successfully added ${fileName} to zip folder ${folderPath}`);
          return { status: 'fulfilled', doc };
        } catch (error) {
          console.error(`Error downloading file ${doc.fileName}:`, error);
          // Check for specific CORS error
          if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
              console.error("This might be a CORS issue. Check Firebase Storage CORS configuration.");
          }
          failedDownloads++;
          return { status: 'failed', reason: error.message, doc };
        }
      });

      // Wait for all downloads to settle
      await Promise.allSettled(downloadPromises);

      // Check if any files were successfully added
      if (Object.keys(zip.files).length === 0) {
        alert('No files could be downloaded. Please check the console for errors (potential CORS issues?).');
        return;
      }

      // Generate and download zip
      console.log('Generating zip file...');
      const content = await zip.generateAsync({ type: 'blob' });
      const zipUrl = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = zipUrl;
      // Sanitize applicant name for filename
      const applicantName = `${loan.firstName || ''}_${loan.lastName || ''}`.replace(/[^a-zA-Z0-9_]/g, '') || 'applicant';
      link.download = `${applicantName}_documents.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(zipUrl);

      if (failedDownloads > 0) {
         alert(`Download complete, but ${failedDownloads} file(s) failed to download. Check the console for details.`);
      } else {
         alert('Files downloaded successfully!');
      }

    } catch (error) {
      console.error('Error creating zip file:', error);
      alert('Failed to download files. Please check the console for error details.');
    }
  };

  // Function to send "Closing Documents Requested" Email
  const sendClosingDocumentsRequestedEmail = async (loanData, requestedDocNames) => {
    if (!loanData || !loanData.emails || !loanData.emails[0] || !requestedDocNames || requestedDocNames.length === 0) {
      console.error('Missing data for closing documents requested email:', { loanData, requestedDocNames });
      return false; // Indicate failure
    }

    try {
      const emailRef = collection(db, 'email');
      const storageRef = ref(storage, 'download (2).png'); // Correct logo path if needed
      const logoUrl = await getDownloadURL(storageRef);
      const sharedUploadUrl = `https://lended.ai/shared-upload/${loanId}`; // Or login URL

      // Create an HTML list of requested documents
      const documentListHtml = requestedDocNames.map(name => `<li style="color: #000000;">${name}</li>`).join('');

      const emailData = {
        to: loanData.emails[0],
        from: {
          email: "isak@lended.ai",
          name: "Isak @ Lended"
        },
        replyTo: "isak@lended.ai",
        subject: `Next Steps: Closing Documents Needed for Your Loan`,
        templateName: "ClosingDocumentsRequested",
        sentAt: new Date().toISOString(), // Use a specific template name
        message: {
          subject: `Next Steps: Closing Documents Needed`,
          firstName: loanData.firstName || 'there',
          html: `<div style="white-space: normal; word-wrap: break-word;">
<p style="color: #000000;">Hey ${loanData.firstName || 'there'},</p>

<p style="color: #000000;">We're moving to the final closing stage for your loan! To complete the process and get you funded, we need a few more documents. Please upload the following items:</p>

<ul style="margin-top: 10px; margin-bottom: 10px; padding-left: 20px;">
  ${documentListHtml}
</ul>

<p style="color: #000000;">You can upload these documents securely through your application portal:</p>
<p style="color: #000000;"><a href="${sharedUploadUrl}">Upload Closing Documents</a></p>

<p style="color: #000000;">Please upload these as soon as possible. We're excited to get this finalized for you!</p>

<p style="color: #000000;">Best,</p>

<div style="margin-top: 20px;">
  <p style="margin: 0; color: #000000;"><strong>Isak</strong></p>
  <p style="margin: 0; color: #000000;">CEO | Lended</p>
  <p style="margin: 0;"><a href="mailto:isak@lended.ai">isak@lended.ai</a></p>
  <p style="margin: 0;"><a href="tel:+19144141106">914-414-1106</a></p>
  <p style="margin-top: 10px;">
    <a href="https://lended.ai">
      <img src="${logoUrl}" alt="Lended" style="height: 30px; width: auto;">
    </a>
  </p>
</div>
</div>`,
        },
      };

      const emailDoc = await addDoc(emailRef, emailData);
      console.log('Closing Documents Requested email created with ID:', emailDoc.id);
      return true; // Indicate success
    } catch (error) {
      console.error('Error sending closing documents requested email:', error);
      return false; // Indicate failure
    }
  };

  // *** New: Function to send "Loan Funded" Email ***
  const sendLoanFundedEmail = async (loanData) => {
    if (!loanData || !loanData.emails || !loanData.emails[0]) {
      console.error('Missing data for loan funded email:', { loanData });
      return false; // Indicate failure
    }

    try {
      const emailRef = collection(db, 'email');
      const storageRef = ref(storage, 'download (2).png'); // Correct logo path if needed
      const logoUrl = await getDownloadURL(storageRef);
      const portalUrl = `https://lended.ai/shared-upload/${loanId}`; // Portal link might still be relevant

      const emailData = {
        to: loanData.emails[0],
        from: {
          email: "isak@lended.ai",
          name: "Isak @ Lended"
        },
        replyTo: "isak@lended.ai",
        subject: `Congratulations! Your Loan Has Been Funded!`,
        templateName: "LoanFundedNotice",
        sentAt: new Date().toISOString(), // Use a specific template name
        message: {
          subject: `Congratulations! Your Loan Has Been Funded!`,
          firstName: loanData.firstName || 'there',
          html: `<div style="white-space: normal; word-wrap: break-word;">
<p style="color: #000000;">Hey ${loanData.firstName || 'there'},</p>

<p style="color: #000000;">Exciting news! Your loan application has been successfully processed and the funds have been disbursed.</p>

<p style="color: #000000;">Congratulations on securing the financing for your business!</p>

<p style="color: #000000;">You can still access your application details and documents via the portal if needed:</p>
<p style="color: #000000;"><a href="${portalUrl}">Access Your Application Portal</a></p>

<p style="color: #000000;">We wish you all the best with your business endeavors. Please don't hesitate to reach out if you have any questions about your loan servicing in the future.</p>

<p style="color: #000000;">Best,</p>

<div style="margin-top: 20px;">
  <p style="margin: 0; color: #000000;"><strong>Isak</strong></p>
  <p style="margin: 0; color: #000000;">CEO | Lended</p>
  <p style="margin: 0;"><a href="mailto:isak@lended.ai">isak@lended.ai</a></p>
  <p style="margin: 0;"><a href="tel:+19144141106">914-414-1106</a></p>
  <p style="margin-top: 10px;">
    <a href="https://lended.ai">
      <img src="${logoUrl}" alt="Lended" style="height: 30px; width: auto;">
    </a>
  </p>
</div>
</div>`,
        },
      };

      const emailDoc = await addDoc(emailRef, emailData);
      console.log('Loan Funded email created with ID:', emailDoc.id);
      return true; // Indicate success
    } catch (error) {
      console.error('Error sending loan funded email:', error);
      return false; // Indicate failure
    }
  };

  // *** New Function to Render Status Card ***
  const renderStatusCard = () => {
    if (!loan) return null;

    const currentStatus = loan.loanStatus || LoanStatuses.NEW_APPLICATION;
    const daysInStatus = getDaysInStage(loan); // Use updated calculation
    const checklist = statusChecklists[currentStatus] || [];
    // Read checklist state directly from the loan object
    const currentChecklistState = loan.statusChecklistState || {};

    // Filter pending requested documents
    const pendingRequested = loan?.requestedDocuments?.filter(req => req.status !== 'uploaded') || [];
    // Determine if the status *would* be locked (for info message logic)
    const isStatusLocked = currentStatus === LoanStatuses.DOCUMENT_CLARIFICATION || currentStatus === LoanStatuses.CLOSING;

  return (
      <div className="rounded-lg bg-white shadow">
        <div className="p-6">
          <h3 className="text-base font-semibold leading-6 text-gray-900">Loan Status</h3>
          <div className="mt-4">
            <label htmlFor="loan-status" className="sr-only">
              Loan Status
            </label>
            <select
              id="loan-status"
              name="loan-status"
              // onChange={(e) => handleStatusChange(e.target.value)} // Removed onChange handler
              value={currentStatus}
              // --- Make the dropdown always disabled ---
              disabled // Always disabled
              className={`block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6 bg-gray-100 cursor-not-allowed`} // Added disabled styles directly
            >
              {/* Display the current status as the only visible, non-selectable option */}
              <option value={currentStatus}>{formatStatusName(currentStatus)}</option>
              {/* Optionally, keep other statuses hidden for context if needed, but they won't be selectable
                 Object.entries(LoanStatuses).map(([key, value]) => (
                   value !== currentStatus && <option key={key} value={value} hidden>{formatStatusName(value)}</option>
                 ))
              */}
            </select>
             {/* --- Add info message based on isStatusLocked logic --- */}
             {isStatusLocked && (
                <p className="mt-2 text-xs text-gray-500">
                  Status will update automatically once required documents are uploaded or via action buttons.
                </p>
             )}
             {/* --- Add general info message for non-locked states --- */}
             {!isStatusLocked && (
                 <p className="mt-2 text-xs text-gray-500">
                   Change status using the 'Options' menu.
                 </p>
             )}
          </div>

          {/* --- Show Pending Requested Documents (if applicable) --- */}
          {isStatusLocked && pendingRequested.length > 0 && (
              // ... pending documents rendering ...
              <div className="mt-6 border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-orange-800">Pending Requested Documents:</h4>
                <ul className="mt-2 list-disc pl-5 space-y-1 text-sm text-orange-700">
                   {pendingRequested.map((doc, index) => (
                     <li key={`pending-req-${index}`}>{doc.name}</li>
                   ))}
                 </ul>
              </div>
          )}
          {/* --- End Pending Requested Documents --- */}


          {/* Checklist */}
          {checklist.length > 0 && (
            // ... checklist rendering ...
            <div className="mt-6 border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-700">Checklist for {currentStatus}</h4>
              <ul className="mt-2 space-y-2">
                {checklist.map((item) => (
                  <li key={item} className="flex items-center">
                    <input
                      id={`checklist-${item}`}
                      name={`checklist-${item}`}
                      type="checkbox"
                      // Read checked state directly from loan.statusChecklistState
                      checked={!!currentChecklistState[item]}
                      onChange={() => handleChecklistChange(item)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <label htmlFor={`checklist-${item}`} className="ml-3 block text-sm text-gray-900">
                      {item}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ... days in status rendering ... */}
          <p className="mt-4 text-sm text-gray-500">
            In current status for {daysInStatus} day{daysInStatus === 1 ? '' : 's'}.
            {currentStatus === LoanStatuses.SUBMITTED && loan.submittedAt && (
              ` (Submitted ${loan.submittedAt.toDate ? loan.submittedAt.toDate().toLocaleDateString() : new Date(loan.submittedAt).toLocaleDateString()})`
            )}
          </p>
        </div>
      </div>
    );
  };

  // *** 3. Add Handler Function for checklist changes ***
  const handleChecklistChange = async (itemText) => {
    if (!loan) return;

    // Get current status from the main loan object
    const currentCheckedStatus = loan.statusChecklistState?.[itemText] || false;
    const newCheckedStatus = !currentCheckedStatus;

    // Optimistic UI Update: Update the main 'loan' state directly
    const updatedLoanState = {
        ...loan,
        statusChecklistState: {
            ...(loan.statusChecklistState || {}),
            [itemText]: newCheckedStatus
        }
    };
    setLoan(updatedLoanState); // Update local state immediately

    // Update Firestore
    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      await updateDoc(loanRef, {
        // Use dot notation for map field update
        [`statusChecklistState.${itemText}`]: newCheckedStatus,
      });
      console.log(`Checklist item "${itemText}" updated to ${newCheckedStatus} in Firestore.`);
    } catch (error) {
      console.error(`Error updating checklist item "${itemText}" in Firestore:`, error);
      // Revert local state on error by setting it back using the *original* loan state
       setLoan(prevLoan => ({ // Use prevLoan to ensure we revert correctly if multiple rapid clicks occurred
         ...prevLoan,
         statusChecklistState: {
           ...(prevLoan.statusChecklistState || {}),
           [itemText]: currentCheckedStatus // Revert to the original status
         }
       }));
      alert('Failed to save checklist change. Please try again.');
    }
  };

  // *** New Helper function to determine alert message ***
  const getAlertMessage = (loan) => { // Simplified parameters
    if (!loan || !loan.loanStatus) return null;

    const loanStatus = loan.loanStatus;
    const daysInStatus = getDaysInStage(loan); // Use updated getDaysInStage
    const checklistState = loan.statusChecklistState || {};
    const submittedAt = loan.submittedAt; // Get the submission timestamp

    // Handle SUBMITTED status
    if (loanStatus === LoanStatuses.SUBMITTED) {
      // Only trigger bank follow-up alerts if the package has been marked as sent
      if (checklistState['Send initial package to bank'] === true) {
        // Calculate days since admin marked as submitted
        const daysSinceSubmission = submittedAt ? getDaysSince(submittedAt) : 0;
        if (daysSinceSubmission >= 4) return "Follow up with bank (Day 4+)";
        if (daysSinceSubmission >= 2) return "Follow up with bank (Day 2)";
      }
      return null; // No alert if package not sent yet, or before Day 2
    }

    // Handle other explicit statuses
    switch (loanStatus) {
      case LoanStatuses.NEW_APPLICATION:
        if (daysInStatus >= 5) return "Follow up with client (Day 5+)";
        if (daysInStatus >= 2) return "Follow up with client (Day 2)";
        return null;
      case LoanStatuses.PREQUALIFIED_FOR:
      case LoanStatuses.TERM_SHEET_EXTENDED:
        // Example: Follow-up needed after 4 days in these states
        if (daysInStatus >= 4) return "Follow up (Day 4+)";
        return null;
      case LoanStatuses.DOCUMENT_CLARIFICATION:
        if (daysInStatus >= 4) return "Follow up with client (Day 4+)";
        if (daysInStatus >= 2) return "Follow up with client (Day 2)";
        return null;
      case LoanStatuses.CLOSING:
        if (daysInStatus >= 5) return "Follow up with bank (Day 5+)";
        return null;
      default:
        return null;
    }
  };

  const getDaysSince = (date) => {
    if (!date) return 0;
    // Ensure Firestore Timestamp or JS Date object
    const targetDate = date?.toDate ? date.toDate() : new Date(date);
    if (isNaN(targetDate.getTime())) {
      // console.warn("Invalid date passed to getDaysSince:", date);
      return 0; // Return 0 if the date is invalid
    }
    const today = new Date();
    const diffTime = Math.abs(today - targetDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getDaysInStage = (loan) => {
      if (!loan) return 0;
      const status = loan.loanStatus || LoanStatuses.NEW_APPLICATION;
      let relevantDate;

      switch(status) {
          case LoanStatuses.SUBMITTED:
              relevantDate = loan.submittedAt; // Use the specific submitted timestamp
              break;
          case LoanStatuses.UNDERWRITING:
              relevantDate = loan.underwritingAt || loan.submittedAt;
              break;
          case LoanStatuses.PREQUALIFIED_FOR:
              relevantDate = loan.prequalified_forAt || loan.submittedAt;
              break;
          case LoanStatuses.TERM_SHEET_EXTENDED:
              relevantDate = loan.term_sheet_extendedAt || loan.submittedAt;
              break;
          case LoanStatuses.DOCUMENT_CLARIFICATION:
              relevantDate = loan.document_clarificationAt || loan.submittedAt;
              break;
          case LoanStatuses.CLOSING:
               relevantDate = loan.closingAt || loan.submittedAt;
               break;
          case LoanStatuses.FUNDED:
              relevantDate = loan.fundedAt || loan.submittedAt;
              break;
          case LoanStatuses.REJECTED:
               relevantDate = loan.rejectedAt || loan.createdAt;
               break;
          case LoanStatuses.NEW_APPLICATION:
          default:
              // Use specific status timestamp if available, else createdAt
              relevantDate = loan[`${status.toLowerCase().replace(/\s+/g, '_')}At`] || loan.createdAt;
              break;
      }
      // Fallback to createdAt if the specific date is missing
      return getDaysSince(relevantDate || loan.createdAt);
  };

  // Save notes function
  const handleSaveNotes = async () => {
    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      await updateDoc(loanRef, {
        notes: notes,
        notesUpdatedAt: serverTimestamp()
      });
      
      setIsEditingNotes(false);
      alert('Notes saved successfully!');
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes. Please try again.');
    }
  };

  // Render notes card function
  const renderNotesCard = () => {
    // Function to detect URLs in text
    const extractUrls = (text) => {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      return text.match(urlRegex) || [];
    };

    // Function to render text with clickable links
    const renderTextWithLinks = (text) => {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = text.split(urlRegex);
      
      return parts.map((part, index) => {
        if (urlRegex.test(part)) {
          return (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {part}
            </a>
          );
        }
        return part;
      });
    };

    const urls = notes ? extractUrls(notes) : [];
    const hasGranolaLink = urls.some(url => url.includes('notes.granola.ai'));

    return (
      <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold leading-7 text-gray-900">Loan Notes</h3>
            <button
              onClick={() => setIsEditingNotes(!isEditingNotes)}
              className="text-sm bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md transition-colors flex items-center space-x-1"
            >
              <PencilSquareIcon className="h-4 w-4" />
              <span>{isEditingNotes ? 'Cancel' : 'Edit'}</span>
            </button>
          </div>
        </div>
        <div className="p-6">
          {isEditingNotes ? (
            <div className="space-y-4">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this loan application... (URLs will be automatically detected and displayed)"
                className="w-full p-3 border border-gray-300 rounded-md resize-vertical min-h-[120px] focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveNotes}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Save Notes
                </button>
                <button
                  onClick={() => {
                    setIsEditingNotes(false);
                    // Reset notes to original value if they cancel
                    if (loan?.notes) {
                      setNotes(loan.notes);
                    }
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="min-h-[60px]">
                {notes ? (
                  <div className="whitespace-pre-wrap text-gray-700 bg-gray-50 p-3 rounded-md">
                    {renderTextWithLinks(notes)}
                  </div>
                ) : (
                  <div className="text-gray-500 italic">
                    No notes added yet. Click "Edit" to add notes about this loan application.
                  </div>
                )}
              </div>
              
              {/* Embedded link preview for Granola or other supported links */}
              {urls.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Linked Content:</h4>
                  {urls.map((url, index) => {
                    const isGranolaLink = url.includes('notes.granola.ai');
                    
                    return (
                      <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            {isGranolaLink && (
                              <div className="w-4 h-4 bg-purple-500 rounded-sm flex items-center justify-center">
                                <span className="text-white text-xs font-bold">G</span>
                              </div>
                            )}
                            <span className="text-xs text-gray-600 truncate">
                              {isGranolaLink ? 'Granola Notes' : url}
                            </span>
                          </div>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                          >
                            <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                            <span>Open</span>
                          </a>
                        </div>
                        
                        {isGranolaLink ? (
                          // Special handling for Granola links
                          <div className="p-6 bg-gradient-to-br from-purple-50 to-indigo-50">
                            <div className="text-center">
                              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mx-auto mb-3">
                                <span className="text-white text-lg font-bold">G</span>
                              </div>
                              <h3 className="text-lg font-medium text-gray-900 mb-2">Granola Notes</h3>
                              <p className="text-gray-600 text-sm mb-4">
                                This link contains meeting notes and recordings from Granola.
                              </p>
                              <div className="space-y-2">
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-medium transition-colors"
                                >
                                  <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                                  Open Granola Notes
                                </a>
                                <p className="text-xs text-gray-500">
                                  Opens in a new tab with your meeting notes and recordings
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Regular iframe for other links
                          <div className="relative">
                            <iframe
                              src={url}
                              className="w-full h-64 border-0"
                              title={`Embedded content ${index + 1}`}
                              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div 
                              className="hidden items-center justify-center h-64 bg-gray-100 text-gray-500"
                              style={{ display: 'none' }}
                            >
                              <div className="text-center">
                                <ArrowTopRightOnSquareIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                <p className="text-sm">Unable to preview this link</p>
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 text-sm underline"
                                >
                                  Open in new tab
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Save follow-up date function
  const handleSaveFollowUp = async () => {
    if (!followUpDate) {
      alert('Please select a follow-up date.');
      return;
    }
    
    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      await updateDoc(loanRef, {
        followUpDate: followUpDate,
        followUpReason: followUpReason || null,
        followUpDateUpdatedAt: serverTimestamp()
      });
      
      setIsEditingFollowUp(false);
      alert('Follow-up date and reason saved successfully!');
    } catch (error) {
      console.error('Error saving follow-up date:', error);
      alert('Error saving follow-up date. Please try again.');
    }
  };

  // Save follow-up notes function
  const handleSaveFollowUpNotes = async () => {
    if (!newFollowUpNote.trim() || !newFollowUpReason.trim()) {
      alert('Please enter both a note and a reason before saving.');
      return;
    }

    try {
      const newNote = {
        note: newFollowUpNote.trim(),
        reason: newFollowUpReason.trim(),
        timestamp: new Date(), // Use regular Date instead of serverTimestamp()
        id: Date.now()
      };

      const updatedNotes = [...followUpNotes, newNote];
      
      const loanRef = doc(db, 'loanApplications', loanId);
      await updateDoc(loanRef, {
        followUpNotes: updatedNotes,
        followUpNotesUpdatedAt: serverTimestamp() // This is fine since it's not inside an array
      });
      
      setFollowUpNotes(updatedNotes);
      setNewFollowUpNote('');
      setNewFollowUpReason('');
      setIsEditingFollowUpNotes(false);
      alert('Follow-up note added successfully!');
    } catch (error) {
      console.error('Error saving follow-up notes:', error);
      alert('Error saving follow-up notes. Please try again.');
    }
  };

  // Render follow-up date card function
  const renderFollowUpCard = () => {
    const today = new Date().toISOString().split('T')[0];
    const isOverdue = followUpDate && followUpDate < today;
    const isDueToday = followUpDate === today;
    const isDueSoon = followUpDate && followUpDate > today && followUpDate <= new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const getStatusColor = () => {
      if (isOverdue) return 'bg-red-50 border-red-200';
      if (isDueToday) return 'bg-yellow-50 border-yellow-200';
      if (isDueSoon) return 'bg-orange-50 border-orange-200';
      return 'bg-blue-50 border-blue-200';
    };

    const getStatusText = () => {
      if (!followUpDate) return 'No follow-up date set';
      if (isOverdue) return 'Overdue';
      if (isDueToday) return 'Due Today';
      if (isDueSoon) return 'Due Soon';
      return 'Scheduled';
    };

    const formatDate = (dateString) => {
      if (!dateString || typeof dateString !== 'string') return '';
      // Create date in local timezone to avoid timezone conversion issues
      const [year, month, day] = dateString.split('-');
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    };

    return (
      <div className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${getStatusColor()}`}>
        <div className="px-5 py-4 border-b border-gray-100 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <ClockIcon className="h-5 w-5 text-blue-600" />
              <h3 className="text-base font-semibold leading-7 text-gray-900">Follow-up Date</h3>
              {followUpDate && (
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  isOverdue ? 'bg-red-100 text-red-800' :
                  isDueToday ? 'bg-yellow-100 text-yellow-800' :
                  isDueSoon ? 'bg-orange-100 text-orange-800' :
                  'bg-blue-100 text-blue-800'
                }`}>
                  {getStatusText()}
                </span>
              )}
            </div>
            <button
              onClick={() => setIsEditingFollowUp(!isEditingFollowUp)}
              className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md transition-colors flex items-center space-x-1"
            >
              <PencilSquareIcon className="h-4 w-4" />
              <span>{isEditingFollowUp ? 'Cancel' : 'Edit'}</span>
            </button>
          </div>
        </div>
        <div className="p-6">
          {isEditingFollowUp ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="followUpDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Follow-up Date
                </label>
                <input
                  type="date"
                  id="followUpDate"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  min={today}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="followUpReason" className="block text-sm font-medium text-gray-700 mb-2">
                  Follow-up Reason
                </label>
                <input
                  type="text"
                  id="followUpReason"
                  value={followUpReason}
                  onChange={(e) => setFollowUpReason(e.target.value)}
                  placeholder="e.g., Initial call, Document follow-up, Status check..."
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveFollowUp}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Save Follow-up Date
                </button>
                <button
                  onClick={() => {
                    setIsEditingFollowUp(false);
                    // Reset follow-up date and reason to original values if they cancel
                    if (loan?.followUpDate) {
                      setFollowUpDate(loan.followUpDate);
                    }
                    if (loan?.followUpReason) {
                      setFollowUpReason(loan.followUpReason);
                    }
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                {followUpDate && (
                  <button
                    onClick={() => {
                      setFollowUpDate('');
                      setFollowUpReason('');
                    }}
                    className="bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Clear Date & Reason
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="min-h-[60px] flex items-center">
              {followUpDate ? (
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <p className="text-lg font-medium text-gray-900">
                      {formatDate(followUpDate)}
                    </p>
                    {followUpReason && (
                      <div className="mt-1">
                        <span className="inline-block bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                          {followUpReason}
                        </span>
                      </div>
                    )}
                    <p className="text-sm text-gray-600 mt-1">
                      Follow-up scheduled for this date
                    </p>
                  </div>
                  {(isOverdue || isDueToday || isDueSoon) && (
                    <div className="flex items-center space-x-2">
                      <ExclamationCircleIcon className={`h-5 w-5 ${
                        isOverdue ? 'text-red-500' :
                        isDueToday ? 'text-yellow-500' :
                        'text-orange-500'
                      }`} />
                      <span className={`text-sm font-medium ${
                        isOverdue ? 'text-red-700' :
                        isDueToday ? 'text-yellow-700' :
                        'text-orange-700'
                      }`}>
                        {isOverdue ? 'Overdue!' : isDueToday ? 'Due Today!' : 'Due Soon!'}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-gray-500 italic">
                  No follow-up date set. Click "Edit" to schedule a follow-up.
                </div>
              )}
            </div>
          )}
          
          {/* Follow-up Notes Section */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <DocumentTextIcon className="h-4 w-4 text-gray-600" />
                <h4 className="text-sm font-medium text-gray-900">Follow-up Notes</h4>
                {followUpNotes.length > 0 && (
                  <span className="text-xs text-gray-500">({followUpNotes.length} notes)</span>
                )}
              </div>
              <button
                onClick={() => setIsEditingFollowUpNotes(!isEditingFollowUpNotes)}
                className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded-md transition-colors flex items-center space-x-1"
              >
                <PlusIcon className="h-3 w-3" />
                <span>{isEditingFollowUpNotes ? 'Cancel' : 'Add Note'}</span>
              </button>
            </div>
            
            {/* Existing Notes List */}
            {followUpNotes.length > 0 && (
              <div className="space-y-3 mb-4">
                {followUpNotes
                  .sort((a, b) => {
                    // Sort by timestamp, newest first
                    const aTime = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
                    const bTime = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
                    return bTime - aTime;
                  })
                  .map((noteEntry, index) => {
                    const noteDate = noteEntry.timestamp?.toDate ? noteEntry.timestamp.toDate() : new Date(noteEntry.timestamp);
                    const formattedDate = noteDate.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    });
                    const formattedTime = noteDate.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    });

                    return (
                      <div key={noteEntry.id || index} className="bg-gray-50 border-l-4 border-blue-200 p-3 rounded-r-md">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {noteEntry.reason && (
                              <div className="mb-2">
                                <span className="inline-block bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                                  {noteEntry.reason}
                                </span>
                              </div>
                            )}
                            <p className="text-sm text-gray-800 whitespace-pre-wrap mb-2">{noteEntry.note}</p>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <ClockIcon className="h-3 w-3" />
                              <span>{formattedDate} at {formattedTime}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
            
            {/* Add New Note Form */}
            {isEditingFollowUpNotes ? (
              <div className="space-y-4 border-t border-gray-200 pt-4">
                <div>
                  <label htmlFor="follow-up-reason" className="block text-sm font-medium text-gray-700 mb-2">
                    Follow-up Reason *
                  </label>
                  <input
                    type="text"
                    id="follow-up-reason"
                    value={newFollowUpReason}
                    onChange={(e) => setNewFollowUpReason(e.target.value)}
                    placeholder="e.g., Initial call, Document follow-up, Status check..."
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="follow-up-notes" className="block text-sm font-medium text-gray-700 mb-2">
                    Follow-up Notes *
                  </label>
                  <textarea
                    id="follow-up-notes"
                    value={newFollowUpNote}
                    onChange={(e) => setNewFollowUpNote(e.target.value)}
                    placeholder="Enter what happened during this follow-up, next steps, client response, etc..."
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleSaveFollowUpNotes}
                    disabled={!newFollowUpNote.trim() || !newFollowUpReason.trim()}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                  >
                    Add Note
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingFollowUpNotes(false);
                      setNewFollowUpNote('');
                      setNewFollowUpReason('');
                    }}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              followUpNotes.length === 0 && (
                <div className="text-gray-500 italic text-sm text-center py-4">
                  No follow-up notes yet. Click "Add Note" to record follow-up activities.
                </div>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  // Save bank assignment function
  const handleSaveBankAssignment = async () => {
    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      await updateDoc(loanRef, {
        assignedWorkingCapitalBank: assignedWorkingCapitalBank,
        bankAssignmentUpdatedAt: serverTimestamp()
      });
      
      setIsEditingBankAssignment(false);
      alert('Bank assignment saved successfully!');
    } catch (error) {
      console.error('Error saving bank assignment:', error);
      alert('Error saving bank assignment. Please try again.');
    }
  };

  // Save multiple banks assignment function
  const handleSaveMultipleBanksAssignment = async () => {
    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      await updateDoc(loanRef, {
        assignedWorkingCapitalBanks: assignedWorkingCapitalBanks,
        bankAssignmentUpdatedAt: serverTimestamp()
      });
      
      setIsEditingBankAssignment(false);
      alert('Bank assignments saved successfully!');
    } catch (error) {
      console.error('Error saving bank assignments:', error);
      alert('Error saving bank assignments. Please try again.');
    }
  };

  // Add bank to the list
  const handleAddBank = () => {
    if (selectedBankToAdd && !assignedWorkingCapitalBanks.includes(selectedBankToAdd)) {
      setAssignedWorkingCapitalBanks([...assignedWorkingCapitalBanks, selectedBankToAdd]);
      setSelectedBankToAdd('');
    }
  };

  // Remove bank from the list
  const handleRemoveBank = (bankToRemove) => {
    setAssignedWorkingCapitalBanks(assignedWorkingCapitalBanks.filter(bank => bank !== bankToRemove));
  };

  // Working Capital Banks list
  const workingCapitalBanks = [
    'Newity',
    'Bankwell',
    'iBusiness',
    'Live Oak',
    'Veritas',
    'Breakout Finance',
    'Fora',
    'Altline'
  ];

  // Render bank assignment card function
  const renderBankAssignmentCard = () => {
    // Only show for working capital loans
    if (loan?.loanPurpose !== 'workingCapital') return null;

    const getBankIcon = (bankName) => {
      // Return different colored circles for each bank
      const colors = {
        'Newity': 'bg-blue-500',
        'Bankwell': 'bg-green-500', 
        'iBusiness': 'bg-purple-500',
        'Live Oak': 'bg-yellow-500',
        'Veritas': 'bg-red-500',
        'Breakout Finance': 'bg-indigo-500',
        'Fora': 'bg-pink-500',
        'Altline': 'bg-gray-500'
      };
      
      const colorClass = colors[bankName] || 'bg-gray-400';
      
      return (
        <div className={`w-8 h-8 ${colorClass} rounded-full flex items-center justify-center text-white text-sm font-bold`}>
          {bankName?.charAt(0) || '?'}
        </div>
      );
    };

    // Get available banks (exclude already assigned ones)
    const availableBanks = workingCapitalBanks.filter(bank => !assignedWorkingCapitalBanks.includes(bank));

    return (
      <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200 bg-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BuildingOfficeIcon className="h-5 w-5 text-indigo-600" />
              <h3 className="text-base font-semibold leading-7 text-gray-900">Bank Assignment</h3>
              {assignedWorkingCapitalBanks.length > 0 && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                  {assignedWorkingCapitalBanks.length} Bank{assignedWorkingCapitalBanks.length !== 1 ? 's' : ''} Assigned
                </span>
              )}
            </div>
            <button
              onClick={() => setIsEditingBankAssignment(!isEditingBankAssignment)}
              className="text-sm bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-md transition-colors flex items-center space-x-1"
            >
              <PencilSquareIcon className="h-4 w-4" />
              <span>{isEditingBankAssignment ? 'Done' : 'Edit'}</span>
            </button>
          </div>
        </div>
        <div className="p-6">
          {/* Display assigned banks */}
          {assignedWorkingCapitalBanks.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Currently Assigned Banks:</h4>
              <div className="flex flex-wrap gap-3">
                {assignedWorkingCapitalBanks.map((bank) => (
                  <div key={bank} className="flex items-center space-x-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                    {getBankIcon(bank)}
                    <span className="text-sm font-medium text-gray-900">{bank}</span>
                    {isEditingBankAssignment && (
                      <button
                        onClick={() => handleRemoveBank(bank)}
                        className="text-red-500 hover:text-red-700 ml-2"
                        title="Remove bank"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add new bank section (only show when editing) */}
          {isEditingBankAssignment && (
            <div className="space-y-4">
              {availableBanks.length > 0 ? (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Add Bank:</h4>
                  <div className="flex space-x-2">
                    <select
                      value={selectedBankToAdd}
                      onChange={(e) => setSelectedBankToAdd(e.target.value)}
                      className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    >
                      <option value="">-- Select Bank to Add --</option>
                      {availableBanks.map((bank) => (
                        <option key={bank} value={bank}>
                          {bank}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleAddBank}
                      disabled={!selectedBankToAdd}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-1"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Add</span>
                    </button>
                  </div>
                </div>
              ) : (
                assignedWorkingCapitalBanks.length === workingCapitalBanks.length && (
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-sm text-gray-500 italic">All available banks have been assigned.</p>
                  </div>
                )
              )}

              {/* Save/Cancel buttons */}
              <div className="flex space-x-2 pt-4">
                <button
                  onClick={handleSaveMultipleBanksAssignment}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Save Bank Assignments
                </button>
                <button
                  onClick={() => {
                    setIsEditingBankAssignment(false);
                    setSelectedBankToAdd('');
                    // Reset to original values if they cancel
                    if (loan?.assignedWorkingCapitalBanks) {
                      setAssignedWorkingCapitalBanks(loan.assignedWorkingCapitalBanks);
                    } else if (loan?.assignedWorkingCapitalBank) {
                      setAssignedWorkingCapitalBanks([loan.assignedWorkingCapitalBank]);
                    }
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                {assignedWorkingCapitalBanks.length > 0 && (
                  <button
                    onClick={() => setAssignedWorkingCapitalBanks([])}
                    className="bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Empty state */}
          {assignedWorkingCapitalBanks.length === 0 && !isEditingBankAssignment && (
            <div className="text-center py-8">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
              <div className="text-gray-500">
                <p className="text-lg font-medium mb-1">No Banks Assigned</p>
                <p className="text-sm">Click "Edit" to assign this working capital loan to one or more banks.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Save borrower prequalification function
  const handleSaveBorrowerPrequal = async () => {
    if (!borrowerPrequalAmount || isNaN(parseFloat(borrowerPrequalAmount.replace(/,/g, '')))) {
      alert('Please enter a valid prequalification amount.');
      return;
    }

    try {
      const loanRef = doc(db, 'loanApplications', loanId);
      const cleanAmount = borrowerPrequalAmount.replace(/,/g, '');

      await updateDoc(loanRef, {
        borrowerPrequalAmount: cleanAmount,
        borrowerPrequalifiedAt: serverTimestamp(),
        borrowerPrequalifiedBy: auth.currentUser?.email || 'admin'
      });

      setBorrowerPrequalifiedAt(new Date());
      setIsEditingBorrowerPrequal(false);
      alert('Borrower prequalified successfully! They can now view their prequalification letter in their portal.');
    } catch (error) {
      console.error('Error saving borrower prequalification:', error);
      alert('Error saving borrower prequalification. Please try again.');
    }
  };

  // Clear borrower prequalification function
  const handleClearBorrowerPrequal = async () => {
    if (!window.confirm('Are you sure you want to clear the borrower prequalification?')) {
      return;
    }

    try {
      const loanRef = doc(db, 'loanApplications', loanId);

      await updateDoc(loanRef, {
        borrowerPrequalAmount: null,
        borrowerPrequalifiedAt: null,
        borrowerPrequalifiedBy: null
      });

      setBorrowerPrequalAmount('');
      setBorrowerPrequalifiedAt(null);
      setIsEditingBorrowerPrequal(false);
      alert('Borrower prequalification cleared.');
    } catch (error) {
      console.error('Error clearing borrower prequalification:', error);
      alert('Error clearing borrower prequalification. Please try again.');
    }
  };

  // Render borrower prequalification card
  const renderBorrowerPrequalCard = () => {
    const isPrequalified = borrowerPrequalAmount && borrowerPrequalifiedAt;
    const formatAmount = (amount) => {
      if (!amount) return '';
      const num = parseFloat(amount.toString().replace(/,/g, ''));
      return isNaN(num) ? '' : num.toLocaleString();
    };

    const formatDate = (date) => {
      if (!date) return '';
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    return (
      <div className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${isPrequalified ? 'border-green-200' : 'border-gray-200'}`}>
        <div className="px-5 py-4 border-b border-gray-100 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <UserGroupIcon className="h-5 w-5 text-green-600" />
              <h3 className="text-base font-semibold leading-7 text-gray-900">Borrower Prequalification</h3>
              {isPrequalified && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                  Prequalified
                </span>
              )}
            </div>
            <button
              onClick={() => setIsEditingBorrowerPrequal(!isEditingBorrowerPrequal)}
              className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg transition-colors flex items-center space-x-1"
            >
              <PencilSquareIcon className="h-4 w-4" />
              <span>{isEditingBorrowerPrequal ? 'Cancel' : 'Edit'}</span>
            </button>
          </div>
        </div>
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-4">
            Prequalify the <strong>borrower</strong> (not a specific listing). Once prequalified, they can view and download their prequalification letter from their portal.
          </p>

          {isEditingBorrowerPrequal ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="borrowerPrequalAmount" className="block text-sm font-medium text-gray-700 mb-2">
                  Prequalification Amount ($)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="text"
                    id="borrowerPrequalAmount"
                    value={formatAmount(borrowerPrequalAmount)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9,]/g, '');
                      setBorrowerPrequalAmount(value);
                    }}
                    placeholder="e.g., 500,000"
                    className="w-full pl-8 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveBorrowerPrequal}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  {isPrequalified ? 'Update Prequalification' : 'Prequalify Borrower'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingBorrowerPrequal(false);
                    // Reset to original value if they cancel
                    if (loan?.borrowerPrequalAmount) {
                      setBorrowerPrequalAmount(loan.borrowerPrequalAmount);
                    }
                  }}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                {isPrequalified && (
                  <button
                    onClick={handleClearBorrowerPrequal}
                    className="bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    Clear Prequalification
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div>
              {isPrequalified ? (
                <div className="bg-green-100 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <CheckCircleIcon className="h-8 w-8 text-green-600" />
                    <div>
                      <p className="text-2xl font-bold text-green-800">
                        ${formatAmount(borrowerPrequalAmount)}
                      </p>
                      <p className="text-sm text-green-700">
                        Prequalified on {formatDate(borrowerPrequalifiedAt)}
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-green-700">
                    The borrower can now view and download their prequalification letter from their portal.
                  </p>
                </div>
              ) : (
                <div className="text-center py-6">
                  <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Borrower not yet prequalified</p>
                  <p className="text-sm text-gray-400 mt-1">Click "Edit" to prequalify the borrower</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEmailHistoryCard = () => {
    const borrowerEmail = loan?.emails?.[0] || loan?.email || null;
    console.log('[EmailHistoryCard] loan.emails:', loan?.emails, '| loan.email:', loan?.email, '| resolved borrowerEmail:', borrowerEmail);

    return (
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div
          className="px-5 py-4 border-b border-gray-100 bg-white cursor-pointer hover:bg-gray-50"
          onClick={() => setEmailHistoryExpanded(!emailHistoryExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <EnvelopeIcon className="h-5 w-5 text-blue-600" />
              <h3 className="text-base font-semibold leading-7 text-gray-900">Email History</h3>
              {borrowerEmail && (
                <span className="text-xs text-gray-500">{borrowerEmail}</span>
              )}
            </div>
            <div className="text-xs font-medium text-indigo-600 flex items-center space-x-1">
              {emailHistoryExpanded ? (
                <><ChevronUpIcon className="h-4 w-4" /><span>Collapse</span></>
              ) : (
                <><ChevronDownIcon className="h-4 w-4" /><span>Show</span></>
              )}
            </div>
          </div>
        </div>
        {emailHistoryExpanded && (
          <div className="p-6">
            {!borrowerEmail ? (
              <p className="text-sm text-gray-400 italic">No email address on file for this borrower.</p>
            ) : (
              <EmailHistoryPanel
                recipientEmail={borrowerEmail}
                maxItems={15}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="loan-details-page min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/95 backdrop-blur">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col space-y-4">
            <div className="flex-1 min-w-0">
              <nav className="flex items-center" aria-label="Breadcrumb">
                <ol className="flex items-center space-x-4">
                  <li>
                    <div>
                      <button
                        onClick={() => navigate(-1)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <ArrowLeftIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                        <span className="sr-only">Back</span>
                      </button>
                    </div>
                  </li>
                  <li className="flex flex-col">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl sm:truncate">
                        {loan?.businessName || `${loan?.firstName || ''} ${loan?.lastName || ''}`.trim() || 'Loan Details'}
                      </h2>
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
                        <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        {formatStatusName(loan?.loanStatus)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{loan?.businessName ? `${loan?.firstName || ''} ${loan?.lastName || ''}`.trim() : 'Loan application'} · Application {loanId?.slice(0, 8)}</p>
                    <div className="mt-1 text-sm text-gray-500 flex items-center space-x-4">
                      {loan?.emails?.[0] && (
                        <a href={`mailto:${loan.emails[0]}`} className="hover:text-gray-700 flex items-center">
                          <EnvelopeIcon className="inline-block h-4 w-4 mr-1" />
                          {loan.emails[0]}
                        </a>
                      )}
                      {loan?.phoneNumbers?.[0] && (
                        <a href={`tel:${loan.phoneNumbers[0]}`} className="hover:text-gray-700 flex items-center">
                          <PhoneIcon className="inline-block h-4 w-4 mr-1" />
                          {loan.phoneNumbers[0]}
                        </a>
                      )}
                    </div>
                  </li>
                </ol>
              </nav>
            </div>

            {/* Action buttons in a separate row */}
            <div className="flex flex-wrap gap-2 items-center">
              <button
                type="button"
                onClick={downloadAllFiles}
                className="inline-flex items-center px-3.5 py-2.5 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowDownTrayIcon className="-ml-1 mr-2 h-5 w-5 text-indigo-600" aria-hidden="true" />
                Download All Files
              </button>
              
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(`https://lended.ai/upload/${loanId}`);
                  alert('Upload link copied to clipboard!');
                }}
                className="inline-flex items-center px-3.5 py-2.5 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <DocumentIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" aria-hidden="true" />
                Copy Upload Link
              </button>
              <button
                type="button"
                onClick={() => handleStatusChange(LoanStatuses.DOCUMENT_CLARIFICATION)}
                className="inline-flex items-center px-4 py-2.5 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <DocumentIcon className="-ml-1 mr-2 h-5 w-5 text-white" aria-hidden="true" />
                Request Documents
              </button>
              {loan?.allPersonalFilesUploaded && !loan?.allDocumentsUploaded && (
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm('Mark ALL deal files complete? This will email the searcher that you are working on their full analysis.')) return;
                    try {
                      const loanDocRef = doc(db, 'loanApplications', loanId);
                      await updateDoc(loanDocRef, { allDocumentsUploaded: true, markedCompleteBy: 'Ben', markedCompleteAt: serverTimestamp() });
                      if (loan?.email && !loan?.allFilesEmailSent) {
                        await addDoc(collection(db, 'email'), {
                          to: loan.email, from: 'Isak @ Lended <isak@lended.ai>', replyTo: 'isak@lended.ai',
                          subject: 'All Documents Received — We\'re On It',
                          message: { subject: 'All Documents Received — We\'re On It', html: `<p>Hi ${loan.firstName || ''},</p><p>We've received all your documents — personal files and deal files. We're working on your full analysis now.</p><p>We'll be in touch soon with next steps.</p><p>Best,</p><p>Isak<br>(914) 414-1106<br><a href="mailto:isak@lended.ai">isak@lended.ai</a> | <a href="https://lended.ai">lended.ai</a></p>` },
                        });
                        await updateDoc(loanDocRef, { allFilesEmailSent: true });
                      }
                      const updatedDoc = await getDoc(loanDocRef);
                      if (updatedDoc.exists()) setLoan({ id: updatedDoc.id, ...updatedDoc.data() });
                      alert('All deal files marked complete. Email sent.');
                    } catch (err) { alert('Error: ' + err.message); }
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <CheckCircleIcon className="-ml-1 mr-2 h-5 w-5 text-white" aria-hidden="true" />
                  All Deal Files Complete
                </button>
              )}
              {loan?.allDocumentsUploaded && (
                <span className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-green-100 text-green-800">
                  <CheckCircleIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                  All Files Received
                </span>
              )}
              {loan?.allPersonalFilesUploaded && !loan?.allDocumentsUploaded && (
                <span className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium bg-amber-100 text-amber-800">
                  <CheckCircleIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                  Personal Files Received
                </span>
              )}
              {loan?.prequalificationImageUrl && (
                <a
                  href={loan.prequalificationImageUrl}
                  download="prequalification.png"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                >
                  <ArrowDownTrayIcon className="-ml-1 mr-2 h-5 w-5 text-white" aria-hidden="true" />
                  Download Prequalification
                </a>
              )}
              
              {/* Status menu and other buttons */}
              <Menu as="div" className="relative inline-block text-left">
                <div>
                  <Menu.Button className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                    Options
                    <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
                  </Menu.Button>
                </div>

                <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    {statusOptions.map((option) => {
                       // Determine if the option requires amount input OR is the request docs action
                      const requiresAmount = option.action === LoanStatuses.PREQUALIFIED_FOR || option.action === LoanStatuses.TERM_SHEET_EXTENDED;
                      // No need for modalTitle/amountField if it's the request docs action
                      const modalTitle = requiresAmount ? (option.action === LoanStatuses.PREQUALIFIED_FOR ? 'Enter Prequalify Amount' : 'Enter Term Sheet Amount') : '';
                      const amountField = requiresAmount ? (option.action === LoanStatuses.PREQUALIFIED_FOR ? 'prequalifyAmount' : 'termSheetAmount') : '';
                      const requiresImage = option.action === LoanStatuses.PREQUALIFIED_FOR;

                      return (
                        <Menu.Item key={option.name}>
                          {({ active }) => (
                            <button
                              type="button"
                              className={`${
                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                              } group flex items-center px-4 py-2 text-sm w-full text-left`}
                              // Pass requiresAmount etc. only if it's NOT the request docs action
                              onClick={() => handleStatusChange(
                                option.action,
                                option.action !== LoanStatuses.DOCUMENT_CLARIFICATION ? requiresAmount : false,
                                option.action !== LoanStatuses.DOCUMENT_CLARIFICATION ? modalTitle : '',
                                option.action !== LoanStatuses.DOCUMENT_CLARIFICATION ? amountField : '',
                                option.action !== LoanStatuses.DOCUMENT_CLARIFICATION ? requiresImage : false
                              )}
                            >
                              <option.icon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                              {option.name}
                            </button>
                          )}
                        </Menu.Item>
                      );
                    })}
                  </div>
                </Menu.Items>
              </Menu>
              
            </div>
          </div>
        </div>
      </header>
  
      <main>
        <div className="max-w-screen-2xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <CapitalReadinessAdmin
            loan={loan}
            loanId={loanId}
            onUpdated={(updates) => setLoan(current => ({ ...current, ...updates }))}
          />
          <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="loan-details-workspace grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-2">
              <div className="lg:col-span-2">{renderUploadedFiles()}</div>
              <div>{renderWorkbookUpload()}</div>
              <div>{renderBusinessCards()}</div>
              <div>{renderBankAssignmentCard()}</div>
              <div>{renderFollowUpCard()}</div>
              <div>{renderEmailHistoryCard()}</div>
              <div>{renderNotesCard()}</div>
              <div>{renderBorrowerPrequalCard()}</div>
              <div>{renderStatusCard()}</div>
              <div className="lg:col-span-2">{renderLoanSizeAndBusinessSummary()}</div>
              <div>{renderCreditScoreCard()}</div>
              <div>{renderEmployeeCount()}</div>
            </div>

            <aside className="space-y-6 xl:sticky xl:top-40">
              <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                  <h3 className="font-semibold text-gray-900">Loan details</h3>
                  <DocumentIcon className="h-5 w-5 text-indigo-500" />
                </div>
                <dl className="divide-y divide-gray-100 px-5">
                  {[
                    ['Borrower', `${loan?.firstName || ''} ${loan?.lastName || ''}`.trim() || 'Not provided'],
                    ['Business', loan?.businessName || 'Not provided'],
                    ['Loan purpose', loan?.loanPurpose || 'Not provided'],
                    ['Loan amount', Number.isFinite(Number(loan?.loanSize)) ? `$${Number(loan.loanSize).toLocaleString()}` : 'Not provided'],
                    ['Status', formatStatusName(loan?.loanStatus)],
                    ['Application ID', loanId?.slice(0, 12) || '—'],
                  ].map(([label, value]) => (
                    <div key={label} className="grid grid-cols-[110px_1fr] gap-3 py-3.5 text-sm">
                      <dt className="text-gray-500">{label}</dt>
                      <dd className="text-right font-medium text-gray-900 break-words">{value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-5 py-4"><h3 className="font-semibold text-gray-900">Contact</h3></div>
                <div className="space-y-3 p-5">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-sm font-bold text-indigo-700">
                    {(loan?.firstName || loan?.businessName || 'L')[0].toUpperCase()}
                  </div>
                  <p className="font-semibold text-gray-900">{`${loan?.firstName || ''} ${loan?.lastName || ''}`.trim() || loan?.businessName || 'Borrower'}</p>
                  {(loan?.emails?.[0] || loan?.email) && <a className="flex items-center gap-2 text-sm text-gray-600 hover:text-indigo-600" href={`mailto:${loan?.emails?.[0] || loan?.email}`}><EnvelopeIcon className="h-4 w-4" />{loan?.emails?.[0] || loan?.email}</a>}
                  {(loan?.phoneNumbers?.[0] || loan?.phoneNumber) && <a className="flex items-center gap-2 text-sm text-gray-600 hover:text-indigo-600" href={`tel:${loan?.phoneNumbers?.[0] || loan?.phoneNumber}`}><PhoneIcon className="h-4 w-4" />{loan?.phoneNumbers?.[0] || loan?.phoneNumber}</a>}
                </div>
              </section>

              {renderBrokerCard()}
            </aside>
          </div>
  
          {/* Working Capital Use Section */}
          {renderWorkingCapitalUse()}
         
          {/* Financial Metrics Display */}
          <div className="mb-8 mt-8">
            {loan && Object.keys(ocrData).length > 0 ? (
              <FinancialMetricsDisplayAdmin 
                ocrData={ocrData}
                loanApplication={loan}
              />
            ) : (
              <p>Loading financial data...</p>
            )}
          </div>
         
          {/* Personal Financial Statement Section */}
          <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Guarantor's Personal Financial Information</h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <div className="sm:divide-y sm:divide-gray-200">
                {/* Tabs for multiple guarantors */}
                {loan?.personalGuarantors && loan.personalGuarantors.length > 1 && (
                  <div className="mb-4 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                      {loan.personalGuarantors.map((guarantor, index) => (
                        <button
                          key={index}
                          onClick={() => setActiveGuarantorIndex(index)}
                          className={`${
                            index === activeGuarantorIndex
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                          {`${guarantor.firstName} ${guarantor.lastName}`}
                        </button>
                      ))}
                    </nav>
                  </div>
                )}
                 {/* Render active guarantor's data */}
                 {personalFinancialData[activeGuarantorIndex] && (
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-2 sm:gap-4 sm:px-6">
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-2">Assets</h4>
                      {renderFinancialItem("Cash on Hand", personalFinancialData[activeGuarantorIndex].Assets?.CashOnHandAndInBanks)}
                      {renderFinancialItem("Savings Accounts", personalFinancialData[activeGuarantorIndex].Assets?.SavingsAccounts)}
                      {renderFinancialItem("Stocks and Bonds", personalFinancialData[activeGuarantorIndex].Assets?.StocksAndBonds)}
                      {renderFinancialItem("Real Estate", personalFinancialData[activeGuarantorIndex].Assets?.RealEstate)}
                      {renderFinancialItem("Automobiles", personalFinancialData[activeGuarantorIndex].Assets?.Automobiles)}
                      {renderFinancialItem("Other Assets", personalFinancialData[activeGuarantorIndex].Assets?.OtherAssets)}
                      {renderFinancialItem("Total Assets", personalFinancialData[activeGuarantorIndex].Assets?.Total, true)}
                    </div>
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-2">Liabilities</h4>
                      {renderFinancialItem("Accounts Payable", personalFinancialData[activeGuarantorIndex].Liabilities?.AccountsPayable)}
                      {renderFinancialItem("Notes Payable", personalFinancialData[activeGuarantorIndex].Liabilities?.NotesPayableToBanksAndOthers)}
                      {renderFinancialItem("Auto Installment", personalFinancialData[activeGuarantorIndex].Liabilities?.InstallmentAccountAuto)}
                      {renderFinancialItem("Other Installment", personalFinancialData[activeGuarantorIndex].Liabilities?.InstallmentAccountOther)}
                      {renderFinancialItem("Life Insurance Loans", personalFinancialData[activeGuarantorIndex].Liabilities?.LoanAgainstLifeInsurance)}
                      {renderFinancialItem("Mortgages", personalFinancialData[activeGuarantorIndex].Liabilities?.MortgagesOnRealEstate)}
                      {renderFinancialItem("Unpaid Taxes", personalFinancialData[activeGuarantorIndex].Liabilities?.UnpaidTaxes)}
                      {renderFinancialItem("Other Liabilities", personalFinancialData[activeGuarantorIndex].Liabilities?.OtherLiabilities)}
                      {renderFinancialItem("Total Liabilities", personalFinancialData[activeGuarantorIndex].Liabilities?.TotalLiabilities, true)}
                    </div>
                  </div>
                )}
                <div className="py-4 sm:py-5 sm:px-6">
                  <div className="text-lg font-bold">
                    Net Worth: {formatCurrency(calculateNetWorth(personalFinancialData[activeGuarantorIndex]))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Lender Match Card */}
          <div className="mt-8">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-base font-semibold leading-7 text-gray-900">Matched Lenders</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {matchedLenders.length > 0 ? (
                    matchedLenders.map((lender, index) => {
                      const isAssigned = assignedBanks[lender.id];
                      return (
                        <div key={lender.id} className={`rounded-lg overflow-hidden ${isAssigned ? 'bg-amber-50 border border-amber-200' : 'bg-white border border-gray-200'} shadow-md transition-all duration-300 hover:shadow-lg`}>
                          <div className="p-4">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="text-lg font-semibold text-gray-800 truncate">{lender.bankName}</h4>
                              {isAssigned ? (
                                <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                                  Assigned
                                </span>
                              ) : (
                                <button
                                  onClick={() => setAssigningBank(lender.id)}
                                  className="px-3 py-1 bg-blue-500 text-white rounded-full text-xs font-medium hover:bg-blue-600 transition-colors duration-300"
                                >
                                  Assign
                                </button>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                              <div>
                                <p className="text-gray-500">Loan Rate</p>
                                <p className="font-bold text-gray-800">{lender.usualLoanRateMin}% - {lender.usualLoanRateMax}%</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Process Time</p>
                                <p className="font-bold text-gray-800">{lender.processTime || 'N/A'} days</p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                              <div>
                                <p className="text-gray-500">Approval Rate</p>
                                <p className="font-bold text-gray-800">{lender.approvalPercentage || 'N/A'}%</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Term Sheet Time</p>
                                <p className="font-bold text-gray-800">{lender.termSheetTime || 'N/A'} days</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p>No matched lenders found.</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Loan Officer Assignment */}
          <div className="mt-8">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-base font-semibold leading-7 text-gray-900">Assign Loan Officer</h3>
              </div>
              <div className="p-6">
                <LoanOfficerAssignment 
                  loanId={loanId} 
                  handleAssignBank={handleAssignBank} 
                  isPrequalification={false}
                />
              </div>
            </div>
          </div>

          {/* Renamed Prequalify Modal to Amount Modal */}
          {isAmountModalOpen && (
            <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
              <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
                {/* Modal panel */}
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full"> {/* Added w-full */}
                        <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                          {amountModalContext.title || 'Enter Amount'} {/* Use dynamic title */}
                        </h3>
                        <div className="mt-2">
                          <label htmlFor="modal-amount-input" className="sr-only">Amount</label> {/* Add label for accessibility */}
                          <div className="relative mt-1 rounded-md shadow-sm"> {/* Add currency symbol */}
                            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                              <span className="text-gray-500 sm:text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              id="modal-amount-input" // Add id
                              value={modalAmountInput}
                              onChange={(e) => setModalAmountInput(e.target.value)}
                              className="mt-1 block w-full rounded-md border-gray-300 pl-7 pr-12 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" // Adjusted padding
                              placeholder="0.00"
                              aria-describedby="price-currency"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={submitModalAmount} // Use the renamed submit function
                    >
                      Submit
                    </button>
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={() => setIsAmountModalOpen(false)} // Close the generic modal
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Request Documents Modal */}
          {isRequestDocsModalOpen && (
            <div className="fixed z-20 inset-0 overflow-y-auto" aria-labelledby="request-docs-modal-title" role="dialog" aria-modal="true">
              <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Background overlay */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
                {/* Modal panel */}
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                        <h3 className="text-lg leading-6 font-medium text-gray-900" id="request-docs-modal-title">
                          Request Additional Documents
                        </h3>
                        <div className="mt-4">
                          <label htmlFor="doc-request-input" className="block text-sm font-medium text-gray-700">
                            Document Name
                          </label>
                          <div className="mt-1 flex rounded-md shadow-sm">
                            <input
                              type="text"
                              id="doc-request-input"
                              value={currentDocRequestInput}
                              onChange={(e) => setCurrentDocRequestInput(e.target.value)}
                              className="block w-full min-w-0 flex-1 rounded-none rounded-l-md border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              placeholder="e.g., Updated Balance Sheet"
                            />
                            <button
                              type="button"
                              onClick={handleAddDocRequest}
                              className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            >
                              Add
                            </button>
                          </div>
                        </div>

                        {pendingDocRequests.length > 0 && (
                          <div className="mt-4 border-t pt-4">
                            <h4 className="text-sm font-medium text-gray-500 mb-2">Documents to Request:</h4>
                            <ul role="list" className="divide-y divide-gray-200">
                              {pendingDocRequests.map((docName) => (
                                <li key={docName} className="flex items-center justify-between py-2">
                                  <span className="text-sm text-gray-800">{docName}</span>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveDocRequest(docName)}
                                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                                  >
                                    Remove
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      disabled={pendingDocRequests.length === 0}
                      className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm ${pendingDocRequests.length === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'}`}
                      // *** Call submitDocumentRequests directly - it uses the requestModalPurpose state ***
                      onClick={submitDocumentRequests}
                    >
                      Submit Request
                    </button>
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={() => { setIsRequestDocsModalOpen(false); setRequestModalPurpose(null); /* Clear purpose on cancel */ }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes Card */}
          {renderNotesCard()}

          {/* Follow-up Date Card */}
          {renderFollowUpCard()}

          {/* Bank Assignment Card */}
          {renderBankAssignmentCard()}

        </div>
      </main>

      {/* Prequalify Modal */}
      {renderPrequalifyModal()}
    </div>
  );
};

export default LoanDetails;



